import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import HomeV2 from "./pages/HomeV2";
import HomeV3 from "./pages/HomeV3";
import HomeV4 from "./pages/HomeV4";
import HomeV5 from "./pages/HomeV5";
import HomeV6 from "./pages/HomeV6";
import ShopifyDev from "./pages/ShopifyDev";
import ShopifyDevV2 from "./pages/ShopifyDevV2";
import MarketingPage from "./pages/MarketingPage";
import ConversionPage from "./pages/ConversionPage";
import HomepageReviewPage from "./pages/HomepageReviewPage";
import MarketingPageV2 from "./pages/MarketingPageV2";
import CreativePage from "./pages/CreativePage";
import AboutPage from "./pages/AboutPage";
import ShowcasePage from "./pages/ShowcasePage";
import VeroniqueGabaiPage from "./pages/case-studies/VeroniqueGabaiPage";
import CaseStudyPage from "./pages/case-studies/CaseStudyPage";
import ContactPage from "./pages/ContactPage";
import LegalPage from "./pages/LegalPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";

/* ====== Admin / CMS ====== */
import { AdminAuthProvider } from "./admin/AuthContext";
import AdminLayout from "./admin/AdminLayout";
import AdminLoginPage from "./admin/LoginPage";
import AdminDashboardPage from "./admin/DashboardPage";
import AdminBlogListPage from "./admin/BlogListPage";
import AdminBlogEditPage from "./admin/BlogEditPage";
import AdminShowcaseListPage from "./admin/ShowcaseListPage";
import AdminShowcaseEditPage from "./admin/ShowcaseEditPage";
import AdminSettingsPage from "./admin/SettingsPage";
import AdminSeoPage from "./admin/SeoPage";
import AdminMediaPage from "./admin/MediaPage";
import AdminScriptsPage from "./admin/ScriptsPage";
import AdminTransactionalPage from "./admin/TransactionalPage";
import AdminContentListPage from "./admin/ContentListPage";
import AdminContentEditPage from "./admin/ContentEditPage";
import AdminSyncPage from "./admin/SyncPage";
import AdminBackupsPage from "./admin/BackupsPage";
import { useCmsPageRoutes } from "./lib/CmsPageRoutes";
import { Navigate } from "react-router-dom";
import { ContactPanelProvider, useContactPanel } from "./components/ContactPanel";
import CustomScripts from "./components/CustomScripts";
import { useContent, mediaUrl } from "./lib/useContent";
import { isContactHref } from "./lib/cta";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  AnimatePresence,
} from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Plus,
  Minus,
  Check,
  Zap,
  Clock,
  Wallet,
  Wrench,
  Rocket,
  Code2,
  Palette,
  Mail,
  Target,
  Search,
  Video,
  Megaphone,
  Instagram,
  MousePointerClick,
  Sparkles,
  MoveRight,
  BookOpen,
  Menu as MenuIcon,
  X as CloseIcon,
  Layers,
  Package,
  Boxes,
  Repeat,
  Globe,
  CreditCard,
  Settings,
  Filter,
  PenTool,
  Film,
  Play,
  Shuffle,
  Briefcase,
} from "lucide-react";

/* ============================================================
   FadeUp — unified scroll-reveal wrapper (framer-motion)
============================================================ */
const FadeUp = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/* ============================================================
   Tokens
   ------------------------------------------------------------
   Charcoal palette:
     base   #0B0F14   (page)
     layer1 #11161D   (alternating sections)
     layer2 #151B23   (deeper layer / cards)
     layer3 #1B2330   (elevated cards / hover)
   Accent: #F4D64E (refined yellow — CTAs, icons, highlights only)
============================================================ */

/* ============================================================
   Shared primitives
============================================================ */

const Eyebrow = ({ children, className = "" }) => (
  <div
    className={`inline-flex items-center gap-3 text-[0.68rem] font-bold tracking-[0.26em] uppercase text-neutral-400 ${className}`}
  >
    <span className="h-px w-7 bg-[#F4D64E]" />
    {children}
  </div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div
    className={`flex flex-col ${
      align === "center" ? "items-center text-center" : "items-start"
    } gap-6 max-w-3xl ${align === "center" ? "mx-auto" : ""}`}
  >
    {eyebrow && (
      <span data-reveal="eyebrow">
        <Eyebrow>{eyebrow}</Eyebrow>
      </span>
    )}
    <h2
      data-reveal="mask-block"
      style={{ "--i": eyebrow ? 1 : 0 }}
      className="font-display text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.03em] leading-[1.02] text-white"
    >
      <span>{title}</span>
    </h2>
    {description && (
      <p
        data-reveal="mask-block"
        style={{ "--i": eyebrow ? 2 : 1 }}
        className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[60ch]"
      >
        <span>{description}</span>
      </p>
    )}
  </div>
);

/* ----- PillarHeader — shared for Services, Marketing 360°, Creative ----- */
const pillarHeadingClass =
  "font-display text-[2.25rem] sm:text-[2.75rem] md:text-[3.25rem] lg:text-[3.5rem] font-extrabold tracking-[-0.03em] leading-[1.04] text-white";

const PillarAccent = ({ children }) => (
  <span className="relative inline-block">
    {children}
    <span
      aria-hidden
      className="reveal-underline absolute -bottom-1 left-0 right-0 h-[2px] md:h-[3px] bg-[#F4D64E]/70 rounded-full"
    />
  </span>
);

const PillarHeader = ({
  eyebrow,
  description,
  children,
  align = "left",
  maxWidth = "max-w-3xl",
}) => (
  <div
    className={`flex flex-col ${
      align === "center" ? "items-center text-center mx-auto" : "items-start"
    } gap-7 ${maxWidth}`}
  >
    {eyebrow && (
      <span data-reveal="eyebrow">
        <Eyebrow>{eyebrow}</Eyebrow>
      </span>
    )}
    <div data-reveal="mask-block" style={{ "--i": eyebrow ? 1 : 0 }}>
      {children}
    </div>
    {description && (
      <p
        data-reveal="mask-block"
        style={{ "--i": eyebrow ? 2 : 1 }}
        className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[58ch]"
      >
        <span>{description}</span>
      </p>
    )}
  </div>
);

/* ----- SectionAmbient — subtle yellow glow + dot grid + soft parallax ----- */
const AMBIENT_VARIANTS = {
  // Services — glow top-right + soft bottom-left
  a:
    "radial-gradient(780px 460px at 88% 10%, rgba(244,214,78,0.045), transparent 62%), radial-gradient(540px 420px at 8% 95%, rgba(244,214,78,0.03), transparent 62%)",
  // Marketing 360° — full-width ambient anchored LEFT-CENTER
  b:
    "radial-gradient(1000px 700px at 0% 50%, rgba(244,214,78,0.06), transparent 62%), radial-gradient(560px 480px at 6% 50%, rgba(244,214,78,0.035), transparent 62%)",
  // Creative — full-width ambient anchored RIGHT-CENTER
  c:
    "radial-gradient(1000px 700px at 100% 50%, rgba(244,214,78,0.06), transparent 62%), radial-gradient(560px 480px at 94% 50%, rgba(244,214,78,0.035), transparent 62%)",
  // Testimonials — balanced dual glow
  d:
    "radial-gradient(720px 460px at 20% 80%, rgba(244,214,78,0.045), transparent 62%), radial-gradient(640px 440px at 85% 18%, rgba(244,214,78,0.03), transparent 62%)",
  // Creative Capabilities (V2) — single centered full-width glow
  e:
    "radial-gradient(1300px 780px at 50% 50%, rgba(244,214,78,0.055), transparent 62%), radial-gradient(780px 520px at 50% 50%, rgba(244,214,78,0.03), transparent 60%)",
};

const SectionAmbient = ({
  variant = "a",
  parallax = 48,
  grid = true,
  className = "absolute inset-0",
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Gentle parallax — gradients drift ~parallax px across the section's
  // viewport life. Subtle by design; content stays the focus.
  const y = useTransform(scrollYProgress, [0, 1], [-parallax, parallax]);
  return (
    <div
      ref={ref}
      aria-hidden
      className={`${className} pointer-events-none overflow-hidden`}
    >
      <motion.div
        className="absolute -inset-y-10 inset-x-0"
        style={{ y, background: AMBIENT_VARIANTS[variant] }}
      />
      {grid && <div className="absolute inset-0 dot-grid opacity-[0.18]" />}
    </div>
  );
};

const PrimaryButton = ({ children, href = "#contact", testId, variant }) => {
  // "marketing" variant matches the Marketing 360° "Talk to us" CTA — flat,
  // editorial, color-shift on hover (no glow/lift).
  if (variant === "marketing") {
    return (
      <a
        href={href}
        data-testid={testId}
        className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-6 py-4 rounded-md transition-colors"
      >
        {children}
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    );
  }
  return (
    <a
      href={href}
      data-testid={testId}
      className="group inline-flex items-center gap-2 bg-[#F4D64E] text-neutral-950 px-7 py-4 text-[0.78rem] font-bold tracking-[0.18em] uppercase rounded-md hover:bg-[#FFDF5C] transition-all duration-300 shadow-[0_0_0_1px_rgba(244,214,78,0.25)] hover:shadow-[0_0_0_1px_rgba(244,214,78,0.45),0_14px_40px_-12px_rgba(244,214,78,0.55)] hover:-translate-y-0.5"
    >
      {children}
      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  );
};

const SecondaryButton = ({ children, href = "#services", testId, variant }) => {
  // "marketing" variant matches the Marketing 360° "See all Marketing Services"
  // CTA — yellow outlined, fills with translucent yellow on hover.
  if (variant === "marketing") {
    return (
      <a
        href={href}
        data-testid={testId}
        className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
      >
        {children}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </a>
    );
  }
  return (
    <a
      href={href}
      data-testid={testId}
      className="group inline-flex items-center gap-2 border border-white/15 bg-white/[0.02] text-white px-7 py-4 text-[0.78rem] font-bold tracking-[0.18em] uppercase rounded-md hover:border-[#F4D64E]/60 hover:text-[#F4D64E] hover:-translate-y-0.5 transition-all duration-300"
    >
      {children}
      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
    </a>
  );
};

/* ============================================================
   Navigation — minimal sticky header (logo + Get Started + burger)
   All section links and version routes live inside the hamburger
   overlay, keeping the sticky header clean and editorial.
============================================================ */

// Version routes — kept reachable from the hamburger overlay so the
// preview header stays clean while every URL still works.
const VERSION_ROUTES = [
  { path: "/v1", label: "V1" },
  { path: "/v2", label: "V2" },
  { path: "/v3", label: "V3" },
  { path: "/v4", label: "V4" },
  { path: "/v5", label: "V5 · Main" },
  { path: "/v6", label: "V6" },
];

/* ============================================================
   useIsPreview — variants strip is shown only when the site
   is being served from a preview/dev host (preview.emergentagent.com,
   localhost, etc). On the live production domain the strip stays
   hidden automatically — no flags, no toggles.
============================================================ */
const PREVIEW_HOST_PATTERNS = [
  "localhost",
  "127.0.0.1",
  "preview",
  "emergentagent",
  "vercel.app",
  "netlify",
];
const useIsPreview = () => {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  return PREVIEW_HOST_PATTERNS.some((p) => host.includes(p));
};

const OVERLAY_PAGES_FALLBACK = [
  { label: "Home", href: "/", sub: "Where it all starts", route: true },
  { label: "Shopify Development", href: "/shopify-experts-development", sub: "Themes, apps, migrations, custom builds", route: true },
  { label: "Marketing 360", href: "/ecommerce-marketing-agency", sub: "Growth, performance & brand", route: true },
  { label: "Creative & Design", href: "/creative", sub: "Editorial, motion, multimedia", route: true },
  { label: "Conversion Optimization", href: "/conversion-optimization", sub: "CRO woven through every build", route: true },
  { label: "Homepage Review", href: "/homepage-review", sub: "Private, by invitation", route: true },
  { label: "Showcase", href: "/showcase", sub: "Selected work · 11 stories", route: true },
  { label: "Our Story", href: "/about", sub: "Two teams, one studio", route: true },
  { label: "Contact", href: "/contact", sub: "Book a call · send a brief", route: true },
  { label: "Blog", href: "/blog", sub: "Insights & playbooks", route: true },
];

const NAV_LOGO_FALLBACK =
  "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/lime8iqi_logo-web.png";

const Nav = () => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState([]);
  const location = useLocation();
  const { open: openContact } = useContactPanel();
  const isAdmin = useIsPreview();
  const globals = useContent("globals");
  const n = globals?.nav || {};
  const logoSrc = n.logo || NAV_LOGO_FALLBACK;
  const ctaLabel = n.cta_label || "Get Started";
  const ctaAction = n.cta_action || "open_contact";
  const ctaHref = n.cta_href || "";
  const overlayPages = Array.isArray(n.overlay_pages) && n.overlay_pages.length
    ? n.overlay_pages.map((it) => ({
        label: it.title || "",
        href: it.href || "/",
        sub: it.body || "",
        route: it.route !== false,
      }))
    : OVERLAY_PAGES_FALLBACK;
  const footerEmail = n.footer_email || "hello@sundaydigital.io";
  const footerTagline = n.footer_tagline || "New York · Remote";
  // `show_variants` defaults to TRUE so nothing breaks on first load; setting
  // it to false hides the entire "Preview · Variants" row on every host
  // (even the preview/dev host).
  const showVariants = n.show_variants === undefined ? true : !!n.show_variants;

  // Pull global social links from /api/settings (public). Order is fixed
  // so admins can reorder by simply filling/clearing fields. Entries
  // with empty/placeholder URLs are filtered out so the strip only
  // shows real, working links. If none are configured, the strip
  // itself is hidden (see render below).
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/settings`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((s) => {
        const social = (s && s.social) || {};
        const ORDER = [
          { key: "instagram", label: "Instagram" },
          { key: "linkedin",  label: "LinkedIn" },
          { key: "dribbble",  label: "Dribbble" },
          { key: "twitter",   label: "Twitter" },
          { key: "tiktok",    label: "TikTok" },
          { key: "behance",   label: "Behance" },
        ];
        const STUBS = new Set(["#", "https://", "http://"]);
        const list = ORDER
          .map(({ key, label }) => ({ label, href: (social[key] || "").trim() }))
          .filter((x) => x.href && !STUBS.has(x.href.toLowerCase()));
        setSocialLinks(list);
      })
      .catch(() => {
        /* silent — no socials show */
      });
  }, []);

  const handleCta = () => {
    if (ctaAction === "external" && ctaHref) {
      if (/^(mailto:|tel:)/i.test(ctaHref) || /^https?:\/\//i.test(ctaHref)) {
        window.location.href = ctaHref;
      } else {
        window.location.assign(ctaHref);
      }
      return;
    }
    openContact();
  };

  // lock body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = overlayOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [overlayOpen]);

  return (
    <>
      <header
        data-testid="site-nav"
        className="sticky top-0 z-[60] bg-[#0B0F14]/80 backdrop-blur-md border-b border-white/[0.06]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="inline-flex items-center"
          >
            <img
              src={mediaUrl(logoSrc) || logoSrc}
              alt="Sunday Digital"
              className="h-9 md:h-10 w-auto select-none"
              draggable={false}
            />
          </Link>

          <div className="flex items-center gap-3">
            {/* Get Started — opens slide-in contact panel.
                Mobile = compact icon-only square (preserves space next to logo).
                Desktop = full text + arrow pill. */}
            <button
              type="button"
              onClick={handleCta}
              data-testid="nav-cta"
              aria-label={ctaLabel}
              className="group inline-flex items-center justify-center md:justify-start gap-0 md:gap-3 text-[0.82rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white w-11 h-11 md:w-auto md:h-auto md:px-6 md:py-3.5 rounded-md transition-colors"
            >
              <span className="hidden md:inline">{ctaLabel}</span>
              <ArrowUpRight className="w-5 h-5 md:w-4 md:h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>

            {/* Hamburger — bigger, bolder, yellow-tinted for visibility */}
            <button
              data-testid="nav-hamburger"
              onClick={() => setOverlayOpen(true)}
              className="group inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-md border border-[#F4D64E]/40 bg-[#F4D64E]/[0.08] text-[#F4D64E] hover:bg-[#F4D64E] hover:text-neutral-950 hover:border-[#F4D64E] transition-colors"
              aria-label="Open menu"
            >
              <MenuIcon className="w-6 h-6" strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </header>

      {/* Full overlay menu (hamburger) */}
      <AnimatePresence>
        {overlayOpen && (
          <motion.div
            data-testid="nav-overlay"
            className="fixed inset-0 z-[70] bg-[#0B0F14]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Solid base to fully block hero ambient behind */}
            <div aria-hidden className="absolute inset-0 bg-[#0B0F14]" />

            <div className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(900px 520px at 80% 10%, rgba(244,214,78,0.08), transparent 60%)",
              }}
            />

            <div className="relative max-w-7xl mx-auto h-full px-6 md:px-10 flex flex-col overflow-y-auto">
              {/* Top row */}
              <div className="h-16 flex items-center justify-between shrink-0">
                <Link
                  to="/"
                  onClick={() => setOverlayOpen(false)}
                  className="inline-flex items-center"
                >
                  <img
                    src={mediaUrl(logoSrc) || logoSrc}
                    alt="Sunday Digital"
                    className="h-9 md:h-10 w-auto select-none"
                    draggable={false}
                  />
                </Link>
                <button
                  data-testid="nav-overlay-close"
                  onClick={() => setOverlayOpen(false)}
                  className="inline-flex items-center gap-2 text-[0.72rem] font-bold tracking-[0.2em] uppercase text-neutral-300 hover:text-[#F4D64E] transition-colors"
                >
                  <span>Close</span>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-white/10 bg-white/[0.02]">
                    <CloseIcon className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                </button>
              </div>

              {/* Mobile-only top CTA — sits directly under the logo / close
                  row so the primary action is visible without scrolling past
                  the link list. Hidden on md+ (the bottom CTA covers desktop). */}
              <div className="md:hidden pt-5 pb-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setOverlayOpen(false);
                    handleCta();
                  }}
                  data-testid="overlay-cta-top"
                  className="group w-full inline-flex items-center justify-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
                >
                  {ctaLabel}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>

              {/* Links grid */}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3 md:gap-y-4 content-center pb-10 md:pb-16">
                {overlayPages.map((p, i) => {
                  const motionProps = {
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    transition: {
                      delay: 0.1 + i * 0.05,
                      duration: 0.6,
                      ease: [0.22, 1, 0.36, 1],
                    },
                    className:
                      "group relative flex items-baseline justify-between gap-6 py-5 border-b border-white/[0.06] hover:border-[#F4D64E]/40 transition-colors",
                  };
                  const inner = (
                    <>
                      <span className="flex items-baseline gap-5">
                        <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 tabular-nums">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-3xl md:text-5xl font-black tracking-[-0.02em] text-white group-hover:text-[#F4D64E] transition-colors">
                          {p.label}
                        </span>
                      </span>
                      <span className="hidden md:inline-block text-xs text-neutral-500 max-w-[24ch] text-right leading-snug group-hover:text-neutral-300 transition-colors">
                        {p.sub}
                      </span>
                    </>
                  );
                  if (isContactHref(p.href)) {
                    return (
                      <motion.button
                        key={`contact-${i}`}
                        type="button"
                        onClick={() => {
                          setOverlayOpen(false);
                          openContact();
                        }}
                        data-testid={`overlay-link-${i + 1}`}
                        {...motionProps}
                        className={`${motionProps.className} text-left w-full`}
                      >
                        {inner}
                      </motion.button>
                    );
                  }
                  return p.route ? (
                    <motion.div
                      key={p.href}
                      data-testid={`overlay-link-${i + 1}`}
                      {...motionProps}
                    >
                      <Link
                        to={p.href}
                        onClick={() => setOverlayOpen(false)}
                        className="contents"
                      >
                        {inner}
                      </Link>
                    </motion.div>
                  ) : (
                    <motion.a
                      key={p.href}
                      href={p.href}
                      onClick={() => setOverlayOpen(false)}
                      data-testid={`overlay-link-${i + 1}`}
                      {...motionProps}
                    >
                      {inner}
                    </motion.a>
                  );
                })}
              </div>

              {/* Prominent Get Started CTA — sits between the link grid
                  and the bottom strip. On mobile we render the CTA at the
                  TOP of the overlay instead (see overlay-cta-top above), so
                  this block is hidden on small screens. */}
              <div className="hidden md:block pt-2 pb-8 md:pb-10">
                <button
                  type="button"
                  onClick={() => {
                    setOverlayOpen(false);
                    handleCta();
                  }}
                  data-testid="overlay-cta"
                  className="group w-full md:w-auto inline-flex items-center justify-center md:justify-start gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-5 md:py-4 rounded-md transition-colors"
                >
                  {ctaLabel}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>

              {/* Bottom strip */}
              <div className="pb-8 pt-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-xs text-neutral-500 border-t border-white/[0.06]">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
                  <a href={`mailto:${footerEmail}`} className="hover:text-[#F4D64E] transition-colors">{footerEmail}</a>
                  <span className="hidden sm:inline">{footerTagline}</span>
                  {/* Version routes — preview-only.
                      Auto-shown on preview/dev hosts, hidden on the live domain.
                      Can be fully disabled from Admin → Nav & Footer → Nav →
                      "Show Preview · Variants row". */}
                  {showVariants && isAdmin ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] mr-2">
                        Preview · Variants
                      </span>
                      {VERSION_ROUTES.map((v) => {
                        const isActive =
                          location.pathname === v.path ||
                          (v.path === "/v5" && location.pathname === "/");
                        return (
                          <Link
                            key={v.path}
                            to={v.path}
                            onClick={() => setOverlayOpen(false)}
                            data-testid={`overlay-version-${v.label.toLowerCase().split(" ")[0]}`}
                            className={`px-2 py-1 rounded-md text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors ${
                              isActive
                                ? "bg-[#F4D64E]/15 text-[#F4D64E]"
                                : "text-neutral-500 hover:text-[#F4D64E]"
                            }`}
                          >
                            {v.label}
                          </Link>
                        );
                      })}
                      <Link
                        to="/ecommerce-marketing-agency-v2"
                        onClick={() => setOverlayOpen(false)}
                        data-testid="overlay-mkt-v2"
                        className={`ml-1 px-2 py-1 rounded-md text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors ${
                          location.pathname === "/ecommerce-marketing-agency-v2"
                            ? "bg-[#F4D64E]/15 text-[#F4D64E]"
                            : "text-neutral-500 hover:text-[#F4D64E]"
                        }`}
                      >
                        Mkt · v2
                      </Link>
                      <Link
                        to="/shopify-experts-development-v1"
                        onClick={() => setOverlayOpen(false)}
                        data-testid="overlay-dev-v1"
                        className={`ml-1 px-2 py-1 rounded-md text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors ${
                          location.pathname === "/shopify-experts-development-v1"
                            ? "bg-[#F4D64E]/15 text-[#F4D64E]"
                            : "text-neutral-500 hover:text-[#F4D64E]"
                        }`}
                      >
                        Dev · v1
                      </Link>
                    </div>
                  ) : null}
                </div>
                {socialLinks.length > 0 ? (
                  <div className="flex items-center gap-6">
                    {socialLinks.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#F4D64E] transition-colors"
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ============================================================
   1. HERO
============================================================ */

/**
 * HeroAmbient — hero-only: two drifting yellow halos + reactive constellation
 * --------------------------------------------------------------
 *  • 2 soft yellow radial halos that drift organically across the hero,
 *    retargeting to random points every 5–10s, lerping slowly.
 *    Feels like ambient studio light rather than a fixed sun.
 *  • ~120 subtle stars distributed across the full hero surface +
 *    constellation lines between close neighbours.
 *  • Every star reacts to the cursor (soft distance falloff, radial +
 *    tangential swirl) — no hard radius, no on/off zone.
 *  • Mobile: 40 stars, no mouse interaction, halos still drift.
 */
const HeroAmbient = ({ drift = false }) => {
  const canvasRef = useRef(null);
  const hostRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const particlesRef = useRef([]);
  const halosRef = useRef([]);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    // Respect prefers-reduced-motion — disables the drift entirely
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const driftActive = drift && !reducedMotion;
    // Subliminal drift speed — full traverse takes ~75 seconds
    // Direction: down-and-slightly-left, like floating through space
    const DRIFT_VX = -8 / 1000;   // -8 px / sec (rightward camera = stars drift left)
    const DRIFT_VY = 6 / 1000;    // +6 px / sec
    let prevT = 0;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      seed();
    };

    const makeHalo = (intensity, radiusFactor) => {
      const r = Math.min(width, height) * radiusFactor;
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        tx: Math.random() * width,
        ty: Math.random() * height,
        radius: r,
        intensity,
        retargetAt: 0,
        // Wavy breathing — unique phase/frequency so halos never sync
        breathePhase: Math.random() * Math.PI * 2,
        breatheFreq: 0.0004 + Math.random() * 0.0007,
        // Slow radius pulse
        pulsePhase: Math.random() * Math.PI * 2,
        pulseFreq: 0.00025 + Math.random() * 0.0005,
        // Small positional wobble on top of the drift (organic, not linear)
        wobblePhaseX: Math.random() * Math.PI * 2,
        wobblePhaseY: Math.random() * Math.PI * 2,
        wobbleFreqX: 0.0003 + Math.random() * 0.0005,
        wobbleFreqY: 0.00035 + Math.random() * 0.0006,
        wobbleAmp: 40 + Math.random() * 60,
      };
    };

    const seed = () => {
      // Stars — evenly distributed across the full hero
      const count = isMobile ? 40 : 120;
      particlesRef.current = Array.from({ length: count }, () => {
        const ox = Math.random() * width;
        const oy = Math.random() * height;
        return {
          ox,
          oy,
          x: ox,
          y: oy,
          vx: 0,
          vy: 0,
          size: 0.6 + Math.random() * 1.6,
          alpha: 0.22 + Math.random() * 0.55,
          phase: Math.random() * Math.PI * 2,
          driftAmp: 1 + Math.random() * 3,
          driftSpeed: 0.00035 + Math.random() * 0.0012,
          reactivity: 0.55 + Math.random() * 0.9,
          swirl: (Math.random() < 0.5 ? -1 : 1) * (0.2 + Math.random() * 0.4),
          spring: 0.012 + Math.random() * 0.01,
          // Cursor-proximity glow — lerp-smoothed so transitions feel continuous
          glowAlpha: 0,
        };
      });

      // Three drifting halos — unequal size/intensity for depth
      halosRef.current = [
        makeHalo(0.55, 0.55),
        makeHalo(0.4, 0.4),
        makeHalo(0.28, 0.3),
      ];
    };

    const retargetHalo = (h, t) => {
      h.tx = Math.random() * width;
      h.ty = Math.random() * height;
      h.retargetAt = t + 5000 + Math.random() * 5000;
    };

    const draw = (t) => {
      // Frame-time delta — used by the drift-through-space mode so motion is
      // independent of frame rate (looks identical at 30 / 60 / 120 fps).
      const dt = prevT === 0 ? 16 : Math.min(50, t - prevT);
      prevT = t;

      // DRIFT mode (V5 only): translate every star anchor by (vx, vy).
      // When a star exits one edge, respawn it on the OPPOSITE edge with a
      // new random tangential coordinate + reset velocity. Halos pan too,
      // but we just slide their target point — they keep their existing
      // organic wobble + breathing for visual continuity.
      if (driftActive) {
        const dx = DRIFT_VX * dt;
        const dy = DRIFT_VY * dt;
        const margin = 24;

        const particles = particlesRef.current;
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          p.ox += dx;
          p.oy += dy;
          p.x += dx;
          p.y += dy;
          // Respawn on the opposite edge if a star drifts off-canvas
          if (p.ox < -margin) {
            p.ox = width + margin;
            p.oy = Math.random() * height;
            p.x = p.ox;
            p.y = p.oy;
            p.vx = 0;
            p.vy = 0;
          } else if (p.ox > width + margin) {
            p.ox = -margin;
            p.oy = Math.random() * height;
            p.x = p.ox;
            p.y = p.oy;
            p.vx = 0;
            p.vy = 0;
          }
          if (p.oy < -margin) {
            p.oy = height + margin;
            p.ox = Math.random() * width;
            p.x = p.ox;
            p.y = p.oy;
            p.vx = 0;
            p.vy = 0;
          } else if (p.oy > height + margin) {
            p.oy = -margin;
            p.ox = Math.random() * width;
            p.x = p.ox;
            p.y = p.oy;
            p.vx = 0;
            p.vy = 0;
          }
        }

        // Slide halo targets so they pan with the field
        const haloPan = halosRef.current;
        for (let i = 0; i < haloPan.length; i++) {
          haloPan[i].tx += dx;
          haloPan[i].ty += dy;
          haloPan[i].x += dx;
          haloPan[i].y += dy;
        }
      }

      ctx.clearRect(0, 0, width, height);

      // Halos (painted first, behind stars) — wavy, breathing, organic
      const halos = halosRef.current;
      for (let i = 0; i < halos.length; i++) {
        const h = halos[i];
        if (t > h.retargetAt) retargetHalo(h, t);
        // Slow lerp toward target — creates base drift
        h.x += (h.tx - h.x) * 0.0014;
        h.y += (h.ty - h.y) * 0.0014;

        // Non-linear wobble on top of drift (different freq per axis)
        const wobbleX = Math.sin(t * h.wobbleFreqX + h.wobblePhaseX) * h.wobbleAmp;
        const wobbleY = Math.cos(t * h.wobbleFreqY + h.wobblePhaseY) * h.wobbleAmp * 0.7;
        const drawX = h.x + wobbleX;
        const drawY = h.y + wobbleY;

        // Randomized intensity — slow breathing, sometimes faint, sometimes richer
        // Two stacked sines give an uneven "sometimes brighter" feel, not a metronome
        const breathe =
          0.45 +
          0.45 * Math.sin(t * h.breatheFreq + h.breathePhase) +
          0.1 * Math.sin(t * h.breatheFreq * 2.3 + h.breathePhase * 0.7);
        const intensityNow = Math.max(0, h.intensity * breathe);

        // Radius also pulses slightly
        const radiusNow =
          h.radius *
          (0.92 + 0.12 * Math.sin(t * h.pulseFreq + h.pulsePhase));

        const grad = ctx.createRadialGradient(drawX, drawY, 0, drawX, drawY, radiusNow);
        grad.addColorStop(0, `rgba(244, 214, 78, ${0.18 * intensityNow})`);
        grad.addColorStop(0.35, `rgba(244, 214, 78, ${0.075 * intensityNow})`);
        grad.addColorStop(0.7, `rgba(244, 214, 78, ${0.02 * intensityNow})`);
        grad.addColorStop(1, "rgba(244, 214, 78, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      // Star physics — every star reacts to cursor via soft falloff
      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const d1 = Math.sin(t * p.driftSpeed + p.phase) * p.driftAmp;
        const d2 =
          Math.cos(t * p.driftSpeed * 0.8 + p.phase * 1.4) * p.driftAmp * 0.7;
        const targetX = p.ox + d1;
        const targetY = p.oy + d2;

        p.vx += (targetX - p.x) * p.spring;
        p.vy += (targetY - p.y) * p.spring;

        if (mouse.active && !isMobile) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy) + 1;
          // Gentle falloff — every star feels the cursor, but softly.
          // ≈ 0.3 max, decays quickly with distance
          const falloff = 120 / (dist + 80);
          const f = Math.min(0.35, falloff * falloff * 0.5) * p.reactivity;
          const ux = dx / dist;
          const uy = dy / dist;
          p.vx += ux * f;
          p.vy += uy * f;
          // Tangential swirl — keep subtle
          p.vx += -uy * f * p.swirl * 0.5;
          p.vy += ux * f * p.swirl * 0.5;
        }

        p.vx *= 0.9;
        p.vy *= 0.9;
        p.x += p.vx;
        p.y += p.vy;
      }

      // Constellation lines
      if (!isMobile) {
        const linkDist = 100;
        ctx.lineWidth = 0.6;
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const a = particles[i];
            const b = particles[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < linkDist * linkDist) {
              const d = Math.sqrt(d2);
              const alpha = (1 - d / linkDist) * 0.15;
              ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
      }

      // Star dots (with cursor-proximity glow on up to 3 nearest particles)
      // -------------------------------------------------------------------
      // Pick the 3 closest particles within `glowRange` of the cursor.
      // Set their target glowAlpha based on proximity (1 near cursor → 0 at range).
      // All other particles target 0. Lerp per-particle for a smooth
      // fade-in/fade-out as the mouse moves.
      const glowRange = 200;
      const targets = new Array(particles.length).fill(0);
      if (mouse.active && !isMobile) {
        const pool = [];
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < glowRange) pool.push({ i, d });
        }
        pool.sort((a, b) => a.d - b.d);
        const top = pool.slice(0, 3);
        for (let k = 0; k < top.length; k++) {
          const { i, d } = top[k];
          // Quadratic falloff for a soft, non-linear feel
          const proximity = 1 - d / glowRange;
          targets[i] = proximity * proximity;
        }
      }

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        // Lerp glow toward target (≈ 300–400ms fade-in / fade-out)
        p.glowAlpha += (targets[i] - p.glowAlpha) * 0.08;

        if (p.glowAlpha > 0.01) {
          const gR = 22 + p.glowAlpha * 18; // big: 22–40px halo
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, gR);
          grad.addColorStop(0, `rgba(255, 255, 255, ${0.28 * p.glowAlpha})`);
          grad.addColorStop(0.45, `rgba(255, 255, 255, ${0.08 * p.glowAlpha})`);
          grad.addColorStop(1, "rgba(255, 255, 255, 0)");
          ctx.fillStyle = grad;
          ctx.fillRect(p.x - gR, p.y - gR, gR * 2, gR * 2);
        }

        // Subtly brighten the dot core when it's glowing (a "lighter mode" of the dot)
        const dotAlpha = Math.min(1, p.alpha + p.glowAlpha * 0.35);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${dotAlpha})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      // Track relative to the host's rect. Listen on `window` so the
      // overlapping hero copy/buttons don't swallow the event.
      const rect = host.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        mouseRef.current = { x, y, active: true };
      } else {
        mouseRef.current.active = false;
      }
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    if (!isMobile) {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseout", onLeave);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden
      data-testid="hero-ambient"
      className="absolute inset-0 pointer-events-auto overflow-hidden"
      style={{ zIndex: 0 }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
    </div>
  );
};

const Hero = ({ ambientProps, reveal } = {}) => {
  // Per-page CMS overrides — defaults preserve the original visual design.
  const home = useContent("home");
  const h = (home && home.hero) || {};
  const line1 = (h.title_line_1 || "One Partner.").trim();
  const emph = (h.title_emphasis || "Every").trim();
  const line2 = (h.title_line_2 || "Shopify Need.").trim();
  const line1Words = line1.split(/\s+/);
  const line2Words = line2.split(/\s+/);
  const eyebrow = h.eyebrow || "Shopify Experts · Since 2014";
  const subtitle = h.subtitle ||
    "Skip the hassle of searching for freelancers. Our Shopify experts handle everything — from quick fixes to full-scale projects — with speed, quality, and personal attention.";
  const ctaPrimaryLabel = h.cta_primary_label || "Start a Project";
  const ctaPrimaryHref = h.cta_primary_href || "#contact";
  const ctaSecondaryLabel = h.cta_secondary_label || "See How It Works";
  // Legacy CMS values once pointed at a removed "#process" section. Treat
  // that exact string as a sentinel and route the CTA to the hero video
  // anchor — admin can still override via CMS to anything else.
  const ctaSecondaryHref =
    !h.cta_secondary_href || h.cta_secondary_href === "#process"
      ? "#hero-video"
      : h.cta_secondary_href;
  // Word indices for staggered reveal animation
  const idxBase1 = 0;
  const idxEmph = line1Words.length;
  const idxBase2 = line1Words.length + 1;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <HeroAmbient {...(ambientProps || {})} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-20 md:pt-28 md:pb-28">
        <div className={`max-w-4xl ${reveal ? "" : "fade-up"}`}>
          {reveal ? (
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
          ) : (
            <Eyebrow>{eyebrow}</Eyebrow>
          )}

          <h1
            data-testid="hero-title"
            data-reveal={reveal ? "words" : undefined}
            className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.25rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
          >
            {reveal ? (
              <>
                {line1Words.map((w, i) => (
                  <span key={`l1-${i}`}>
                    <span className="reveal-word" style={{ "--word-i": idxBase1 + i }}><span>{w}</span></span>
                    {i < line1Words.length - 1 ? " " : null}
                  </span>
                ))}
                <br />
                <span className="reveal-word" style={{ "--word-i": idxEmph }}>
                  <span className="italic font-normal text-neutral-400 pr-[0.12em]">{emph}</span>
                </span>{" "}
                <span className="relative inline-block">
                  {line2Words.map((w, i) => (
                    <span key={`l2-${i}`}>
                      <span className="reveal-word" style={{ "--word-i": idxBase2 + i }}><span>{w}</span></span>
                      {i < line2Words.length - 1 ? " " : null}
                    </span>
                  ))}
                  <span
                    aria-hidden
                    className="reveal-underline absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
                  />
                </span>
              </>
            ) : (
              <>
                {line1}
                <br />
                <span className="italic font-normal text-neutral-400 pr-[0.12em]">{emph}</span>{" "}
                <span className="relative inline-block">
                  {line2}
                  <span
                    aria-hidden
                    className="absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
                  />
                </span>
              </>
            )}
          </h1>

          {reveal ? (
            <p
              data-reveal="mask-block"
              className="mt-10 max-w-[56ch] text-[1.125rem] md:text-[1.25rem] text-neutral-300 leading-[1.6]"
            >
              <span>{subtitle}</span>
            </p>
          ) : (
            <p className="mt-10 max-w-[56ch] text-[1.125rem] md:text-[1.25rem] text-neutral-300 leading-[1.6]">
              {subtitle}
            </p>
          )}

          <div className="mt-11 flex flex-wrap items-center gap-4">
            <PrimaryButton
              testId="hero-primary-cta"
              href={ctaPrimaryHref}
              variant={reveal ? "marketing" : undefined}
            >
              {ctaPrimaryLabel}
            </PrimaryButton>
            <SecondaryButton
              testId="hero-secondary-cta"
              href={ctaSecondaryHref}
              variant={reveal ? "marketing" : undefined}
            >
              {ctaSecondaryLabel}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   1-V1. HERO (V1 only) — controlled organic system
   ------------------------------------------------------------
   • ~25 nodes placed with Poisson-like rejection sampling (no grid,
     no symmetry, no tight clusters — balanced distribution).
   • Each node pre-pairs with 1–2 nearest neighbours at seed time
     (selective connections, never a full mesh).
   • Very slow sine drift (barely perceptible).
   • Extremely gentle cursor influence; connections flex with node
     positions rather than reacting as a discrete "event".
   • Keeps the two drifting yellow halos from HeroAmbient but dimmer,
     so the scene reads as a calm, intelligent system — not a glow.
============================================================ */
const HeroAmbientV1 = () => {
  const canvasRef = useRef(null);
  const hostRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const dustRef = useRef([]);
  const beamRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      seed();
    };

    /* --- Poisson-disk-ish rejection sampling --- */
    const placeNodes = (target, minDist, attempts) => {
      const pts = [];
      const maxAttempts = attempts || 60;
      let tries = 0;
      const limit = target * maxAttempts;
      while (pts.length < target && tries < limit) {
        const x = 40 + Math.random() * (width - 80);
        const y = 40 + Math.random() * (height - 80);
        let ok = true;
        for (let i = 0; i < pts.length; i++) {
          const dx = pts[i].x - x;
          const dy = pts[i].y - y;
          if (dx * dx + dy * dy < minDist * minDist) {
            ok = false;
            break;
          }
        }
        if (ok) pts.push({ x, y });
        tries++;
      }
      return pts;
    };

    const seed = () => {
      /* --- Bright constellation nodes (20–30, irregular) --- */
      const target = isMobile ? 14 : 24;
      const area = width * height;
      const minDist = Math.sqrt(area / target) * 0.78;
      const pts = placeNodes(target, minDist, 80);

      nodesRef.current = pts.map((_, idx) => {
        const p = pts[idx];
        // ~30% of nodes get a soft glow of varying strength
        const hasGlow = Math.random() < 0.32;
        return {
          ox: p.x,
          oy: p.y,
          x: p.x,
          y: p.y,
          vx: 0,
          vy: 0,
          size: 1.2 + Math.random() * 0.9,
          alpha: 0.55 + Math.random() * 0.35,
          phase: Math.random() * Math.PI * 2,
          // Primary drift — visible but slow
          driftAmp: 1.4 + Math.random() * 2.2,
          driftSpeed: 0.00014 + Math.random() * 0.00034,
          // Secondary very slow wobble — constellation shape evolves over time
          slowPhase: Math.random() * Math.PI * 2,
          slowSpeed: 0.00005 + Math.random() * 0.0001,
          slowAmp: 3 + Math.random() * 4,
          reactivity: 0.35 + Math.random() * 0.45,
          spring: 0.01 + Math.random() * 0.006,
          // Soft glow (yellow or warm-white, random)
          glow: hasGlow ? 0.35 + Math.random() * 0.55 : 0,
          glowPhase: Math.random() * Math.PI * 2,
          glowFreq: 0.0003 + Math.random() * 0.0008,
          glowWarm: Math.random() < 0.45, // yellow vs warm-white
          glowRadius: 10 + Math.random() * 14,
          // Alpha twinkle per-node (very slow)
          twinkleFreq: 0.0002 + Math.random() * 0.0005,
          twinklePhase: Math.random() * Math.PI * 2,
        };
      });

      /* --- Selective, organic edges ---
       * Variable per-node: 70% → 1 edge, 20% → 2 edges, 10% → 0 edges.
       * Pick friends from 4 nearest neighbours so clusters stay tight
       * but not mechanical. Dedupe pairs. */
      const n = nodesRef.current.length;
      const edges = new Set();
      for (let i = 0; i < n; i++) {
        const a = nodesRef.current[i];
        const nbrs = [];
        for (let j = 0; j < n; j++) {
          if (j === i) continue;
          const b = nodesRef.current[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          nbrs.push({ j, d: dx * dx + dy * dy });
        }
        nbrs.sort((x, y) => x.d - y.d);
        const candidates = nbrs.slice(0, 4);
        const r = Math.random();
        let keep;
        if (r < 0.1) keep = 0;
        else if (r < 0.8) keep = 1;
        else keep = 2;
        for (let k = 0; k < keep && k < candidates.length; k++) {
          const a2 = i;
          const b2 = candidates[k].j;
          const key = a2 < b2 ? `${a2}-${b2}` : `${b2}-${a2}`;
          edges.add(key);
        }
      }
      edgesRef.current = Array.from(edges).map((key) => {
        const [a, b] = key.split("-").map(Number);
        return { a, b };
      });

      /* --- Background dust — tiny faint specks, atmospheric texture --- */
      const dustCount = isMobile ? 90 : 220;
      dustRef.current = Array.from({ length: dustCount }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: 0.3 + Math.random() * 0.45,
        alphaBase: 0.05 + Math.random() * 0.1,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleFreq: 0.0003 + Math.random() * 0.0008,
      }));

      /* --- Single soft yellow light beam from top-right --- */
      beamRef.current = {
        // centre slightly off-canvas to the upper-right
        cx: width * 0.88,
        cy: -height * 0.12,
        radius: Math.max(width, height) * 1.15,
        // Smoothed state (lerped each frame) for buttery transitions
        intensity: 0.9,
        spread: 1,
        offsetX: 0,
        offsetY: 0,
        // Phases/frequencies for random-but-unique motion
        i1Phase: Math.random() * Math.PI * 2,
        i2Phase: Math.random() * Math.PI * 2,
        i1Freq: 0.00011,
        i2Freq: 0.00028,
        sPhase: Math.random() * Math.PI * 2,
        sFreq: 0.00013,
        oxPhase: Math.random() * Math.PI * 2,
        oyPhase: Math.random() * Math.PI * 2,
        oxFreq: 0.0001,
        oyFreq: 0.00014,
      };
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, width, height);

      /* --- Soft top-right light column (no visible orb) --- */
      const beam = beamRef.current;
      if (beam) {
        // Compute smooth targets from double-sine composition so the
        // curve never feels periodic or stepped.
        const targetIntensity =
          0.82 +
          0.22 * Math.sin(t * beam.i1Freq + beam.i1Phase) +
          0.08 * Math.sin(t * beam.i2Freq + beam.i2Phase);
        const targetSpread =
          1 + 0.06 * Math.sin(t * beam.sFreq + beam.sPhase);
        const targetOffsetX =
          Math.sin(t * beam.oxFreq + beam.oxPhase) * 48;
        const targetOffsetY =
          Math.cos(t * beam.oyFreq + beam.oyPhase) * 28;

        // Lerp smoothing — filters out any micro-jitter, yields
        // perceptually continuous motion
        beam.intensity += (targetIntensity - beam.intensity) * 0.035;
        beam.spread += (targetSpread - beam.spread) * 0.035;
        beam.offsetX += (targetOffsetX - beam.offsetX) * 0.03;
        beam.offsetY += (targetOffsetY - beam.offsetY) * 0.03;

        const cx = beam.cx + beam.offsetX;
        const cy = beam.cy + beam.offsetY;
        const r = beam.radius * beam.spread;
        const intensity = Math.max(0, beam.intensity);
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, `rgba(255, 236, 160, ${0.22 * intensity})`);
        grad.addColorStop(0.25, `rgba(244, 214, 78, ${0.11 * intensity})`);
        grad.addColorStop(0.55, `rgba(244, 214, 78, ${0.035 * intensity})`);
        grad.addColorStop(0.85, `rgba(244, 214, 78, ${0.01 * intensity})`);
        grad.addColorStop(1, "rgba(244, 214, 78, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      /* --- Background dust with gentle twinkle --- */
      const dust = dustRef.current;
      for (let i = 0; i < dust.length; i++) {
        const d = dust[i];
        const tw =
          0.6 + 0.4 * Math.sin(t * d.twinkleFreq + d.twinklePhase);
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${d.alphaBase * tw})`;
        ctx.fill();
      }

      /* --- Node physics --- */
      const nodes = nodesRef.current;
      const mouse = mouseRef.current;

      for (let i = 0; i < nodes.length; i++) {
        const p = nodes[i];
        // Primary drift
        const d1 = Math.sin(t * p.driftSpeed + p.phase) * p.driftAmp;
        const d2 =
          Math.cos(t * p.driftSpeed * 0.8 + p.phase * 1.4) * p.driftAmp * 0.7;
        // Secondary very-slow wobble — constellation shape evolves
        const s1 = Math.sin(t * p.slowSpeed + p.slowPhase) * p.slowAmp;
        const s2 =
          Math.cos(t * p.slowSpeed * 1.3 + p.slowPhase * 0.6) * p.slowAmp * 0.85;
        const targetX = p.ox + d1 + s1;
        const targetY = p.oy + d2 + s2;

        p.vx += (targetX - p.x) * p.spring;
        p.vy += (targetY - p.y) * p.spring;

        if (mouse.active && !isMobile) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy) + 1;
          // Very gentle shift — max ~3px effective displacement
          const falloff = 70 / (dist + 120);
          const f = Math.min(0.12, falloff * falloff * 0.3) * p.reactivity;
          p.vx += (dx / dist) * f;
          p.vy += (dy / dist) * f;
        }

        p.vx *= 0.9;
        p.vy *= 0.9;
        p.x += p.vx;
        p.y += p.vy;
      }

      /* --- Connections with cursor-aware brightening --- */
      if (!isMobile) {
        ctx.lineWidth = 0.5;
        const edges = edgesRef.current;
        const fadeStart = 200;
        const fadeEnd = 380;
        const proxRange = 160; // how far the cursor's influence reaches edges
        for (let i = 0; i < edges.length; i++) {
          const a = nodes[edges[i].a];
          const b = nodes[edges[i].b];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          let baseAlpha;
          if (d < fadeStart) baseAlpha = 0.16;
          else if (d < fadeEnd)
            baseAlpha = 0.16 * (1 - (d - fadeStart) / (fadeEnd - fadeStart));
          else baseAlpha = 0;
          if (baseAlpha <= 0) continue;

          let alpha = baseAlpha;
          if (mouse.active) {
            // distance from cursor to edge midpoint
            const mx = (a.x + b.x) * 0.5;
            const my = (a.y + b.y) * 0.5;
            const pdx = mouse.x - mx;
            const pdy = mouse.y - my;
            const pd = Math.sqrt(pdx * pdx + pdy * pdy);
            if (pd < proxRange) {
              const boost = 1 - pd / proxRange;
              alpha = Math.min(0.42, baseAlpha + boost * 0.22);
            }
          }
          ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      /* --- Constellation nodes (glow pass + dot) --- */
      for (let i = 0; i < nodes.length; i++) {
        const p = nodes[i];
        // Per-node twinkle — subtle alpha modulation so the dots breathe
        const tw = 0.85 + 0.15 * Math.sin(t * p.twinkleFreq + p.twinklePhase);
        const nodeAlpha = p.alpha * tw;

        // Draw glow first if this node has one
        if (p.glow > 0) {
          const pulse =
            0.55 + 0.45 * Math.sin(t * p.glowFreq + p.glowPhase);
          const gA = p.glow * pulse;
          const gR = p.glowRadius;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, gR);
          if (p.glowWarm) {
            grad.addColorStop(0, `rgba(255, 232, 150, ${0.42 * gA})`);
            grad.addColorStop(0.35, `rgba(244, 214, 78, ${0.14 * gA})`);
            grad.addColorStop(1, "rgba(244, 214, 78, 0)");
          } else {
            grad.addColorStop(0, `rgba(255, 255, 255, ${0.38 * gA})`);
            grad.addColorStop(0.35, `rgba(255, 255, 255, ${0.1 * gA})`);
            grad.addColorStop(1, "rgba(255, 255, 255, 0)");
          }
          ctx.fillStyle = grad;
          ctx.fillRect(p.x - gR, p.y - gR, gR * 2, gR * 2);
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${nodeAlpha})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        active: true,
      };
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    if (!isMobile) {
      host.addEventListener("mousemove", onMove);
      host.addEventListener("mouseleave", onLeave);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden
      data-testid="hero-ambient-v1"
      className="absolute inset-0 pointer-events-auto overflow-hidden"
      style={{ zIndex: 0 }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
    </div>
  );
};

const HeroV1 = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    <HeroAmbientV1 />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-20 md:pt-28 md:pb-28">
      <div className="max-w-4xl fade-up">
        <Eyebrow>Shopify Experts · Since 2014</Eyebrow>

        <h1
          data-testid="hero-title"
          className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.25rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
        >
          One Partner.
          <br />
          <span className="italic font-normal text-neutral-400">Every</span>{" "}
          <span className="relative inline-block">
            Shopify Need.
            <span
              aria-hidden
              className="absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
            />
          </span>
        </h1>

        <p className="mt-10 max-w-[56ch] text-[1.125rem] md:text-[1.25rem] text-neutral-300 leading-[1.6]">
          Skip the hassle of searching for freelancers. Our Shopify experts
          handle everything — from quick fixes to full-scale projects — with
          speed, quality, and personal attention.
        </p>

        <div className="mt-11 flex flex-wrap items-center gap-4">
          <PrimaryButton testId="hero-primary-cta" href="#contact">
            Start a Project
          </PrimaryButton>
          <SecondaryButton testId="hero-secondary-cta" href="#process">
            See How It Works
          </SecondaryButton>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   1-V3. HERO (V3 only) — cinematic light beam environment
   ------------------------------------------------------------
   Deliberately stripped. No particle system, no nodes, no lines,
   no constellation logic. One soft, diffused diagonal light beam
   from top-right + 8 barely-visible dust motes. Gentle breathing
   on intensity/spread; cursor subtly tilts the beam.
============================================================ */
const HeroAmbientV3 = () => {
  const canvasRef = useRef(null);
  const hostRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const beamRef = useRef(null);
  const dustRef = useRef([]);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      seed();
    };

    const seed = () => {
      // Apple-style focused beam: anchored slightly deeper off-canvas
      // upper-right, casts a longer, more directed diagonal across the frame.
      beamRef.current = {
        baseX: width * 1.02,
        baseY: -height * 0.22,
        intensity: 1,
        spread: 1,
        offsetX: 0,
        offsetY: 0,
        iPhaseA: Math.random() * Math.PI * 2,
        iPhaseB: Math.random() * Math.PI * 2,
        iFreqA: 0.00011,
        iFreqB: 0.00026,
        sPhase: Math.random() * Math.PI * 2,
        sFreq: 0.00013,
      };

      // Even fewer, cleaner dust motes — Apple restraint
      const count = isMobile ? 4 : 6;
      dustRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.05,
        vy: (Math.random() - 0.3) * 0.035,
        size: 0.55 + Math.random() * 0.7,
        alpha: 0.04 + Math.random() * 0.05,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleFreq: 0.00025 + Math.random() * 0.00045,
      }));
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, width, height);

      const b = beamRef.current;
      if (b) {
        const targetIntensity =
          0.88 +
          0.12 * Math.sin(t * b.iFreqA + b.iPhaseA) +
          0.05 * Math.sin(t * b.iFreqB + b.iPhaseB);
        const targetSpread =
          1 + 0.05 * Math.sin(t * b.sFreq + b.sPhase);

        let targetOffsetX = 0;
        let targetOffsetY = 0;
        if (mouseRef.current.active) {
          const dx = mouseRef.current.x - width * 0.5;
          const dy = mouseRef.current.y - height * 0.5;
          // Even gentler — an Apple "barely any" cursor response
          targetOffsetX = -dx * 0.025;
          targetOffsetY = -dy * 0.018;
        }

        b.intensity += (targetIntensity - b.intensity) * 0.04;
        b.spread += (targetSpread - b.spread) * 0.035;
        b.offsetX += (targetOffsetX - b.offsetX) * 0.03;
        b.offsetY += (targetOffsetY - b.offsetY) * 0.03;

        const cx = b.baseX + b.offsetX;
        const cy = b.baseY + b.offsetY;
        // Slightly tighter falloff → more "directed" beam feel
        const r = Math.max(width, height) * 1.2 * b.spread;
        const I = Math.max(0, b.intensity);

        // Apple-style luxury beam:
        // Core reads as clean warm-cream (almost neutral white with a
        // whisper of warmth), brand yellow only emerges in the mid
        // falloff, then dissolves fully into the dark. No hard edges.
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, `rgba(255, 244, 220, ${0.16 * I})`);
        grad.addColorStop(0.1, `rgba(255, 236, 195, ${0.1 * I})`);
        grad.addColorStop(0.28, `rgba(250, 222, 150, ${0.055 * I})`);
        grad.addColorStop(0.5, `rgba(244, 214, 78, ${0.022 * I})`);
        grad.addColorStop(0.78, `rgba(244, 214, 78, ${0.006 * I})`);
        grad.addColorStop(1, "rgba(244, 214, 78, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      const dust = dustRef.current;
      for (let i = 0; i < dust.length; i++) {
        const d = dust[i];
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < -10) d.x = width + 10;
        if (d.x > width + 10) d.x = -10;
        if (d.y < -10) d.y = height + 10;
        if (d.y > height + 10) d.y = -10;

        const tw = 0.6 + 0.4 * Math.sin(t * d.twinkleFreq + d.twinklePhase);
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${d.alpha * tw})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        mouseRef.current = { x, y, active: true };
      } else {
        mouseRef.current.active = false;
      }
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    if (!isMobile) {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseout", onLeave);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden
      data-testid="hero-ambient-v3"
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 0 }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
};

const HeroV3 = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden bg-[#07090D]"
  >
    <HeroAmbientV3 />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-20 md:pt-28 md:pb-28">
      <div className="max-w-4xl fade-up">
        <Eyebrow>Shopify Experts · Since 2014</Eyebrow>

        <h1
          data-testid="hero-title"
          className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.25rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
        >
          One Partner.
          <br />
          <span className="italic font-normal text-neutral-400">Every</span>{" "}
          <span className="relative inline-block">
            Shopify Need.
            <span
              aria-hidden
              className="absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
            />
          </span>
        </h1>

        <p className="mt-10 max-w-[56ch] text-[1.125rem] md:text-[1.25rem] text-neutral-300 leading-[1.6]">
          Skip the hassle of searching for freelancers. Our Shopify experts
          handle everything — from quick fixes to full-scale projects — with
          speed, quality, and personal attention.
        </p>

        <div className="mt-11 flex flex-wrap items-center gap-4">
          <PrimaryButton testId="hero-primary-cta" href="#contact">
            Start a Project
          </PrimaryButton>
          <SecondaryButton testId="hero-secondary-cta" href="#process">
            See How It Works
          </SecondaryButton>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   1b. TRUST STRIP — thin horizontal under the hero
============================================================ */

const trustMetrics = [
  { value: "250+", label: "Custom Features" },
  { value: "10+", label: "Years on Shopify" },
  { value: "24–48h", label: "Turnaround" },
  { value: "30+", label: "Trusted Stores" },
];

const TrustStrip = () => (
  <section
    data-testid="trust-strip"
    className="relative bg-[#0B0F14] border-y border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 md:py-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3"
      >
        {trustMetrics.map((m, i) => (
          <div
            key={m.label}
            data-testid={`trust-metric-${i + 1}`}
            className="flex items-center gap-6"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-display text-[1.05rem] md:text-[1.2rem] font-black tracking-tight text-white">
                {m.value}
              </span>
              <span className="text-[0.72rem] md:text-[0.78rem] text-neutral-400 font-medium">
                {m.label}
              </span>
            </div>
            {i < trustMetrics.length - 1 && (
              <span
                aria-hidden
                className="hidden md:inline-block w-1 h-1 rounded-full bg-[#F4D64E]/50"
              />
            )}
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   1c. LOGO STRIP — premium, grayscale, one row
============================================================ */

const clientLogos = [
  "Veronique Gabai",
  "Cafe Joyeux",
  "Teddy Blake NYC",
  "Rose Forever",
  "SWAND",
  "Sclarandis",
];

const LogoStrip = () => {
  // duplicate the list for a seamless left→right loop
  const loop = [...clientLogos, ...clientLogos];
  return (
    <section data-testid="logo-strip" className="relative bg-[#0B0F14]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10"
        >
          <span className="text-[0.64rem] md:text-[0.68rem] tracking-[0.24em] uppercase text-neutral-500 font-semibold shrink-0">
            Trusted by
          </span>

          {/* Marquee track */}
          <div
            data-testid="logo-marquee"
            className="marquee-group relative flex-1 overflow-hidden marquee-fade"
          >
            <div
              aria-hidden="false"
              className="animate-marquee-ltr flex items-center gap-12 md:gap-16 w-max"
            >
              {loop.map((name, i) => {
                const isOriginal = i < clientLogos.length;
                return (
                  <span
                    key={`${name}-${i}`}
                    data-testid={isOriginal ? `logo-${i + 1}` : undefined}
                    aria-hidden={!isOriginal}
                    className="font-display text-[0.95rem] md:text-[1rem] tracking-[0.01em] text-neutral-300/55 hover:text-neutral-100 transition-colors duration-500 cursor-default select-none whitespace-nowrap"
                    style={{ fontFeatureSettings: "'ss01'" }}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ============================================================
   1c-V2. LOGO STRIP (V2) — two rows, image-based, counter-scrolling
============================================================ */

const clientLogosV2 = [
  {
    name: "Veronique Gabai",
    src: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/74tni3x0_2825e286-618a-789d-c6bf-1187d8bec1d4-w.png",
  },
  {
    name: "Teddy Blake NYC",
    src: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/ybb2cwe5_logo-black-popup-w.png",
  },
  {
    name: "Madam Glam",
    src: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/rbg2w0na_MadamGlam_Logo_Desktop_Pro_59bf4872-8eb7-4482-ab03-ee0533ba98b8-w.png",
  },
  {
    name: "MG",
    src: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/24xxyvv0_mg_logo_1X_a48efdf6-973b-4e68-b2e5-30f56d5c5124-w.png",
  },
  {
    name: "MYW",
    src: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/yf6y72pp_myw_logo_type-w.png",
  },
];

// Build 8 slots from the 5 available logos — repeat some to fill,
// then split into two rows of 4 that don't start with the same logo.
const logoSlotsV2 = [
  clientLogosV2[0],
  clientLogosV2[1],
  clientLogosV2[2],
  clientLogosV2[3],
  clientLogosV2[4],
  clientLogosV2[0], // repeat
  clientLogosV2[2], // repeat
  clientLogosV2[4], // repeat
];

const LogoRowV2 = ({ logos, direction = "ltr", testId, style = "color", speed }) => {
  const animClass =
    direction === "ltr" ? "animate-marquee-ltr" : "animate-marquee-rtl";
  const loop = [...logos, ...logos];
  // style filter — matches the admin "Display style" select
  const filterClass =
    style === "white"
      ? "brightness-0 invert"
      : style === "grayscale"
      ? "grayscale contrast-125"
      : "";
  // Optional inline duration override — when the admin has set a
  // speed in the CMS, apply it as `animation-duration` to override
  // the CSS default (45s).
  const rowStyle =
    Number.isFinite(speed) && speed > 0
      ? { animationDuration: `${speed}s` }
      : undefined;
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden marquee-fade"
    >
      <div
        className={`${animClass} flex items-center gap-16 md:gap-24 w-max`}
        style={rowStyle}
      >
        {loop.map((l, i) => {
          const isOriginal = i < logos.length;
          const src = l.src || l.media;
          const name = l.name || l.brand || "";
          const href = l.href || "";
          const resolvedSrc =
            src && /^https?:\/\//i.test(src)
              ? src
              : src
              ? `${process.env.REACT_APP_BACKEND_URL}${src.startsWith("/") ? "" : "/"}${src}`
              : "";
          const content = (
            <img
              src={resolvedSrc}
              alt={name}
              loading="lazy"
              className={`h-full w-auto object-contain select-none ${filterClass}`}
              draggable={false}
              style={{ maxWidth: "180px" }}
            />
          );
          if (!resolvedSrc) return null;
          return (
            <div
              key={`${name}-${i}`}
              data-testid={isOriginal ? `${testId}-logo-${i + 1}` : undefined}
              aria-hidden={!isOriginal}
              className="shrink-0 h-10 md:h-12 flex items-center"
              title={name}
            >
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-full flex items-center"
                >
                  {content}
                </a>
              ) : (
                content
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LogoStripV2 = () => {
  // Read CMS overrides. Falls back to the hardcoded logo slots so the
  // live design is preserved when an admin hasn't added any logos yet.
  const home = useContent("home");
  const ls = (home && home.logo_strip) || {};
  const cmsLine1 = Array.isArray(ls.line_1) ? ls.line_1.filter((l) => l && (l.media || l.src)) : [];
  const cmsLine2 = Array.isArray(ls.line_2) ? ls.line_2.filter((l) => l && (l.media || l.src)) : [];
  const style = ls.style || "color";
  const row1 = cmsLine1.length ? cmsLine1 : logoSlotsV2.slice(0, 4);
  const row2 = cmsLine2.length ? cmsLine2 : logoSlotsV2.slice(4, 8);
  // Per-row scroll speed (seconds per loop). Falls back to the CSS
  // default (45s) when unset. Admin-editable from /admin/content/home.
  const speed1 = Number(ls.line_1_speed);
  const speed2 = Number(ls.line_2_speed);
  return (
    <section data-testid="logo-strip-v2" className="relative bg-[#0B0F14]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-6 md:gap-8"
        >
          <LogoRowV2 logos={row1} direction="ltr" testId="logo-row-1" style={style} speed={speed1} />
          <LogoRowV2 logos={row2} direction="rtl" testId="logo-row-2" style={style} speed={speed2} />
        </motion.div>
      </div>
    </section>
  );
};

/* ============================================================
   2. SHOPIFY DEVELOPMENT — consolidated
============================================================ */

const devPillars = [
  {
    icon: Wrench,
    title: "Small tasks",
    body: "On-demand Shopify development for quick fixes, updates, and enhancements.",
  },
  {
    icon: Rocket,
    title: "Full projects",
    body: "Complete Shopify builds, custom redesigns, and scalable growth systems.",
  },
  {
    icon: Wallet,
    title: "Cost-conscious",
    body: "Expert results engineered around your budget — no inflated retainers.",
  },
  {
    icon: Zap,
    title: "Fast & efficient",
    body: "Swift turnaround without compromising code quality or design rigor.",
  },
];

/* ----- Word-by-word scroll reveal — scroll-driven, reading-paced ----- */
const RevealWord = ({ word, start, end, progress, isLast, accent = false }) => {
  // Softer initial state (very low contrast) → full white.
  // Linear interpolation (no easing) so motion feels like "appearance",
  // not a UI animation.
  const color = useTransform(
    progress,
    [start, end],
    accent
      ? ["rgba(244, 214, 78, 0.12)", "rgba(244, 214, 78, 1)"]
      : ["rgba(255, 255, 255, 0.08)", "rgba(255, 255, 255, 1)"],
    { clamp: true }
  );
  // Extremely subtle letter-spacing: slightly wider while hidden, normal when revealed
  const letterSpacing = useTransform(
    progress,
    [start, end],
    ["0.012em", "0em"],
    { clamp: true }
  );
  return (
    <>
      <motion.span
        style={{ color, letterSpacing }}
        className={accent ? "font-semibold" : undefined}
      >
        {word}
      </motion.span>
      {!isLast && " "}
    </>
  );
};

const ScrollRevealText = ({
  text,
  progress,
  className = "",
  accentWords = [],
  as: Tag = "div",
}) => {
  const words = text.split(" ");
  const n = words.length;
  return (
    <Tag className={className}>
      {words.map((word, i) => {
        // Base slot per word — narrow so each word has its own moment
        const span = 1 / n;
        // Deterministic micro-jitter so neighbouring words don't march in
        // perfect lockstep. Stable across renders (index-based, no randomness).
        const offset = Math.sin(i * 1.73) * span * 0.3;
        // Each word reveals across ~1.4 spans so the reading wave overlaps
        // gently rather than stepping word-by-word.
        const start = Math.max(0, i * span + offset);
        const end = Math.min(1, start + span * 1.4);
        return (
          <RevealWord
            key={`${word}-${i}`}
            word={word}
            start={start}
            end={end}
            progress={progress}
            isLast={i === n - 1}
            accent={accentWords.includes(i)}
          />
        );
      })}
    </Tag>
  );
};

const DevelopmentOverview = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "start 0.15"],
  });

  // CMS override — falls back to the live hard-coded paragraph when
  // the admin hasn't touched this section. Admin-managed via
  // /admin/content/home → "Development overview".
  const home = useContent("home");
  const body =
    (home && home.development_overview && home.development_overview.body) ||
    "From small fixes to full Shopify builds — done right. One team for every Shopify and Shopify Plus need — custom themes, advanced functionality, integrations, and optimized workflows. Over 250 custom features built. If it's logical, it's codable.";

  return (
    <section
      id="development"
      data-testid="development-section"
      ref={ref}
      className="relative bg-[#11161D]"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <ScrollRevealText
          as="p"
          text={body}
          progress={scrollYProgress}
          className="font-display text-[1.625rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[2.85rem] font-semibold tracking-[-0.02em] leading-[1.22] text-center"
        />
      </div>
    </section>
  );
};

/* ============================================================
   3. HOW IT WORKS
============================================================ */

const processSteps = [
  {
    no: "01",
    title: "Contact & Connect",
    body: "Meet a Customer Success Manager who understands how design, development, marketing, and branding combine to produce results.",
  },
  {
    no: "02",
    title: "Consultation",
    body: "Expert consulting up-front — so every project aligns with your goals, your tech stack, and your growth targets.",
  },
  {
    no: "03",
    title: "Execution & Delivery",
    body: "From concept to completion: high-quality, fully tested solutions delivered on time and ready to scale.",
  },
];

const HowItWorks = () => (
  <section
    id="process"
    data-testid="process-section"
    className="bg-[#0B0F14]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <Eyebrow>Simple Process</Eyebrow>
          <h2 className="font-display mt-7 text-[2.5rem] md:text-[3.5rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white max-w-2xl">
            How it works.
          </h2>
        </div>
        <p className="text-[1rem] md:text-[1.08rem] text-neutral-400 leading-[1.65] max-w-[40ch]">
          A clear, three-step engagement — no opaque briefs, no missed deadlines.
        </p>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
        <div className="hidden md:block absolute top-6 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-[#F4D64E]/30 to-transparent" />

        {processSteps.map((step, i) => (
          <div
            key={step.no}
            data-testid={`process-step-${i + 1}`}
            className="relative flex flex-col gap-5"
          >
            <div className="relative flex items-center gap-4">
              <div className="relative z-10 w-12 h-12 rounded-full bg-[#F4D64E] text-neutral-950 flex items-center justify-center font-display font-black text-sm tracking-wide">
                {step.no}
              </div>
              <div className="font-display text-[1.35rem] md:text-[1.5rem] font-bold tracking-[-0.01em] text-white">
                {step.title}
              </div>
            </div>
            <p className="text-[1rem] md:text-[1.05rem] text-neutral-400 leading-[1.65] max-w-[38ch]">
              {step.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   3-V2. HOW IT WORKS — V2 only
   ------------------------------------------------------------
   Bigger cards, oversized background numeral, icon chip, tags,
   animated connecting rail, scroll-reveal with staggered entry,
   hover lift. Heading + description unchanged.
============================================================ */
const processStepsV2 = [
  {
    no: "01",
    title: "Contact & Connect",
    body: "Meet a Customer Success Manager who understands how design, development, marketing, and branding combine to produce results.",
    icon: Target,
    tags: ["Discovery call", "Success Manager", "Goals alignment"],
  },
  {
    no: "02",
    title: "Consultation",
    body: "Expert consulting up-front — so every project aligns with your goals, your tech stack, and your growth targets.",
    icon: Search,
    tags: ["Technical audit", "Roadmap", "Stack alignment"],
  },
  {
    no: "03",
    title: "Execution & Delivery",
    body: "From concept to completion: high-quality, fully tested solutions delivered on time and ready to scale.",
    icon: Rocket,
    tags: ["Design & build", "QA tested", "On-time handoff"],
  },
];

/* ============================================================
   3-V2b. HERO VIDEO (V2 only) — premium inline player
   ------------------------------------------------------------
   Sits above "Simple Process". Wide (but not edge-to-edge)
   rounded container. Custom poster (first frame shown under a
   dark gradient + glassy play button). Clicking starts inline
   playback with native controls (which include fullscreen and
   seek). No modal, no auto-play. `playsInline` for iOS.
============================================================ */
const HEROVIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/wxuo5if7_invideo-ai-1080%20Stop%20Searching.%20Start%20Shipping%E2%80%94Your%20Shop%202026-02-06.mp4";

const HeroVideoV2 = ({ engageOnScroll = false } = {}) => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);

  // CMS override — admin-managed via /admin/content/home → "Hero video".
  // Falls back to the original hard-coded URL when unset. Relative
  // upload paths (e.g. /uploads/foo.mp4) are resolved against
  // REACT_APP_BACKEND_URL.
  const home = useContent("home");
  const hv = (home && home.hero_video) || {};
  const rawSrc = hv.media || HEROVIDEO_SRC;
  // Overlay caption overrides — keep current copy as defaults so the
  // public layout is byte-for-byte unchanged until the admin edits it.
  const overlayEyebrow = hv.eyebrow || "Watch · 60s";
  const overlayHeadline = hv.headline || "Stop searching. Start shipping.";
  const overlayCtaHint = hv.cta_hint || "Click to play";
  const resolvedSrc =
    rawSrc && /^https?:\/\//i.test(rawSrc) ? rawSrc :
    rawSrc ? `${process.env.REACT_APP_BACKEND_URL || ""}${rawSrc.startsWith("/") ? "" : "/"}${rawSrc}` :
    "";

  const handlePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    // Hide the overlay first — if we wait for play() to resolve, iOS
    // can swallow the user-gesture context that allows playback.
    setStarted(true);
    try {
      const p = v.play();
      // .play() returns a Promise on modern browsers. Catch the
      // rejection (e.g. user navigated away) but keep the UI in the
      // started state because the native controls now own the flow.
      if (p && typeof p.then === "function") {
        await p;
      }
    } catch (_) {
      // Fall back to the overlay so the user can try again.
      setStarted(false);
    }
  };

  // V5 / main only — scroll-linked scale: card enters small, grows to full
  // size as the section scrolls into the centre of the viewport. Pairs with
  // the page's signature mask-wipe reveal for a single editorial entrance.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "center center"],
  });
  const scaleEngaged = useTransform(scrollYProgress, [0, 1], [0.78, 1]);

  const cardClasses =
    "relative aspect-video overflow-hidden rounded-[1.5rem] md:rounded-[2.25rem] border border-white/[0.07] bg-[#0B0F14] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.75)]";

  const cardContents = (
    <>
      {/* Video — native controls are always mounted so iOS Safari
          doesn't pause the stream when the `controls` attribute is
          toggled mid-playback (the cause of the mobile "starts for
          1s then pauses" bug). The overlay button below sits ON TOP
          of the video and intercepts the first tap, so visually the
          native controls only appear after the user starts playback. */}
      <video
        ref={videoRef}
        data-testid="hero-video-player"
        src={resolvedSrc}
        preload="metadata"
        playsInline
        controls
        controlsList="nodownload"
        onEnded={() => setStarted(false)}
        className="absolute inset-0 w-full h-full object-cover bg-black"
      />

      {/* Poster overlay — shown until the user clicks play */}
      {!started && (
        <motion.button
          type="button"
          onClick={handlePlay}
          data-testid="hero-video-play"
          aria-label="Play video"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 group cursor-pointer focus:outline-none"
        >
          {/* Top-to-bottom cinematic gradient — preserves readability */}
          <span
            aria-hidden
            className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/35 via-transparent to-[#0B0F14]/80"
          />
          {/* Subtle yellow vignette in the top-right, brand-tinted */}
          <span
            aria-hidden
            className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl pointer-events-none"
          />

          {/* Centered play button — glass-morphism with pulse ring */}
          <span className="absolute inset-0 flex items-center justify-center">
            <span
              aria-hidden
              className="relative flex items-center justify-center w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/10 backdrop-blur-xl border border-white/25 transition-all duration-500 ease-out group-hover:bg-[#F4D64E] group-hover:border-[#F4D64E] group-hover:scale-105 group-active:scale-95"
            >
              {/* Outer pulse ring */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full border border-white/20 scale-110 opacity-0 group-hover:opacity-100 group-hover:scale-125 transition-all duration-700"
              />
              <Play
                className="w-8 h-8 md:w-10 md:h-10 text-white fill-current ml-1 transition-colors duration-500 group-hover:text-neutral-950"
              />
            </span>
          </span>

          {/* Bottom-left caption row */}
          <span className="absolute bottom-6 md:bottom-10 left-6 md:left-10 right-6 md:right-10 flex items-end justify-between gap-6 pointer-events-none">
            <span className="flex flex-col items-start">
              <span className="text-[0.64rem] md:text-[0.72rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/90">
                {overlayEyebrow}
              </span>
              <span className="mt-3 font-display text-[1.25rem] md:text-[1.75rem] font-bold text-white tracking-[-0.015em] leading-[1.15] max-w-[28ch]">
                {overlayHeadline}
              </span>
            </span>
            <span className="hidden md:inline-flex items-center gap-2 text-[0.68rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
              {overlayCtaHint}
              <span className="h-px w-8 bg-neutral-500/60" />
            </span>
          </span>
        </motion.button>
      )}
    </>
  );

  return (
    <section id="hero-video" ref={sectionRef} data-testid="hero-video-v2" className="relative bg-[#0B0F14]">
      {/* Container width is capped by viewport height so the 16:9 card never
          exceeds the screen on Mac/laptop, while still using almost all the
          available width on larger displays. Padding is kept tight so the
          card dominates the viewport. */}
      <div className="mx-auto px-4 sm:px-6 md:px-8 pt-10 md:pt-14 pb-6 md:pb-8 max-w-[60rem] xl:max-w-[min(100rem,calc((100svh-9rem)*16/9))]">
        {engageOnScroll ? (
          // V5 / main — slow elegant slide-up from below, layered with the
          // scroll-linked scale on the inner motion.div.
          <motion.div
            initial={{ opacity: 0, y: 96 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.18 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              style={{ scale: scaleEngaged, transformOrigin: "center center" }}
              className={cardClasses}
            >
              {cardContents}
            </motion.div>
          </motion.div>
        ) : (
          // V2 — original simple fade-up reveal, untouched.
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className={cardClasses}
          >
            {cardContents}
          </motion.div>
        )}
      </div>
    </section>
  );
};

const ProcessStepCardV2 = ({ step, index, total }) => {
  const Icon = step.icon;
  // Each step has its own "turn" in the reveal sequence
  const baseDelay = index * 0.55;
  return (
    <div
      data-testid={`process-step-v2-${index + 1}`}
      className="group relative flex flex-col"
    >
      {/* Top row: yellow dot + step label (no rail here — moved below) */}
      <div className="relative flex items-center">
        {/* Dot — scales in with spring */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
            delay: baseDelay,
          }}
          className="relative z-10 w-12 h-12 rounded-full bg-[#F4D64E] text-neutral-950 flex items-center justify-center font-display font-black text-sm tracking-wide shadow-[0_0_0_6px_rgba(244,214,78,0.1)]"
        >
          {step.no}
        </motion.div>

        {/* Step label fades in right after the dot */}
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1],
            delay: baseDelay + 0.15,
          }}
          className="ml-4 flex items-center gap-2 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500"
        >
          <span>Step</span>
          <span className="tabular-nums text-[#F4D64E]">{step.no}</span>
        </motion.div>
      </div>

      {/* Rail row — lives BELOW the dot row, not through it.
          A short hairline continues from under this step into the next
          column and ends with a faint chevron, reading as a timeline
          rather than a stroke through the dots. Skipped on the last step. */}
      {index < total - 1 && (
        <motion.div
          aria-hidden
          initial={{ scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
            delay: baseDelay + 0.35,
          }}
          style={{ transformOrigin: "left center" }}
          className="hidden md:flex items-center gap-2 mt-6 ml-0 pr-0 lg:pr-0 w-[calc(100%+3rem)] lg:w-[calc(100%+4rem)]"
        >
          <span className="flex-1 h-px bg-gradient-to-r from-[#F4D64E]/50 via-[#F4D64E]/25 to-transparent" />
          <ArrowRight className="w-3 h-3 text-[#F4D64E]/40 shrink-0" />
        </motion.div>
      )}

      {/* Content — fades up as a group, staggered per step */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1],
          delay: baseDelay + 0.25,
        }}
        className={`relative flex flex-col gap-5 max-w-[38ch] ${
          index < total - 1 ? "mt-8" : "mt-10"
        }`}
      >
        {/* Icon chip */}
        <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#F4D64E]/10 border border-[#F4D64E]/25 transition-all duration-500 group-hover:bg-[#F4D64E]/15 group-hover:border-[#F4D64E]/45">
          <Icon className="w-[1.1rem] h-[1.1rem] text-[#F4D64E]" strokeWidth={1.75} />
        </div>

        {/* Title */}
        <h3 className="font-display text-[1.5rem] md:text-[1.75rem] font-black tracking-[-0.02em] leading-[1.1] text-white">
          {step.title}
        </h3>

        {/* Body */}
        <p className="text-[0.98rem] md:text-[1.02rem] text-neutral-400 leading-[1.65]">
          {step.body}
        </p>

        {/* Tags */}
        <div className="mt-2 flex flex-wrap gap-2">
          {step.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[0.78rem] text-neutral-300 transition-colors duration-300 group-hover:border-[#F4D64E]/25 group-hover:bg-[#F4D64E]/[0.04]"
            >
              <span className="w-1 h-1 rounded-full bg-[#F4D64E]/70" />
              {t}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const HowItWorksV2 = () => (
  <section
    id="process"
    data-testid="process-section"
    className="bg-[#0B0F14]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20">
        <div>
          <Eyebrow>Simple Process</Eyebrow>
          <h2 className="font-display mt-7 text-[2.5rem] md:text-[3.5rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white max-w-2xl">
            How it works.
          </h2>
        </div>
        <p className="text-[1rem] md:text-[1.08rem] text-neutral-400 leading-[1.65] max-w-[40ch]">
          A clear, three-step engagement — no opaque briefs, no missed deadlines.
        </p>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
        {processStepsV2.map((step, i) => (
          <ProcessStepCardV2
            key={step.no}
            step={step}
            index={i}
            total={processStepsV2.length}
          />
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   4. SHOPIFY SERVICES
============================================================ */

const services = [
  {
    tag: "01 / Theme",
    title: "Custom Theme Development",
    body: "Responsive Shopify themes that combine smart design with advanced functionality to boost engagement and conversions.",
  },
  {
    tag: "02 / Ops",
    title: "Store Maintenance",
    body: "Regular updates and optimizations that keep your store running smoothly and scaling with your business.",
  },
  {
    tag: "03 / Build",
    title: "App Development",
    body: "Web applications that streamline operations, engage customers, and deliver a smooth experience end-to-end.",
  },
  {
    tag: "04 / Custom",
    title: "Shopify Custom Tasks",
    body: "Push beyond Shopify limits. We customize your store to meet unique requirements — the way you actually work.",
  },
  {
    tag: "05 / Migrate",
    title: "Migration to Shopify",
    body: "Hassle-free migrations that unlock the full potential of Shopify and set your business up for long-term growth.",
  },
  {
    tag: "06 / Partner",
    title: "White Label Development",
    body: "Our in-house team delivers reliable, scalable white-label Shopify development with full support for your clients.",
  },
];

const ServicesGrid = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative overflow-hidden bg-[#11161D]"
  >
    <SectionAmbient variant="a" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
        <PillarHeader
          eyebrow="Our Shopify Services"
          description="Comprehensive Shopify services tailored to your business needs — from the storefront to the checkout to the back office."
        >
          <h2 className={pillarHeadingClass}>
            Development expertise — across every surface of{" "}
            <PillarAccent>your store</PillarAccent>.
          </h2>
        </PillarHeader>
        <a
          href="#contact"
          data-testid="services-all-link"
          className="hidden md:inline-flex items-center gap-2 text-sm font-bold tracking-wide text-[#F4D64E] link-underline whitespace-nowrap"
        >
          See all services <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
        }}
      >
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            data-testid={`service-card-${i + 1}`}
            variants={{
              hidden: { opacity: 0, y: 24 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
              },
            }}
            className="group relative bg-[#151B23] border border-white/[0.06] rounded-lg p-8 md:p-9 flex flex-col gap-5 hover:bg-[#1B2330] hover:border-[#F4D64E]/30 hover:-translate-y-0.5 transition-all duration-400 overflow-hidden"
          >
            <div className="flex items-center justify-between">
              <span className="text-[0.7rem] font-bold tracking-[0.2em] uppercase text-[#F4D64E]/80">
                {s.tag}
              </span>
              <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-[#F4D64E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
            </div>
            <h3 className="font-display text-[1.3rem] md:text-[1.5rem] font-bold tracking-[-0.015em] text-white leading-[1.2]">
              {s.title}
            </h3>
            <p className="text-[0.98rem] md:text-[1rem] text-neutral-400 leading-[1.65]">
              {s.body}
            </p>
            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#F4D64E] group-hover:w-full transition-all duration-500" />
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   4-V2. SHOPIFY SERVICES — V2 only
   ------------------------------------------------------------
   Bigger 2-column cards, oversized index numeral anchor, big
   title typography, layered reveal (index → title → body →
   footer) with staggered entrance across the grid. Heading,
   description, and copy unchanged.
============================================================ */
/* ============================================================
   4-V2. SHOPIFY SERVICES — V2 only (horizontal hover accordion)
   ------------------------------------------------------------
   Six vertical panels, always visible. Hovering expands the
   target panel and compresses its siblings. Each panel has a
   huge translucent representative icon behind the content, a
   compact "teaser" state (icon + vertical title + tag), and a
   rich expanded state (body, key points, CTA). Keyboard + tap
   accessible via click-to-activate. Mobile falls back to a
   clean stacked list.
============================================================ */

// Icon per service — picks a representative lucide glyph for each title
const serviceIconsV2 = [
  Palette, // Custom Theme Development
  Wrench, // Store Maintenance
  Layers, // App Development
  Code2, // Shopify Custom Tasks
  Shuffle, // Migration to Shopify
  Briefcase, // White Label Development
];

// Maps the schema's icon name → the actual lucide component.
// Used by the CMS-driven service rows so that icon stays bound to
// the service item rather than its position in the array.
const SERVICE_ICON_MAP = {
  Palette,
  Wrench,
  Layers,
  Code2,
  Shuffle,
  Briefcase,
  Rocket,
  Target,
  Search,
  Wallet,
  Zap,
  Video,
  MousePointerClick,
  PenTool,
  Film,
};

// 3 bullet points per service — previewed in the expanded state
const serviceHighlightsV2 = [
  ["Bespoke theme builds", "Responsive & accessible", "Conversion-tuned UI"],
  ["Updates & patches", "Performance tuning", "Ongoing monitoring"],
  ["Custom storefronts", "Admin apps & tooling", "API integrations"],
  ["Workflow automations", "Custom features", "Unique requirements"],
  ["Zero-downtime moves", "Data integrity", "Re-platform strategy"],
  ["Partner delivery", "Full lifecycle support", "Your brand, our team"],
];

const ServicePanelV2 = ({ s, index, active, onActivate }) => {
  const Icon = serviceIconsV2[index] || Code2;
  const [num, label] = s.tag.split(" / ");
  const highlights = serviceHighlightsV2[index] || [];
  const isActive = active === index;
  const anyActive = active !== null && active !== undefined;

  // ONE shared transition config — 400ms ease-in-out, used by every
  // animated property on this panel so nothing races ahead of anything else.
  const T = { duration: 0.4, ease: [0.4, 0, 0.2, 1] };

  // Framer animates `flex` reliably across browsers (unlike CSS-transitioned
  // flex-grow, which Chrome often snaps). Expanded panels take 4.5x the base
  // width, siblings compress to 0.6x.
  const flexValue = isActive ? 4.5 : anyActive ? 0.6 : 1;

  return (
    <motion.div
      data-testid={`service-panel-v2-${index + 1}`}
      onMouseEnter={() => onActivate(index)}
      onFocus={() => onActivate(index)}
      onClick={() => onActivate(index)}
      tabIndex={0}
      role="button"
      aria-expanded={isActive}
      aria-label={s.title}
      data-active={isActive}
      animate={{ flex: flexValue }}
      transition={T}
      className={`group relative h-[32rem] min-w-0 cursor-pointer overflow-hidden rounded-2xl border bg-gradient-to-br from-[#151B23] via-[#13181F] to-[#0F141A] focus:outline-none focus-visible:border-[#F4D64E]/50 ${
        isActive
          ? "border-[#F4D64E]/30 shadow-[0_28px_72px_-24px_rgba(244,214,78,0.22)]"
          : "border-white/[0.08]"
      }`}
      style={{ transition: "border-color 400ms ease-in-out, box-shadow 400ms ease-in-out" }}
    >
      {/* Huge translucent icon — opacity + color crossfade, one transition */}
      <Icon
        aria-hidden
        strokeWidth={1}
        className="absolute -right-10 -bottom-14 w-[20rem] h-[20rem] pointer-events-none select-none"
        style={{
          color: isActive ? "rgba(244, 214, 78, 0.13)" : "rgba(255, 255, 255, 0.05)",
          transform: isActive ? "scale(1.1) rotate(-3deg)" : "scale(1) rotate(0deg)",
          transition: "color 400ms ease-in-out, transform 400ms ease-in-out",
        }}
      />

      {/* Warm corner wash — single opacity transition */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F4D64E]/[0.06] blur-3xl"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 400ms ease-in-out",
        }}
      />

      {/* Bottom sweep */}
      <span
        aria-hidden
        className="absolute bottom-0 left-0 h-[2px] bg-[#F4D64E]"
        style={{
          width: isActive ? "100%" : "0%",
          transition: "width 400ms ease-in-out",
        }}
      />

      {/* Number — absolute to the panel, always top-left, fades on active */}
      <span
        className="absolute top-6 left-6 tabular-nums text-[0.74rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] z-10"
        style={{
          opacity: isActive ? 0 : 1,
          transition: "opacity 400ms ease-in-out",
        }}
      >
        {num}
      </span>

      {/* COLLAPSED state — vertical title. Opacity only, single transition. */}
      <div
        className="absolute left-0 right-0 bottom-6 flex justify-center pointer-events-none"
        style={{
          opacity: isActive ? 0 : 1,
          transition: "opacity 400ms ease-in-out",
        }}
      >
        <h3
          className="font-display font-black tracking-[-0.015em] text-white text-[0.98rem] md:text-[1.05rem] whitespace-nowrap"
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
          }}
        >
          {s.title}
        </h3>
      </div>

      {/* EXPANDED state — full content. Opacity only, single transition. */}
      <div
        className="absolute inset-0 p-8 md:p-10 flex flex-col"
        style={{
          opacity: isActive ? 1 : 0,
          pointerEvents: isActive ? "auto" : "none",
          transition: "opacity 400ms ease-in-out",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-[0.74rem] font-bold tracking-[0.22em] uppercase">
            <span className="tabular-nums text-[#F4D64E]">{num}</span>
            <span className="h-px w-6 bg-[#F4D64E]/50" />
            <span className="text-neutral-400 whitespace-nowrap">{label}</span>
          </div>
          <ArrowUpRight
            aria-hidden
            className="w-4 h-4 text-[#F4D64E]"
          />
        </div>

        <h3 className="mt-10 font-display text-[1.75rem] md:text-[2rem] lg:text-[2.25rem] font-black tracking-[-0.025em] leading-[1.05] text-white max-w-[16ch]">
          {s.title}
        </h3>

        <p className="mt-5 text-[0.96rem] md:text-[1rem] text-neutral-400 leading-[1.6] max-w-[44ch]">
          {s.body}
        </p>

        <ul className="mt-5 flex flex-col gap-2">
          {highlights.map((h) => (
            <li
              key={h}
              className="flex items-center gap-3 text-[0.88rem] text-neutral-300"
            >
              <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
              {h}
            </li>
          ))}
        </ul>

        <div className="flex-1" />

        <div className="pt-5 border-t border-white/[0.07] flex items-center justify-between">
          <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
            Learn more
          </span>
          <span className="text-[0.7rem] tracking-[0.22em] uppercase text-neutral-500 tabular-nums">
            0{index + 1} / 06
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Mobile-only stacked card — shown instead of the accordion on small screens
const ServiceMobileCardV2 = ({ s, index }) => {
  const Icon = SERVICE_ICON_MAP[s.icon] || serviceIconsV2[index] || Layers;
  const num = s.tag_number != null ? s.tag_number : (s.tag ? s.tag.split(" / ")[0] : String(index + 1).padStart(2, "0"));
  const label = s.tag_label != null ? s.tag_label : (s.tag ? s.tag.split(" / ")[1] || "" : "");
  const highlights = Array.isArray(s.highlights) ? s.highlights : (serviceHighlightsV2[index] || []);
  return (
    <div
      data-testid={`service-mobile-v2-${index + 1}`}
      className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-br from-[#151B23] via-[#13181F] to-[#0F141A] p-6"
    >
      <Icon
        aria-hidden
        strokeWidth={1}
        className="absolute -right-6 -bottom-8 w-56 h-56 text-white/[0.06] pointer-events-none select-none"
      />
      <div className="relative">
        <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase">
          <span className="tabular-nums text-[#F4D64E]">{num}</span>
          <span className="h-px w-6 bg-[#F4D64E]/50" />
          <span className="text-neutral-400">{label}</span>
        </div>
        <h3 className="mt-5 font-display text-[1.4rem] font-black tracking-[-0.02em] leading-[1.05] text-white">
          {s.title}
        </h3>
        <p className="mt-3 text-[0.94rem] text-neutral-400 leading-[1.6]">
          {s.body}
        </p>
        <ul className="mt-4 flex flex-col gap-2">
          {highlights.map((h) => (
            <li
              key={h}
              className="flex items-center gap-3 text-[0.85rem] text-neutral-300"
            >
              <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ServicesGridV2 = () => {
  // Start with NO active panel — all 6 show only their vertical title.
  // Hovering any panel reveals the full content in place.
  const [active, setActive] = useState(null);
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative overflow-hidden bg-[#11161D]"
    >
      <SectionAmbient variant="a" />
      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-14 md:mb-20">
          <PillarHeader
            eyebrow="Our Shopify Services"
            description="Comprehensive Shopify services tailored to your business needs — from the storefront to the checkout to the back office."
          >
            <h2 className={pillarHeadingClass}>
              Development expertise — across every surface of{" "}
              <PillarAccent>your store</PillarAccent>.
            </h2>
          </PillarHeader>
          <a
            href="#contact"
            data-testid="services-all-link"
            className="hidden md:inline-flex items-center gap-2 text-sm font-bold tracking-wide text-[#F4D64E] link-underline whitespace-nowrap"
          >
            See all services <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Desktop — hover accordion. Last hovered panel stays open
            (no onMouseLeave reset) so the user can read the content
            without it snapping shut. */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="hidden md:flex gap-2 lg:gap-3"
        >
          {services.map((s, i) => (
            <ServicePanelV2
              key={s.title}
              s={s}
              index={i}
              active={active}
              onActivate={setActive}
            />
          ))}
        </motion.div>

        {/* Hint row under the accordion */}
        <div className="hidden md:flex items-center gap-3 mt-6 text-[0.68rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
          <span className="h-px w-8 bg-neutral-700" />
          Hover a service to expand
        </div>

        {/* Mobile — stacked list */}
        <div className="md:hidden flex flex-col gap-4">
          {services.map((s, i) => (
            <ServiceMobileCardV2 key={s.title} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   5. MARKETING 360° & CREATIVE
============================================================ */

const marketing = [
  { icon: Megaphone, title: "Paid Marketing", body: "Targeted campaigns across channels — optimized for ROAS and scaled revenue." },
  { icon: Mail, title: "Email Marketing", body: "Automated, personalized flows and campaigns that convert and retain." },
  { icon: Instagram, title: "Social Management", body: "Presence as an asset — content that engages, builds loyalty, and sells." },
  { icon: Search, title: "SEO", body: "Audits and competitive strategy that put your brand in front of the right audience." },
  { icon: Target, title: "Influencer Marketing", body: "Personalized influencer campaigns that raise awareness and lift sales." },
  { icon: Sparkles, title: "Content Creation", body: "Strong content makes your store more appealing, easier to navigate, and higher-converting." },
];

const creative = [
  { icon: Palette, title: "Advanced UI/UX", body: "Beyond aesthetics — precision-built eCommerce design that drives results." },
  { icon: Code2, title: "Shopify Web Design", body: "Sharp design that performs — where creativity meets conversion at scale." },
  { icon: Video, title: "Video Ads Creative", body: "From storyboard to screen — videos designed to convert and scale performance." },
  { icon: MousePointerClick, title: "Social Ads Creative", body: "Concept to conversion: audience × message × platform × timing, optimized." },
  { icon: PenTool, title: "Brand Identity", body: "Logos, visual systems, and guidelines that make your brand instantly recognizable." },
  { icon: Film, title: "Motion Design", body: "Animated product reveals, micro-interactions, and campaign motion that make stores move." },
];

/* ----- PillarCard — unified hover system, identical to Services cards ----- */
const PillarCard = ({ icon: Icon, title, body }) => (
  <div className="group relative bg-[#151B23] border border-white/[0.06] rounded-lg p-6 flex flex-col gap-3 hover:bg-[#1B2330] hover:border-[#F4D64E]/30 hover:-translate-y-0.5 transition-all duration-400 overflow-hidden">
    <div className="w-9 h-9 rounded-md bg-[#F4D64E]/10 border border-[#F4D64E]/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#F4D64E]" strokeWidth={1.75} />
    </div>
    <h4 className="font-display text-2xl font-bold text-white">{title}</h4>
    <p className="text-base text-neutral-400 leading-relaxed">{body}</p>
    <span
      aria-hidden
      className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#F4D64E] group-hover:w-full transition-all duration-500"
    />
  </div>
);

const MarketingCreative = () => (
  <section
    id="marketing"
    data-testid="marketing-section"
    className="relative overflow-hidden bg-[#11161D]"
  >
    {/* Marketing 360° ambient — full-width, anchored LEFT-CENTER, covers the Marketing pillar area */}
    <SectionAmbient
      variant="b"
      grid={false}
      parallax={160}
      className="absolute inset-x-0 top-0 h-[60%] md:h-[55%]"
    />

    {/* Creative Capabilities ambient — full-width, anchored RIGHT-CENTER, covers the Creative pillar area */}
    <SectionAmbient
      variant="c"
      grid={false}
      parallax={160}
      className="absolute inset-x-0 bottom-0 h-[60%] md:h-[55%]"
    />

    {/* Leading pillar-divider — visually joins Services → Marketing into one system */}
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <div className="h-px bg-white/[0.06]" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-32">
      {/* Marketing 360° */}
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <Eyebrow>Marketing 360°</Eyebrow>
          <h2 className={`mt-7 ${pillarHeadingClass}`}>
            A single partner for every{" "}
            <PillarAccent>digital task</PillarAccent>.
          </h2>
          <p className="mt-7 text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[48ch]">
            Skip the hassle of juggling agencies. From campaign setup to
            full-scale strategy — we turn ideas into results you can count on.
          </p>
          <div className="mt-9">
            <SecondaryButton testId="marketing-cta" href="#contact">
              Talk to us
            </SecondaryButton>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {marketing.map((m) => (
            <PillarCard key={m.title} {...m} />
          ))}
        </div>
      </div>

      <div className="my-20 md:my-28 h-px bg-white/[0.06]" />

      {/* Creative */}
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 order-2 lg:order-1 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {creative.map((m) => (
            <PillarCard key={m.title} {...m} />
          ))}
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-24 self-start">
          <Eyebrow>Creative Capabilities</Eyebrow>
          <h2 className={`mt-7 ${pillarHeadingClass}`}>
            Creative thinking. <br />
            <span className="italic font-normal text-neutral-400">Proven</span>{" "}
            <PillarAccent>strategies</PillarAccent>.
          </h2>
          <p className="mt-7 text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[48ch]">
            From Shopify design and advanced UX/UI to video production and social
            ad creatives — we craft digital experiences that are clean, cohesive,
            and impactful.
          </p>
          <div className="mt-9">
            <SecondaryButton testId="creative-cta" href="#contact">
              Work with us
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   5-V2. CREATIVE CAPABILITIES — V2 only (vertical accordion)
   ------------------------------------------------------------
   Mirrors the horizontal Services accordion but rotated 90°.
   Collapsed strips show number + title + icon (fixed 64px row
   height — exactly the title-row). Hover a strip → it grows
   to take the remaining vertical space, siblings stay at 64px
   (never "crushed" thinner than the title row).

   When expanded, each row reveals a custom looping micro-visual
   on the right side, themed to that capability (à la Tiller).
============================================================ */

/* --- Creative Capability micro-visuals (V2 only) ------------
   Each returns an SVG that only animates while its row is open.
   Minimal, monochrome + #F4D64E accents, 2–3s looping cycles.
------------------------------------------------------------ */
const YEL = "#F4D64E";

/* 01 — Advanced UI/UX: dashboard wireframe coming to life */
const VisualUIUX = () => (
  <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
    <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
    {/* Sidebar */}
    <rect x="10" y="10" width="50" height="140" rx="4" fill="rgba(244,214,78,0.06)" stroke="rgba(244,214,78,0.22)" />
    {[22, 36, 50, 64, 78].map((y, i) => (
      <motion.rect key={y} x="18" y={y} width="34" height="3" rx="1.5" fill={YEL}
        initial={{ opacity: 0.25 }}
        animate={{ opacity: [0.25, 1, 0.25] }}
        transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.25, ease: "easeInOut" }} />
    ))}
    {/* Header */}
    <rect x="70" y="10" width="180" height="20" rx="3" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" />
    <circle cx="80" cy="20" r="3" fill={YEL} opacity="0.6" />
    <rect x="90" y="18" width="70" height="4" rx="2" fill="rgba(255,255,255,0.25)" />
    {/* KPI tiles */}
    {[{ x: 70, d: 0, hi: true }, { x: 132, d: 0.3 }, { x: 194, d: 0.6 }].map((b, i) => (
      <motion.g key={i} animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, delay: b.d, ease: "easeInOut" }}>
        <rect x={b.x} y="40" width="56" height="38" rx="3"
          fill={b.hi ? "rgba(244,214,78,0.14)" : "rgba(255,255,255,0.04)"}
          stroke={b.hi ? "rgba(244,214,78,0.4)" : "rgba(255,255,255,0.1)"} />
        <rect x={b.x + 6} y="48" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.4)" />
        <rect x={b.x + 6} y="56" width="30" height="6" rx="2" fill={b.hi ? YEL : "rgba(255,255,255,0.6)"} />
      </motion.g>
    ))}
    {/* Chart bars (bottom row) */}
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <motion.rect key={i} x={74 + i * 22} width="14" rx="1.5"
        fill={i === 4 ? YEL : "rgba(255,255,255,0.22)"}
        style={{ transformBox: "fill-box", transformOrigin: "50% 100%" }}
        y="94" height="52"
        animate={{ scaleY: [0.25, 0.9, 0.5, 1, 0.3] }}
        transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.12, ease: "easeInOut" }} />
    ))}
  </svg>
);

/* 02 — Shopify Web Design: browser with code lines + product card */
const VisualWebDesign = () => (
  <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
    <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
    {/* Browser chrome */}
    <rect x="10" y="10" width="240" height="140" rx="5" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" />
    <circle cx="22" cy="22" r="2.5" fill="rgba(255,99,99,0.6)" />
    <circle cx="32" cy="22" r="2.5" fill="rgba(244,214,78,0.7)" />
    <circle cx="42" cy="22" r="2.5" fill="rgba(99,220,150,0.6)" />
    <rect x="55" y="18" width="120" height="8" rx="2" fill="rgba(255,255,255,0.06)" />
    <line x1="10" y1="34" x2="250" y2="34" stroke="rgba(255,255,255,0.08)" />
    {/* Left — "code" lines typing */}
    {[{ w: 50, y: 46, c: YEL }, { w: 80, y: 58, c: "rgba(255,255,255,0.4)" },
      { w: 40, y: 70, c: "rgba(255,255,255,0.4)" }, { w: 70, y: 82, c: YEL },
      { w: 60, y: 94, c: "rgba(255,255,255,0.4)" }, { w: 90, y: 106, c: "rgba(255,255,255,0.4)" },
      { w: 45, y: 118, c: YEL }, { w: 70, y: 130, c: "rgba(255,255,255,0.4)" }].map((l, i) => (
      <motion.rect key={i} x="20" y={l.y} height="3" rx="1.5" fill={l.c}
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: [0, l.w, l.w, 0], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.15, ease: "easeOut", times: [0, 0.3, 0.85, 1] }} />
    ))}
    {/* Right — product card sliding in */}
    <motion.g
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: [40, 0, 0, 40], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut", times: [0, 0.35, 0.9, 1] }}>
      <rect x="150" y="48" width="90" height="94" rx="4" fill="rgba(255,255,255,0.04)" stroke="rgba(244,214,78,0.35)" />
      <rect x="156" y="54" width="78" height="50" rx="3" fill="rgba(244,214,78,0.1)" />
      <circle cx="195" cy="79" r="10" fill="none" stroke={YEL} strokeWidth="1.2" />
      <path d="M190 79 L193 82 L200 75" stroke={YEL} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <rect x="156" y="110" width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.5)" />
      <rect x="156" y="118" width="60" height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />
      <rect x="156" y="128" width="30" height="8" rx="4" fill={YEL} />
    </motion.g>
  </svg>
);

/* 03 — Video Ads Creative: film strip + progress scrub */
const VisualVideo = () => {
  const frames = [0, 1, 2, 3, 4];
  return (
    <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
      <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
      {/* Sprocket holes (top & bottom film-strip feel) */}
      {[20, 50, 80, 110, 140, 170, 200, 230].map((x) => (
        <rect key={`top-${x}`} x={x} y="20" width="12" height="6" rx="1" fill="rgba(255,255,255,0.08)" />
      ))}
      {[20, 50, 80, 110, 140, 170, 200, 230].map((x) => (
        <rect key={`bot-${x}`} x={x} y="102" width="12" height="6" rx="1" fill="rgba(255,255,255,0.08)" />
      ))}
      {/* Scrolling frames */}
      <g>
        {frames.map((i) => (
          <motion.g key={i}
            animate={{ x: [0, -260] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear", delay: i * -1.2 }}>
            <rect x={i * 60 + 20} y="32" width="54" height="68" rx="3"
              fill={i === 2 ? "rgba(244,214,78,0.14)" : "rgba(255,255,255,0.04)"}
              stroke={i === 2 ? "rgba(244,214,78,0.4)" : "rgba(255,255,255,0.1)"} />
            <polygon points={`${i * 60 + 40},58 ${i * 60 + 40},82 ${i * 60 + 58},70`}
              fill={i === 2 ? YEL : "rgba(255,255,255,0.45)"} />
          </motion.g>
        ))}
      </g>
      {/* Progress scrubber */}
      <rect x="20" y="128" width="220" height="3" rx="1.5" fill="rgba(255,255,255,0.1)" />
      <motion.rect x="20" y="128" width="0" height="3" rx="1.5" fill={YEL}
        initial={{ width: 0 }}
        animate={{ width: [0, 220, 0] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }} />
      <motion.circle cx="20" cy="129.5" r="4" fill={YEL}
        initial={{ cx: 20 }}
        animate={{ cx: [20, 240, 20] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }} />
      {/* Timecode */}
      <rect x="20" y="138" width="28" height="10" rx="2" fill="rgba(255,255,255,0.05)" />
      <rect x="24" y="142" width="20" height="2" rx="1" fill={YEL} />
    </svg>
  );
};

/* 04 — Social Ads Creative: stacked feed cards + pulsing engagement */
const VisualSocial = () => (
  <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
    <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
    {/* Back card */}
    <motion.g
      initial={{ x: 24, y: 24, opacity: 0.5 }}
      animate={{ x: [24, 12, 12], y: [24, 16, 16], opacity: [0.4, 0.7, 0.7] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}>
      <rect x="80" y="14" width="120" height="120" rx="6" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
    </motion.g>
    {/* Mid card */}
    <motion.g
      initial={{ x: 12, y: 12 }}
      animate={{ x: [12, 0, 0], y: [12, 0, 0] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}>
      <rect x="80" y="14" width="120" height="120" rx="6" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.14)" />
    </motion.g>
    {/* Front card (main) */}
    <motion.g
      animate={{ rotate: [-1, 1, -1] }}
      style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
      <rect x="80" y="14" width="120" height="120" rx="6"
        fill="rgba(244,214,78,0.06)" stroke="rgba(244,214,78,0.4)" />
      {/* Avatar */}
      <circle cx="94" cy="28" r="6" fill={YEL} opacity="0.5" />
      <rect x="104" y="24" width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.6)" />
      <rect x="104" y="30" width="26" height="2" rx="1" fill="rgba(255,255,255,0.3)" />
      {/* Image block */}
      <rect x="88" y="42" width="104" height="56" rx="3" fill="rgba(244,214,78,0.1)" />
      <path d="M105 82 L120 66 L140 86 L160 70 L175 82" stroke={YEL} strokeWidth="1.2" fill="none" strokeLinecap="round" />
      {/* Engagement */}
      <motion.g
        style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
        <path d="M94 112 c -4 -4 -4 -10 0 -12 c 2 -1 4 0 5 2 c 1 -2 3 -3 5 -2 c 4 2 4 8 0 12 l -5 4 z"
          fill={YEL} stroke={YEL} strokeWidth="0.5" />
      </motion.g>
      <rect x="112" y="114" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.7)" />
      <circle cx="140" cy="116" r="3" fill="none" stroke="rgba(255,255,255,0.5)" />
      <rect x="148" y="114" width="16" height="3" rx="1.5" fill="rgba(255,255,255,0.4)" />
    </motion.g>
    {/* Small side badge ticker */}
    <motion.g
      animate={{ y: [0, -4, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
      <rect x="210" y="64" width="38" height="18" rx="9" fill="rgba(244,214,78,0.14)" stroke={YEL} />
      <path d="M217 73 l 3 3 l 6 -7" stroke={YEL} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <rect x="228" y="71" width="16" height="3" rx="1" fill={YEL} />
    </motion.g>
  </svg>
);

/* 05 — Brand Identity: S monogram stroke-draw + color swatches */
const VisualBrand = () => (
  <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
    <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
    {/* Big S monogram drawing itself */}
    <motion.path
      d="M 150 30 C 110 30, 90 50, 90 68 C 90 86, 110 92, 130 92 C 150 92, 170 98, 170 116 C 170 134, 150 150, 110 150"
      stroke={YEL} strokeWidth="6" fill="none" strokeLinecap="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", times: [0, 0.5, 0.85, 1] }} />
    {/* Grid dots backdrop */}
    {Array.from({ length: 20 }).map((_, i) => {
      const gx = 10 + (i % 10) * 10;
      const gy = 10 + Math.floor(i / 10) * 140;
      return <circle key={i} cx={gx} cy={gy} r="0.8" fill="rgba(255,255,255,0.15)" />;
    })}
    {/* Color swatches cycling */}
    {[{ x: 16, c: "#F4D64E" }, { x: 16, c: "#0F141A" }, { x: 16, c: "#E8E4D8" }].map((s, i) => (
      <motion.g key={i}
        animate={{ x: [16, 16], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.4, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}>
        <rect x={16 + i * 18} y="118" width="14" height="24" rx="2" fill={s.c}
          stroke={s.c === "#0F141A" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"} strokeWidth="0.6" />
      </motion.g>
    ))}
    {/* Caption chip */}
    <rect x="72" y="126" width="60" height="12" rx="6" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
    <rect x="80" y="131" width="44" height="2" rx="1" fill="rgba(255,255,255,0.4)" />
    {/* Corner tick */}
    <circle cx="234" cy="26" r="8" fill="rgba(244,214,78,0.14)" stroke={YEL} />
    <path d="M230 26 l 3 3 l 5 -6" stroke={YEL} strokeWidth="1.4" fill="none" strokeLinecap="round" />
  </svg>
);

/* 06 — Motion Design: shapes cross-fading with rotation + orbiting dots */
const VisualMotion = () => {
  const shapes = [
    // Circle
    <circle key="c" cx="130" cy="90" r="48" />,
    // Rounded square
    <rect key="s" x="82" y="42" width="96" height="96" rx="14" />,
    // Triangle
    <polygon key="t" points="130,40 180,134 80,134" />,
    // Hexagon
    <polygon key="h" points="130,40 175,65 175,115 130,140 85,115 85,65" />,
  ];
  return (
    <svg viewBox="0 0 260 160" className="w-full h-full" fill="none">
      <rect x="1" y="1" width="258" height="158" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
      {/* Orbit dots */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const baseAngle = (i / 6) * Math.PI * 2;
        const startCx = 130 + 70 * Math.cos(baseAngle);
        const startCy = 90 + 54 * Math.sin(baseAngle);
        return (
          <motion.circle key={i} cx={startCx} cy={startCy} r="2" fill={YEL}
            initial={{ cx: startCx, cy: startCy }}
            animate={{
              cx: Array.from({ length: 13 }).map((_, k) =>
                130 + 70 * Math.cos(baseAngle + (k / 12) * Math.PI * 2)
              ),
              cy: Array.from({ length: 13 }).map((_, k) =>
                90 + 54 * Math.sin(baseAngle + (k / 12) * Math.PI * 2)
              ),
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            opacity="0.5"
          />
        );
      })}
      {/* Cross-fading shapes, each rotating gently */}
      {shapes.map((shape, i) => (
        <motion.g key={i}
          style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
          animate={{
            opacity: [0, 1, 1, 0, 0],
            scale: [0.85, 1, 1, 0.92, 0.85],
            rotate: [0, 8, -4, 0, 0],
          }}
          transition={{
            duration: 5.6,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.15, 0.22, 0.32, 1],
            delay: i * 1.4,
          }}
          stroke={YEL} strokeWidth="2" fill="rgba(244,214,78,0.08)" strokeLinejoin="round">
          {shape}
        </motion.g>
      ))}
      {/* Center pulse */}
      <motion.circle cx="130" cy="90" r="4" fill={YEL}
        animate={{ scale: [1, 1.8, 1], opacity: [1, 0.4, 1] }}
        style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
    </svg>
  );
};

const CREATIVE_VISUALS = [
  VisualUIUX,
  VisualWebDesign,
  VisualVideo,
  VisualSocial,
  VisualBrand,
  VisualMotion,
];

const CreativeRowV2 = ({ c, index, active, onActivate }) => {
  const Icon = c.icon;
  const Visual = CREATIVE_VISUALS[index];
  const isActive = active === index;
  const anyActive = active !== null && active !== undefined;
  const T = { duration: 0.4, ease: [0.4, 0, 0.2, 1] };

  // Collapsed rows pin to exactly 5rem (80px) — the title row height.
  // Active row grows to fill remaining space.
  // No-active state → all rows share equally (flex-grow: 1).
  const rowAnimate = isActive
    ? { flexGrow: 1, flexShrink: 1, flexBasis: 0 }
    : anyActive
    ? { flexGrow: 0, flexShrink: 0, flexBasis: "5rem" }
    : { flexGrow: 1, flexShrink: 1, flexBasis: 0 };

  return (
    <motion.div
      data-testid={`creative-row-v2-${index + 1}`}
      onMouseEnter={() => onActivate(index)}
      onFocus={() => onActivate(index)}
      onClick={() => onActivate(index)}
      tabIndex={0}
      role="button"
      aria-expanded={isActive}
      aria-label={c.title}
      data-active={isActive}
      animate={rowAnimate}
      transition={T}
      className={`group relative w-full min-h-[5rem] cursor-pointer overflow-hidden rounded-2xl border bg-gradient-to-br from-[#151B23] via-[#13181F] to-[#0F141A] focus:outline-none focus-visible:border-[#F4D64E]/50 ${
        isActive
          ? "border-[#F4D64E]/30 shadow-[0_24px_64px_-22px_rgba(244,214,78,0.22)]"
          : "border-white/[0.08]"
      }`}
      style={{
        transition:
          "border-color 400ms ease-in-out, box-shadow 400ms ease-in-out",
      }}
    >
      {/* Left-side vertical accent bar — draws itself when active */}
      <span
        aria-hidden
        className="absolute top-0 left-0 w-[2px] bg-[#F4D64E]"
        style={{
          height: isActive ? "100%" : "0%",
          transition: "height 400ms ease-in-out",
        }}
      />

      {/* Subtle corner glow — very light, only when active */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-20 w-72 h-72 rounded-full bg-[#F4D64E]/[0.04] blur-3xl"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 400ms ease-in-out",
        }}
      />

      {/* COLLAPSED state — title row always visible, anchored to the TOP
          so it doesn't drift to the vertical center when the panel is tall */}
      <div className="absolute top-0 left-0 right-0 h-20 flex items-center px-6 md:px-8 gap-6 pointer-events-none">
        {/* Number — always anchored top/left area */}
        <span className="tabular-nums text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] w-10 shrink-0">
          0{index + 1}
        </span>
        {/* Icon chip — always visible */}
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F4D64E]/10 border border-[#F4D64E]/25 shrink-0">
          <Icon
            className="w-5 h-5 text-[#F4D64E]"
            strokeWidth={1.75}
          />
        </div>
        {/* Title — slightly larger when active */}
        <h4
          className="font-display font-black tracking-[-0.015em] text-white"
          style={{
            fontSize: isActive ? "1.75rem" : "1.3rem",
            transition: "font-size 400ms ease-in-out",
          }}
        >
          {c.title}
        </h4>
        {/* Right arrow indicator */}
        <div className="flex-1" />
        <ArrowUpRight
          aria-hidden
          className="w-5 h-5 shrink-0"
          style={{
            color: isActive ? "#F4D64E" : "rgba(115, 115, 115, 1)",
            transition: "color 400ms ease-in-out",
          }}
        />
      </div>

      {/* EXPANDED state — two-column: body text (left) + micro-visual (right) */}
      <div
        className="absolute left-0 right-0 top-20 bottom-0 px-6 md:px-8 pb-6 pt-5 pointer-events-none overflow-hidden"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 400ms ease-in-out",
          pointerEvents: isActive ? "auto" : "none",
        }}
      >
        <div className="h-full flex items-center gap-8 md:gap-12">
          {/* Left — body copy only (original, unchanged) */}
          <div className="flex-1 min-w-0">
            <p className="text-[1.02rem] md:text-[1.1rem] text-neutral-300 leading-[1.6] max-w-[46ch]">
              {c.body}
            </p>
          </div>
          {/* Right — animated micro-visual, rendered only while active */}
          <div
            aria-hidden
            className="hidden md:block shrink-0 w-[300px] lg:w-[360px] h-full max-h-[240px]"
            data-testid={`creative-visual-v2-${index + 1}`}
          >
            {isActive && Visual ? (
              <motion.div
                key={`visual-${index}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
                className="w-full h-full"
              >
                <Visual />
              </motion.div>
            ) : null}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   5-V2b. MARKETING 360° — V2 only (pinned horizontal scroll)
   ------------------------------------------------------------
   Mirrors the HorizontalFeatures mechanic from the "250+ Custom
   Features" section, but with oversized 45vw × 30vw cards —
   2 fully visible + 3rd peeking in. Original copy preserved.
============================================================ */
const MarketingPinnedV2 = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);

  // Measure track width and compute the exact horizontal distance
  // so the last card lands flush-right with a small breathing pad.
  useEffect(() => {
    const calc = () => {
      if (!trackRef.current) return;
      const trackWidth = trackRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;
      const END_PAD = 48;
      const distance = Math.max(0, trackWidth - viewportWidth + END_PAD);
      setMaxX(-distance);
    };
    calc();
    window.addEventListener("resize", calc);
    const raf = requestAnimationFrame(calc);
    const t = setTimeout(calc, 300);
    return () => {
      window.removeEventListener("resize", calc);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, maxX]);
  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="marketing-pinned"
      data-testid="marketing-pinned-v2"
      className="relative bg-[#11161D]"
    >
      {/* ============ DESKTOP (md+): pinned horizontal scroll ============ */}
      <div
        ref={ref}
        className="hidden md:block relative"
        style={{ height: "280vh" }}
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          {/* Section header — matches 250+ Features layout */}
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10">
            <FadeUp>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
                <div className="flex flex-col gap-5 max-w-3xl">
                  <Eyebrow>Marketing 360°</Eyebrow>
                  <h2 className="font-display text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                    A single partner for every{" "}
                    <PillarAccent>digital task</PillarAccent>.
                  </h2>
                  <p className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[58ch]">
                    Skip the hassle of juggling agencies. From campaign setup
                    to full-scale strategy — we turn ideas into results you
                    can count on.
                  </p>
                </div>

                {/* Progress indicator — mirrors 250+ Features */}
                <div className="hidden md:flex items-center gap-4 min-w-[220px]">
                  <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                    Scroll
                  </span>
                  <div className="relative flex-1 h-[2px] bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#F4D64E] rounded-full"
                      style={{ width: barWidth }}
                    />
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          {/* Horizontal track — oversized cards 45vw × 30vw */}
          <motion.div
            ref={trackRef}
            style={{ x }}
            className="flex items-stretch gap-5 md:gap-6 px-6 md:px-10 will-change-transform"
          >
            {marketing.map((m, i) => {
              const Icon = m.icon;
              return (
                <article
                  key={m.title}
                  data-testid={`marketing-pinned-card-${i + 1}`}
                  className="group relative shrink-0 w-[45vw] h-[30vw] max-h-[560px] min-h-[440px] bg-[#151B23] border border-white/[0.06] rounded-2xl p-9 md:p-12 flex flex-col gap-6 hover:bg-[#1B2330] hover:border-[#F4D64E]/30 transition-all duration-400 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.95)] overflow-hidden"
                >
                  {/* Subtle top-right glow */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#F4D64E]/[0.05] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  />
                  {/* Translucent oversized icon — bottom-right, decorative */}
                  <Icon
                    aria-hidden
                    strokeWidth={1}
                    className="pointer-events-none absolute -right-10 -bottom-14 w-[22rem] h-[22rem] text-white/[0.03] group-hover:text-[#F4D64E]/[0.08] transition-colors duration-500"
                  />

                  {/* Top row — number + arrow */}
                  <div className="relative flex items-center justify-between">
                    <span className="text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/80 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-neutral-500 group-hover:text-[#F4D64E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
                  </div>

                  {/* Icon chip */}
                  <div className="relative w-14 h-14 rounded-lg bg-[#F4D64E]/10 border border-[#F4D64E]/25 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#F4D64E]" strokeWidth={1.6} />
                  </div>

                  {/* Title */}
                  <h3 className="relative font-display text-[2rem] md:text-[2.5rem] font-black tracking-[-0.02em] text-white leading-[1.05]">
                    {m.title}
                  </h3>

                  {/* Body */}
                  <p className="relative text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.6] max-w-[48ch]">
                    {m.body}
                  </p>

                  {/* Footer tag — matches 250+ cards */}
                  <div className="relative mt-auto pt-5 flex items-center gap-3 text-[0.72rem] tracking-[0.2em] uppercase text-neutral-500 font-bold">
                    <span className="h-px w-8 bg-[#F4D64E]/60" />
                    Marketing 360°
                  </div>
                </article>
              );
            })}

            {/* Trailing CTA card — absorbs the "Talk to us" intent */}
            <article className="shrink-0 w-[45vw] h-[30vw] max-h-[560px] min-h-[440px] bg-transparent border border-dashed border-white/20 rounded-2xl p-9 md:p-12 flex flex-col justify-between gap-8">
              <div>
                <span className="text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/80">
                  One partner · Zero juggling
                </span>
                <h3 className="font-display text-[2rem] md:text-[2.75rem] font-black tracking-[-0.02em] text-white leading-[1.05] mt-6">
                  Ready to consolidate
                  <br />
                  <span className="italic font-normal text-neutral-400">your</span>{" "}
                  marketing stack?
                </h3>
                <p className="mt-5 text-[1rem] md:text-[1.05rem] text-neutral-400 leading-[1.6] max-w-[44ch]">
                  One team. One strategy. Every channel covered — from campaign
                  setup to full-scale growth.
                </p>
              </div>
              <a
                href="#contact"
                data-testid="marketing-pinned-cta"
                className="group inline-flex items-center gap-2 text-[0.82rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] rounded-md border border-[#F4D64E]/40 hover:bg-[#F4D64E] hover:text-neutral-950 px-6 py-4 w-fit transition-colors"
              >
                Talk to us
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </article>
          </motion.div>
        </div>
      </div>

      {/* ============ MOBILE (<md): swipeable snap carousel ============ */}
      <div className="md:hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-4">
          <FadeUp>
            <div className="flex flex-col gap-5 mb-10">
              <Eyebrow>Marketing 360°</Eyebrow>
              <h2 className="font-display text-[2.25rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                A single partner for every{" "}
                <PillarAccent>digital task</PillarAccent>.
              </h2>
              <p className="text-[1.02rem] text-neutral-400 leading-[1.65]">
                Skip the hassle of juggling agencies. From campaign setup to
                full-scale strategy — we turn ideas into results you can count
                on.
              </p>
            </div>
          </FadeUp>
        </div>
        {/* Horizontal swipe carousel with snap */}
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-6 pl-6 pr-6 scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {marketing.map((m, i) => {
            const Icon = m.icon;
            return (
              <article
                key={m.title}
                className="snap-start shrink-0 w-[82vw] bg-[#151B23] border border-white/[0.06] rounded-xl p-7 flex flex-col gap-4"
              >
                <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="w-11 h-11 rounded-md bg-[#F4D64E]/10 border border-[#F4D64E]/20 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-[1.65rem] font-black tracking-[-0.015em] text-white leading-[1.08]">
                  {m.title}
                </h3>
                <p className="text-[0.95rem] text-neutral-400 leading-relaxed">
                  {m.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const MarketingCreativeV2 = () => {
  // Start with no active row — all 6 show in their compact height.
  // Last hovered row stays open.
  const [activeCreative, setActiveCreative] = useState(null);
  return (
    <section
      id="marketing"
      data-testid="marketing-section"
      className="relative bg-[#11161D]"
    >
      {/* Single centered full-width ambient glow — moves down on scroll-down,
          up on scroll-up via the built-in parallax transform. */}
      <SectionAmbient
        variant="e"
        grid={false}
        parallax={90}
        className="absolute inset-0"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-32">
        {/* Creative — vertical accordion replaces the 6 boxes */}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Vertical accordion — desktop */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              data-testid="creative-accordion-v2"
              className="hidden md:flex md:flex-col h-[52rem] gap-2 lg:gap-3"
            >
              {creative.map((c, i) => (
                <CreativeRowV2
                  key={c.title}
                  c={c}
                  index={i}
                  active={activeCreative}
                  onActivate={setActiveCreative}
                />
              ))}
            </div>
            {/* Mobile — stacked cards (existing PillarCard look) */}
            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
              {creative.map((m) => (
                <PillarCard key={m.title} {...m} />
              ))}
            </div>
          </div>

          {/* Copy column — unchanged */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-24 self-start">
            <Eyebrow>Creative Capabilities</Eyebrow>
            <h2 className={`mt-7 ${pillarHeadingClass}`}>
              Creative thinking. <br />
              <span className="italic font-normal text-neutral-400">
                Proven
              </span>{" "}
              <PillarAccent>strategies</PillarAccent>.
            </h2>
            <p className="mt-7 text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[48ch]">
              From Shopify design and advanced UX/UI to video production and
              social ad creatives — we craft digital experiences that are
              clean, cohesive, and impactful.
            </p>
            <div className="mt-9">
              <SecondaryButton testId="creative-cta" href="#contact">
                Work with us
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   6. CASE STUDIES
============================================================ */

const caseStudies = [
  {
    name: "Veronique Gabai",
    tagline:
      "Sunshine for the Senses and for the Soul — luxury perfumes, fragrance jewelry & beauty.",
    tasks: "320+",
    since: "2020",
    img: "https://images.unsplash.com/photo-1773527142304-58116364b8a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    name: "SWAND",
    tagline: "A modern candle-care tool designed to reconnect with your flame.",
    tasks: "75+",
    since: "2025",
    img: "https://images.unsplash.com/photo-1707839568483-9f1924d5f5de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    name: "Cafe Joyeux",
    tagline:
      "Served from the heart — coffee with a mission. A café restaurant like no other.",
    tasks: "125+",
    since: "2023",
    img: "https://images.unsplash.com/photo-1764361276489-79b17d9a8782?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

const CaseStudies = () => (
  <section id="work" data-testid="work-section" className="bg-[#11161D]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
        <SectionHeader
          eyebrow="Featured Clients"
          title="Stores we've shaped, scaled, and shipped for."
          description="A small sample of long-term partnerships — where every pixel, pipeline, and pageview has our fingerprints."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {caseStudies.map((c, i) => (
          <a
            key={c.name}
            href="#contact"
            data-testid={`case-study-${i + 1}`}
            className="group relative block overflow-hidden bg-[#0B0F14] aspect-[4/5] border border-white/[0.06] rounded-xl hover:border-[#F4D64E]/40 hover:-translate-y-0.5 transition-all duration-500"
          >
            <img
              src={c.img}
              alt={c.name}
              className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-all duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/60 to-transparent" />
            <div className="absolute top-5 right-5 flex items-center gap-3 text-[#F4D64E] text-[0.7rem] font-bold tracking-[0.18em] uppercase">
              <span>{c.tasks} tasks</span>
              <span className="h-px w-3 bg-[#F4D64E]/60" />
              <span>Since {c.since}</span>
            </div>
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 text-white">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-[1.6rem] md:text-[1.85rem] font-black tracking-[-0.025em]">
                  {c.name}
                </h3>
                <ArrowUpRight className="w-5 h-5 text-[#F4D64E] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
              <p className="mt-3 text-[0.95rem] text-neutral-400 leading-[1.6] max-w-[40ch]">
                {c.tagline}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   6-V2. CASE STUDIES — V2 only
   ------------------------------------------------------------
   Richer, higher-impact layout: alternating horizontal rows
   per project (big image ↔ detail panel), scroll-reveal per row,
   services tags, uses the client photography from sundaydigital.io.
============================================================ */
const caseStudiesV2 = [
  {
    name: "Veronique Gabai",
    tagline:
      "Sunshine for the Senses and for the Soul — luxury perfumes, fragrance jewelry & beauty.",
    tasks: "320+",
    since: "2020",
    services: [
      "Shopify theme customization",
      "Custom development features",
      "Store management",
      "360° digital services",
    ],
    img: "https://static.wixstatic.com/media/1dfc4b_5fdc10e22982473fb394f844437aa4f4~mv2.jpeg/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_5fdc10e22982473fb394f844437aa4f4~mv2.jpeg",
  },
  {
    name: "SWAND",
    tagline:
      "A modern candle-care tool designed to reconnect with your flame.",
    tasks: "75+",
    since: "2025",
    services: [
      "Shopify theme customization",
      "Custom development features",
      "Store management",
      "360° digital services",
    ],
    img: "https://static.wixstatic.com/media/1dfc4b_fa8443c372284a1d92b0f2e5060b6959~mv2.webp/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_fa8443c372284a1d92b0f2e5060b6959~mv2.webp",
  },
  {
    name: "Cafe Joyeux",
    tagline:
      "Served from the heart — coffee with a mission. A café restaurant like no other.",
    tasks: "125+",
    since: "2023",
    services: [
      "Shopify theme customization",
      "Custom development features",
      "Store management",
      "360° digital services",
    ],
    img: "https://static.wixstatic.com/media/1dfc4b_eabf130d251f4293a250d08d7a480296~mv2.jpg/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_eabf130d251f4293a250d08d7a480296~mv2.jpg",
  },
];

const CaseStudyRow = ({ c, index }) => {
  const reversed = index % 2 === 1;
  return (
    <motion.a
      href="#contact"
      data-testid={`case-study-v2-${index + 1}`}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
      className={`group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${
        reversed ? "lg:[&>div:first-child]:order-2" : ""
      }`}
    >
      {/* Image */}
      <div className="lg:col-span-7 relative">
        <div className="relative aspect-[5/4] overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0B0F14]">
          <motion.img
            src={c.img}
            alt={c.name}
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/55 via-transparent to-transparent" />
          {/* Index badge */}
          <div className="absolute top-5 left-5 flex items-center gap-3 text-[#F4D64E]/90 text-[0.7rem] font-bold tracking-[0.22em] uppercase">
            <span className="tabular-nums">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="h-px w-6 bg-[#F4D64E]/50" />
            <span>Featured</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        >
          <Eyebrow>Client Work</Eyebrow>
          <h3 className="mt-6 font-display text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.25rem] font-black tracking-[-0.03em] leading-[1.02] text-white">
            {c.name}
          </h3>
          <p className="mt-5 text-[1.02rem] md:text-[1.1rem] text-neutral-300 leading-[1.6] max-w-[48ch]">
            {c.tagline}
          </p>

          {/* Stats row */}
          <div className="mt-8 flex items-center gap-8">
            <div>
              <div className="font-display text-[1.75rem] font-black text-[#F4D64E] tabular-nums leading-none">
                {c.tasks}
              </div>
              <div className="mt-2 text-[0.7rem] font-bold tracking-[0.18em] uppercase text-neutral-500">
                Completed Tasks
              </div>
            </div>
            <div className="h-10 w-px bg-white/[0.08]" />
            <div>
              <div className="font-display text-[1.75rem] font-black text-white tabular-nums leading-none">
                {c.since}
              </div>
              <div className="mt-2 text-[0.7rem] font-bold tracking-[0.18em] uppercase text-neutral-500">
                Partner Since
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="mt-8">
            <div className="text-[0.7rem] font-bold tracking-[0.18em] uppercase text-neutral-500 mb-3">
              Services
            </div>
            <div className="flex flex-wrap gap-2">
              {c.services.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[0.8rem] text-neutral-300 transition-colors duration-300 group-hover:border-[#F4D64E]/30 group-hover:bg-[#F4D64E]/[0.05]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* CTA link */}
          <div className="mt-9 inline-flex items-center gap-2 text-[#F4D64E] text-sm font-bold tracking-wide link-underline">
            View project
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </div>
        </motion.div>
      </div>
    </motion.a>
  );
};

const CaseStudiesV2 = () => (
  <section id="work" data-testid="work-section" className="bg-[#11161D]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
        <SectionHeader
          eyebrow="Featured Clients"
          title="Stores we've shaped, scaled, and shipped for."
          description="A small sample of long-term partnerships — where every pixel, pipeline, and pageview has our fingerprints."
        />
      </div>

      <div className="flex flex-col gap-20 md:gap-28">
        {caseStudiesV2.map((c, i) => (
          <CaseStudyRow key={c.name} c={c} index={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   7. TESTIMONIALS
============================================================ */

const testimonials = [
  {
    quote:
      "Working with Sunday Digital was a great experience from start to finish. The team was responsive, professional, and really understood my brand vision.",
    name: "Sarah Sclarandis",
    role: "Owner · sclarandis.com",
  },
  {
    quote:
      "Consistently delivers exceptional service. Prompt support, reliable code, and real responsiveness. The longevity of their solutions speaks for itself.",
    name: "Teddy W.",
    role: "CFO · Rose Forever NYC",
  },
  {
    quote:
      "Our decade-long partnership is a testament to their reliability and expertise. Highly recommendable for top-notch IT development.",
    name: "Sam W.",
    role: "Founder · Teddy Blake NYC",
  },
  {
    quote:
      "They built our website and B2B store with real care. Fast, reliable, cost-conscious — and they genuinely care about our mission.",
    name: "Julie A.",
    role: "General Manager · Cafe Joyeux NYC",
  },
];

const Testimonials = () => (
  <section
    id="testimonials"
    data-testid="testimonials-section"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    <SectionAmbient variant="d" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <SectionHeader eyebrow="In Their Own Words" title="What customers say." />

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {testimonials.map((t, i) => (
          <figure
            key={t.name}
            data-testid={`testimonial-${i + 1}`}
            className="relative bg-[#151B23] border border-white/[0.06] rounded-xl p-8 md:p-10 flex flex-col gap-6 hover:border-[#F4D64E]/30 hover:-translate-y-0.5 transition-all duration-300"
          >
            <div className="font-display text-5xl leading-none text-[#F4D64E]/40 select-none">
              &ldquo;
            </div>
            <blockquote className="text-[1.1rem] md:text-[1.2rem] text-neutral-200 leading-[1.55] tracking-[-0.005em] max-w-[55ch] -mt-4">
              {t.quote}
            </blockquote>
            <figcaption className="mt-auto pt-4 border-t border-white/[0.06] flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-white text-sm">
                  {t.name}
                </div>
                <div className="text-xs text-neutral-500">{t.role}</div>
              </div>
              <div className="flex gap-1 text-[#F4D64E]">
                {[...Array(5)].map((_, k) => (
                  <span key={k} className="text-xs">
                    ★
                  </span>
                ))}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   8. FAQ
============================================================ */

const faqs = [
  {
    q: "Why choose Sunday Digital?",
    a: "Compared to development platforms, freelancers, or typical agencies, we offer professional experts for all your Shopify needs — design, development, and complex projects — delivering speed, precision, and consistency without the hassle of managing multiple providers.",
  },
  {
    q: "Are you an agency?",
    a: "We're not a typical agency. We're a dedicated Shopify team that handles everything in-house — from small updates to full-scale projects — providing the speed, expertise, and consistency you won't get from freelancers or larger agencies.",
  },
  {
    q: "Do you test your work and provide revisions?",
    a: "Yes. Every project is fully tested, and we offer unlimited revisions. At the start of each project, we provide expert consulting to ensure the final result aligns with your goals.",
  },
  {
    q: "What's the turnaround for small tasks and large projects?",
    a: "Small tasks are usually completed within 24–48 hours, with express options for urgent issues. Larger projects follow a clear timeline set at kickoff — ensuring fast, high-quality delivery.",
  },
  {
    q: "Is there a minimum spend or commitment?",
    a: "No strict minimum spend or long-term commitment. Pay-per-task options are available, alongside tailored packages designed to fit your budget and project scope. Get in touch for a detailed quote & promotional packages.",
  },
];

const FAQ = () => {
  const c = useContent("home");
  const fc = c.faq || {};
  const faqItems = Array.isArray(fc.items) && fc.items.length
    ? fc.items
    : faqs.map((f) => ({ title: f.q, body: f.a }));
  const eyebrow = fc.eyebrow || "Got Questions?";
  const headline = fc.headline || "Frequently asked questions.";
  const description = fc.description || "Everything you need to know before starting a project.";
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="bg-[#11161D]">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <SectionHeader
          eyebrow={eyebrow}
          title={headline}
          description={description}
        />
        <div className="mt-14 divide-y divide-white/[0.06] border-y border-white/[0.06]">
          {faqItems.map((f, i) => {
            const isOpen = open === i;
            const q = f.title || f.q || "";
            const a = f.body || f.a || "";
            return (
              <div key={i} data-testid={`faq-item-${i + 1}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i + 1}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span
                    className={`font-display text-[1.1rem] md:text-[1.35rem] font-semibold tracking-[-0.01em] leading-[1.3] pr-4 transition-colors ${
                      isOpen ? "text-[#F4D64E]" : "text-white group-hover:text-[#F4D64E]"
                    }`}
                  >
                    {q}
                  </span>
                  <span
                    className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                      isOpen
                        ? "bg-[#F4D64E] border-[#F4D64E] text-neutral-950"
                        : "border-white/20 text-white group-hover:border-[#F4D64E] group-hover:text-[#F4D64E]"
                    }`}
                  >
                    {isOpen ? (
                      <Minus className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 pb-7"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-[1rem] md:text-[1.05rem] text-neutral-400 leading-[1.7] max-w-[68ch] pt-1">
                      {a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   9. FINAL CTA — single CTA button + right info panel
============================================================ */

const FinalCTA = () => {
  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 480px at 80% 100%, rgba(244,214,78,0.10), transparent 60%), radial-gradient(600px 400px at 10% 0%, rgba(244,214,78,0.06), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 dot-grid opacity-40"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — message + single CTA */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Eyebrow>Ready to transform?</Eyebrow>
            <h2 className="font-display mt-7 text-[2.75rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.04em] leading-[0.98] text-white">
              Start here —
              <br />
              <span className="italic font-normal text-neutral-400">
                we&apos;ll handle
              </span>{" "}
              the rest.
            </h2>
            <p className="mt-8 text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[52ch]">
              Tell us where to send your proposal. We&apos;ll follow up within
              one business day with a scoped plan and a fixed quote.
            </p>

            <div className="mt-10">
              <a
                href="#contact"
                data-testid="final-cta-button"
                className="group inline-flex items-center justify-center gap-2 bg-[#F4D64E] text-neutral-950 px-9 py-5 text-[0.82rem] font-bold tracking-[0.2em] uppercase rounded-md hover:bg-[#FFDF5C] transition-colors shadow-[0_0_0_1px_rgba(244,214,78,0.3),0_20px_60px_-20px_rgba(244,214,78,0.55)]"
              >
                Get Proposal
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>

          {/* Right — info panel */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="bg-[#11161D] border border-white/[0.06] rounded-xl p-7 md:p-8">
              <div className="text-[0.7rem] tracking-[0.22em] uppercase text-[#F4D64E] font-bold">
                What to expect
              </div>

              <ul className="mt-6 space-y-5">
                {[
                  {
                    t: "Reply in < 24 hours",
                    d: "A real human from our team, not an autoresponder.",
                    icon: Clock,
                  },
                  {
                    t: "Scoped proposal & fixed quote",
                    d: "No retainers, no hourly gotchas. Pay per task or per project.",
                    icon: Wallet,
                  },
                  {
                    t: "Kickoff within 48 hours",
                    d: "Small fixes go live in 24–48h. Larger projects start the same week.",
                    icon: Zap,
                  },
                ].map((row) => {
                  const Icon = row.icon;
                  return (
                    <li key={row.t} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-md bg-[#F4D64E]/10 border border-[#F4D64E]/20 flex items-center justify-center shrink-0">
                        <Icon
                          className="w-4 h-4 text-[#F4D64E]"
                          strokeWidth={1.75}
                        />
                      </div>
                      <div>
                        <div className="font-display text-white font-semibold">
                          {row.t}
                        </div>
                        <div className="text-sm text-neutral-400 leading-relaxed">
                          {row.d}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 pt-6 border-t border-white/[0.06] flex items-center justify-between">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-[#F4D64E]/80 border-2 border-[#11161D]" />
                  <div className="w-8 h-8 rounded-full bg-neutral-700 border-2 border-[#11161D]" />
                  <div className="w-8 h-8 rounded-full bg-neutral-500 border-2 border-[#11161D]" />
                </div>
                <div className="text-xs text-neutral-500">
                  30+ active partners · 4.9★ avg.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   10. BLOG — "From Ideas to Impact: Explore Our Blog"
   Copy taken verbatim from sundaydigital.io/revamp01-1
============================================================ */

const blogPosts = [
  {
    title:
      "BFCM Growth Kit: Your Strategic Guide to Black Friday & Cyber Monday",
    excerpt:
      "Black Friday and Cyber Monday are the biggest opportunities of the year — but they're also the most competitive. The difference between a good BFCM and a record-breaking one comes down to small, high-impact improvements across your store, emails, and creative. The Sunday Digital BFCM Growth Kit gives you a clear, founder-friendly framework to maximize revenue without getting overwhelmed.",
    readTime: "5 min read",
    img: "https://static.wixstatic.com/media/1dfc4b_91aee6ff655646f0ac0731942b4c26a5~mv2.png/v1/fill/w_610,h_610,fp_0.50_0.50,q_95,enc_avif,quality_auto/1dfc4b_91aee6ff655646f0ac0731942b4c26a5~mv2.webp",
    href: "https://www.sundaydigital.io/post/bfcm-growth-kit-your-strategic-guide-to-black-friday-cyber-monday",
  },
  {
    title: "The Fork in the Road: Shopify Apps or Custom Development?",
    excerpt:
      "Every Shopify merchant faces a familiar challenge: Should I rely on apps, or invest in custom development? Shopify's App Store is vast. From product reviews to subscription billing, there's an app for almost every feature. Apps are convenient, fast, and cost-effective upfront. But here's the catch: apps are designed for the masses — not your unique business model.",
    readTime: "3 min read",
    img: "https://static.wixstatic.com/media/nsplsh_4b61703869417a42357349~mv2_d_5979_3986_s_4_2.jpg/v1/fill/w_612,h_610,fp_0.50_0.50,q_90,enc_avif,quality_auto/nsplsh_4b61703869417a42357349~mv2_d_5979_3986_s_4_2.webp",
    href: "https://www.sundaydigital.io/post/shopify-apps-vs-custom-development-scalable-budget-conscious-solutions",
  },
  {
    title:
      "Klaviyo vs Mailchimp: Which Email Marketing Platform Fits Your Brand Best?",
    excerpt:
      "Email marketing is still one of the highest ROI channels in digital marketing. Whether it's nurturing leads, driving repeat purchases, or building brand loyalty, email consistently outperforms many other tactics when done right. But to execute well, you need the right tool. And two names dominate the conversation: Klaviyo and Mailchimp.",
    readTime: "3 min read",
    img: "https://static.wixstatic.com/media/1dfc4b_cc9a0509b45b4303bbd1fe5acd6e8da4~mv2.jpg/v1/fill/w_610,h_610,fp_0.50_0.50,q_90,enc_avif,quality_auto/1dfc4b_cc9a0509b45b4303bbd1fe5acd6e8da4~mv2.webp",
    href: "https://www.sundaydigital.io/post/klaviyo-vs-mailchimp-which-email-marketing-platform-fits-your-brand-best",
  },
  {
    title:
      "Data-Driven Creativity: How Smart E-Commerce Brands Balance Art & Analytics",
    excerpt:
      "In the fast-paced world of e-commerce, creativity alone won't cut it — and neither will data by itself. The most successful brands know how to merge the two, turning analytics into inspiration and creativity into measurable results. This balance between art and science is what we at Sunday Digital call data-driven creativity — and it's the secret behind building high-performing online stores that connect emotionally and convert effectively.",
    readTime: "2 min read",
    img: "https://static.wixstatic.com/media/1dfc4b_68bf4ead37904ddd82a0c987f013c1f5~mv2.png/v1/fill/w_610,h_610,fp_0.50_0.50,q_95,enc_avif,quality_auto/1dfc4b_68bf4ead37904ddd82a0c987f013c1f5~mv2.webp",
    href: "https://www.sundaydigital.io/post/data-driven-creativity-how-smart-e-commerce-brands-balance-art-analytics",
  },
];

const Blog = () => {
  // CMS overrides — /admin/content/home → "Blog teaser". Empty fields
  // fall back to defaults so the public layout is byte-identical until
  // the admin edits anything. CTA href default points to the internal
  // /blog route (not the legacy sundaydigital.io/blog URL).
  const home = useContent("home");
  const b = (home && home.blog_teaser) || {};
  const eyebrow = b.eyebrow || "From Ideas to Impact";
  const headline = b.headline || "From Ideas to Impact: Explore Our Blog";
  const body = b.body ||
    "From emerging trends to actionable strategies, our blog delivers expert insights that help transform ideas into measurable impact in Shopify eCommerce and web marketing.";
  const ctaLabel = b.cta_label || "Read our blog";
  // Legacy CTA sentinel: anyone whose stored CMS value still points
  // at the old website root is auto-rerouted to the internal /blog
  // route. Admins can override to any URL.
  const LEGACY = new Set([
    "https://www.sundaydigital.io/blog",
    "https://sundaydigital.io/blog",
    "http://www.sundaydigital.io/blog",
  ]);
  const ctaHref = !b.cta_href || LEGACY.has(b.cta_href) ? "/blog" : b.cta_href;
  const ctaExternal = /^https?:\/\//i.test(ctaHref);

  return (
  <section id="blog" data-testid="blog-section" className="bg-[#11161D]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
        <div className="flex flex-col gap-5 max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="font-display text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
            {headline}
          </h2>
          <p className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[60ch]">
            {body}
          </p>
        </div>
        <a
          href={ctaHref}
          {...(ctaExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          data-testid="blog-read-all-link"
          className="hidden md:inline-flex items-center gap-2 text-sm font-bold tracking-[0.18em] uppercase text-[#F4D64E] link-underline whitespace-nowrap"
        >
          {ctaLabel}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {blogPosts.map((post, i) => (
          <a
            key={post.title}
            href={post.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`blog-post-${i + 1}`}
            className="group relative flex flex-col bg-[#151B23] border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#F4D64E]/40 hover:-translate-y-0.5 transition-all duration-400"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-[#0B0F14]">
              <img
                src={post.img}
                alt={post.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-95 group-hover:scale-[1.04] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#151B23] via-transparent to-transparent" />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#0B0F14]/70 backdrop-blur-sm border border-white/10 px-2.5 py-1 text-[0.62rem] font-bold tracking-[0.16em] uppercase text-[#F4D64E]">
                <BookOpen className="w-3 h-3" strokeWidth={2} />
                {post.readTime}
              </div>
            </div>

            <div className="flex flex-col gap-3 p-5 md:p-6 flex-1">
              <h3 className="font-display text-[1.05rem] md:text-[1.15rem] font-bold leading-snug text-white group-hover:text-[#F4D64E] transition-colors">
                {post.title}
              </h3>
              <p className="text-[0.82rem] text-neutral-400 leading-relaxed line-clamp-4">
                {post.excerpt}
              </p>
              <div className="mt-auto pt-3 flex items-center justify-between text-[0.7rem] tracking-[0.14em] uppercase text-neutral-500 font-semibold">
                <span>Read article</span>
                <ArrowUpRight className="w-4 h-4 text-[#F4D64E] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-10 flex md:hidden">
        <a
          href={ctaHref}
          {...(ctaExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="inline-flex items-center gap-2 text-sm font-bold tracking-[0.18em] uppercase text-[#F4D64E] link-underline"
        >
          {ctaLabel}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   Horizontal pinned scroll — "250+ custom features"
============================================================ */

const horizontalFeatures = [
  {
    icon: Layers,
    kicker: "Storefront",
    title: "Custom product configurators",
    body: "Visualize every variant combo in real time — from engraving to bundle pricing — without apps that bloat your theme.",
  },
  {
    icon: Package,
    kicker: "AOV",
    title: "Bundle & kit builders",
    body: "Merchandise multi-product kits with dynamic pricing, inventory sync, and native checkout support.",
  },
  {
    icon: Boxes,
    kicker: "B2B",
    title: "Wholesale portals",
    body: "Gated pricing, tiered accounts, NET-30 flows, and bulk-order UIs built straight into Shopify Plus.",
  },
  {
    icon: Repeat,
    kicker: "Retention",
    title: "Subscription flows",
    body: "Custom skip, swap, pause, and gift logic on top of Shopify's native subscriptions API — no third-party lock-in.",
  },
  {
    icon: Globe,
    kicker: "Global",
    title: "Multi-store orchestration",
    body: "Ship across regions with synced catalogs, localized content, currency, and tax — managed from one cockpit.",
  },
  {
    icon: CreditCard,
    kicker: "Checkout",
    title: "Checkout extensibility",
    body: "Dynamic upsells, gift messaging, delivery slots, and custom validations using Shopify Functions & UI Extensions.",
  },
  {
    icon: Settings,
    kicker: "Ops",
    title: "Back-office automations",
    body: "Order routing, fulfillment logic, ERP sync, and internal dashboards — so your team stops living in spreadsheets.",
  },
  {
    icon: Filter,
    kicker: "Discovery",
    title: "Advanced search & filtering",
    body: "Faceted navigation, merchandising rules, and instant predictive search tuned for your taxonomy and SKUs.",
  },
];

const HorizontalFeatures = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);

  // Measure the horizontal track and viewport; compute pixel-accurate
  // end translate so the last card lands flush at the right edge
  // (plus a small breathing padding).
  useEffect(() => {
    const calc = () => {
      if (!trackRef.current) return;
      const trackWidth = trackRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;
      const END_PAD = 40; // breathing room at the end
      const distance = Math.max(0, trackWidth - viewportWidth + END_PAD);
      setMaxX(-distance);
    };
    calc();
    window.addEventListener("resize", calc);
    // recompute once fonts & layout settle
    const raf = requestAnimationFrame(calc);
    const t = setTimeout(calc, 300);
    return () => {
      window.removeEventListener("resize", calc);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  // Pixel-accurate horizontal translate
  const x = useTransform(scrollYProgress, [0, 1], [0, maxX]);
  // Subtle progress bar width
  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="capabilities"
      data-testid="horizontal-features-section"
      className="relative bg-[#0B0F14]"
    >
      {/* ============ DESKTOP (md+): pinned horizontal scroll ============ */}
      <div
        ref={ref}
        className="hidden md:block relative"
        style={{ height: "320vh" }}
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          {/* Section header */}
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10">
            <FadeUp>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
                <div className="flex flex-col gap-5 max-w-3xl">
                  <Eyebrow>250+ Custom Features</Eyebrow>
                  <h2 className="font-display text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                    Built for edge cases.{" "}
                    <span className="italic font-normal text-neutral-400">
                      Engineered
                    </span>{" "}
                    to scale.
                  </h2>
                  <p className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[58ch]">
                    A few of the customizations we&apos;ve shipped for merchants
                    who refused to settle for off-the-shelf apps.
                  </p>
                </div>

                {/* Progress indicator */}
                <div className="hidden md:flex items-center gap-4 min-w-[220px]">
                  <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                    Scroll
                  </span>
                  <div className="relative flex-1 h-[2px] bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#F4D64E] rounded-full"
                      style={{ width: barWidth }}
                    />
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          {/* Horizontal track */}
          <motion.div
            ref={trackRef}
            style={{ x }}
            className="flex items-stretch gap-5 md:gap-6 px-6 md:px-10 will-change-transform"
          >
            {horizontalFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <article
                  key={f.title}
                  data-testid={`horizontal-feature-${i + 1}`}
                  className="group relative shrink-0 w-[78vw] sm:w-[56vw] md:w-[38vw] lg:w-[30vw] xl:w-[26vw] bg-[#151B23] border border-white/[0.06] rounded-xl p-7 md:p-9 flex flex-col gap-5 hover:bg-[#1B2330] hover:border-[#F4D64E]/30 transition-all duration-400 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[0.7rem] font-bold tracking-[0.2em] uppercase text-[#F4D64E]/80">
                      {String(i + 1).padStart(2, "0")} · {f.kicker}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-[#F4D64E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
                  </div>

                  <div className="w-11 h-11 rounded-md bg-[#F4D64E]/10 border border-[#F4D64E]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.5} />
                  </div>

                  <h3 className="font-display text-2xl md:text-[1.6rem] font-bold tracking-tight text-white leading-[1.15]">
                    {f.title}
                  </h3>
                  <p className="text-[0.95rem] text-neutral-400 leading-relaxed">
                    {f.body}
                  </p>

                  <div className="mt-auto pt-5 flex items-center gap-2 text-[0.7rem] tracking-[0.18em] uppercase text-neutral-500 font-bold">
                    <span className="h-px w-5 bg-[#F4D64E]/60" />
                    Custom build
                  </div>
                </article>
              );
            })}

            {/* trailing CTA card */}
            <article className="shrink-0 w-[78vw] sm:w-[56vw] md:w-[38vw] lg:w-[30vw] xl:w-[26vw] bg-transparent border border-dashed border-white/20 rounded-xl p-7 md:p-9 flex flex-col justify-between gap-6">
              <div>
                <span className="text-[0.7rem] font-bold tracking-[0.2em] uppercase text-[#F4D64E]/80">
                  +240 more
                </span>
                <h3 className="font-display text-2xl md:text-[1.7rem] font-bold tracking-tight text-white leading-[1.1] mt-4">
                  Got a wild idea?
                  <br />
                  <span className="italic font-normal text-neutral-400">We probably</span>{" "}
                  built it already.
                </h3>
                <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
                  Tell us what your store needs — if it&apos;s logical, it&apos;s codable.
                </p>
              </div>
              <a
                href="#contact"
                data-testid="horizontal-features-cta"
                className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] rounded-md border border-[#F4D64E]/40 hover:bg-[#F4D64E] hover:text-neutral-950 px-5 py-3 w-fit transition-colors"
              >
                Pitch your build
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </article>
          </motion.div>
        </div>
      </div>

      {/* ============ MOBILE (<md): vertical stack, subscribfy-style ============ */}
      <div className="md:hidden">
        <div className="max-w-7xl mx-auto px-6 py-20">
          {/* Header */}
          <FadeUp>
            <div className="flex flex-col gap-5 mb-12">
              <Eyebrow>250+ Custom Features</Eyebrow>
              <h2 className="font-display text-[2.25rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                Built for edge cases.{" "}
                <span className="italic font-normal text-neutral-400">
                  Engineered
                </span>{" "}
                to scale.
              </h2>
              <p className="text-[1rem] text-neutral-400 leading-[1.65]">
                A few of the customizations we&apos;ve shipped for merchants who
                refused to settle for off-the-shelf apps.
              </p>
            </div>
          </FadeUp>

          {/* Stack of cards */}
          <div className="flex flex-col gap-4">
            {horizontalFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <FadeUp key={f.title} delay={Math.min(i * 0.05, 0.35)}>
                  <article
                    data-testid={`horizontal-feature-mobile-${i + 1}`}
                    className="group relative overflow-hidden bg-[#151B23] border border-white/[0.06] rounded-2xl"
                  >
                    {/* Visual area (icon hero) */}
                    <div className="relative h-44 overflow-hidden">
                      {/* decorative gradient bg */}
                      <div
                        aria-hidden
                        className="absolute inset-0"
                        style={{
                          background:
                            "radial-gradient(600px 220px at 80% 20%, rgba(244,214,78,0.18), transparent 60%), radial-gradient(400px 200px at 10% 90%, rgba(244,214,78,0.08), transparent 60%), linear-gradient(180deg, #1B2330 0%, #151B23 100%)",
                        }}
                      />
                      {/* subtle dot grid */}
                      <div
                        aria-hidden
                        className="absolute inset-0 opacity-40"
                        style={{
                          backgroundImage:
                            "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
                          backgroundSize: "18px 18px",
                        }}
                      />

                      {/* kicker top-left */}
                      <div className="absolute top-5 left-5 flex items-center gap-2 text-[0.64rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
                        <span className="tabular-nums text-neutral-500">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="h-px w-4 bg-[#F4D64E]/60" />
                        {f.kicker}
                      </div>

                      {/* big glyph — offset right, blurred yellow halo behind it */}
                      <div className="absolute right-5 top-1/2 -translate-y-1/2">
                        <div
                          aria-hidden
                          className="absolute inset-0 blur-xl bg-[#F4D64E]/15 rounded-full"
                        />
                        <div className="relative w-20 h-20 rounded-2xl bg-[#F4D64E]/10 border border-[#F4D64E]/30 flex items-center justify-center">
                          <Icon
                            className="w-9 h-9 text-[#F4D64E]"
                            strokeWidth={1.5}
                          />
                        </div>
                      </div>

                      {/* title overlay at bottom-left */}
                      <div className="absolute bottom-5 left-5 right-28">
                        <h3 className="font-display text-[1.5rem] font-bold tracking-tight text-white leading-[1.1]">
                          {f.title}
                        </h3>
                      </div>
                    </div>

                    {/* Body row — description + circular arrow */}
                    <div className="flex items-center justify-between gap-5 p-5">
                      <p className="text-[0.88rem] text-neutral-400 leading-relaxed flex-1">
                        {f.body}
                      </p>
                      <a
                        href="#contact"
                        aria-label={`Learn more about ${f.title}`}
                        data-testid={`horizontal-feature-mobile-cta-${i + 1}`}
                        className="shrink-0 w-11 h-11 rounded-full bg-[#F4D64E] text-neutral-950 flex items-center justify-center hover:bg-[#FFDF5C] transition-colors shadow-[0_0_0_1px_rgba(244,214,78,0.25)]"
                      >
                        <ArrowRight className="w-5 h-5" strokeWidth={2} />
                      </a>
                    </div>
                  </article>
                </FadeUp>
              );
            })}

            {/* Trailing CTA card (mobile) */}
            <FadeUp delay={0.4}>
              <article className="relative overflow-hidden bg-transparent border border-dashed border-white/20 rounded-2xl p-6 flex flex-col gap-4">
                <span className="text-[0.64rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
                  +240 more
                </span>
                <h3 className="font-display text-[1.6rem] font-bold tracking-tight text-white leading-[1.1]">
                  Got a wild idea?
                  <br />
                  <span className="italic font-normal text-neutral-400">
                    We probably
                  </span>{" "}
                  built it already.
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  Tell us what your store needs — if it&apos;s logical, it&apos;s
                  codable.
                </p>
                <a
                  href="#contact"
                  data-testid="horizontal-features-mobile-cta"
                  className="group mt-2 inline-flex items-center gap-2 text-[0.74rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] rounded-md hover:bg-[#FFDF5C] px-5 py-3 w-fit transition-colors"
                >
                  Pitch your build
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </article>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   Footer
============================================================ */

const Footer = () => {
  const { open: openContact } = useContactPanel();
  const globals = useContent("globals");
  const n = globals?.nav || {};
  const f = globals?.footer || {};
  // Footer logo reuses Nav logo if empty. Same fallback as Nav.
  const logoSrc = f.logo || n.logo || NAV_LOGO_FALLBACK;
  const tagline = f.tagline ||
    "One partner. Every Shopify need. Design, development, marketing — in-house.";
  const columns = Array.isArray(f.columns) && f.columns.length
    ? f.columns.map((c) => ({
        title: c.title || "",
        links: parseLinksField(c.links),
      }))
    : [
        { title: "Services", links: [
          { label: "Development", href: "#services" },
          { label: "Marketing", href: "#marketing" },
          { label: "Creative", href: "#marketing" },
          { label: "Migration", href: "#services" },
        ] },
        { title: "Company", links: [
          { label: "Work", href: "#work" },
          { label: "Process", href: "#process" },
          { label: "FAQ", href: "#faq" },
          { label: "Contact", href: "#contact" },
        ] },
        { title: "Get in touch", links: [
          { label: "hello@sundaydigital.io", href: "mailto:hello@sundaydigital.io" },
          { label: "Book a call", href: "#contact" },
        ] },
      ];
  const copyrightTpl = f.copyright || "© {year} Sunday Digital. All rights reserved.";
  const copyright = copyrightTpl.replace(/\{year\}/g, String(new Date().getFullYear()));
  const legalLinks = Array.isArray(f.legal_links) && f.legal_links.length
    ? f.legal_links.map((l) => ({ label: l.title || "", href: l.body || "#" }))
    : [{ label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }];

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B0F14] text-neutral-400 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <img
              src={mediaUrl(logoSrc) || logoSrc}
              alt="Sunday Digital"
              className="h-11 w-auto select-none"
              draggable={false}
            />
            <p className="mt-4 text-sm leading-relaxed max-w-xs">
              {tagline}
            </p>
          </div>
          {columns.map((col, i) => (
            <div key={i}>
              <div className="text-[0.7rem] tracking-[0.22em] uppercase text-neutral-500 font-bold">
                {col.title}
              </div>
              <ul className="mt-5 space-y-3 text-sm">
                {col.links.map((l, j) => (
                  <li key={j}>
                    {isContactHref(l.href) ? (
                      <button
                        type="button"
                        onClick={openContact}
                        className="hover:text-[#F4D64E] text-left"
                      >
                        {l.label}
                      </button>
                    ) : (
                      <a href={l.href} className="hover:text-[#F4D64E]">{l.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>{copyright}</div>
          <div className="flex items-center gap-6">
            {legalLinks.map((l, i) => (
              isContactHref(l.href) ? (
                <button
                  key={i}
                  type="button"
                  onClick={openContact}
                  className="hover:text-[#F4D64E]"
                >
                  {l.label}
                </button>
              ) : l.href && l.href.startsWith("/") ? (
                <Link key={i} to={l.href} className="hover:text-[#F4D64E]">{l.label}</Link>
              ) : (
                <a key={i} href={l.href} className="hover:text-[#F4D64E]">{l.label}</a>
              )
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Parse the `Label | href` multi-line field used for footer column links.
// Each line becomes `{ label, href }`. Blank lines ignored.
function parseLinksField(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, ...rest] = line.split("|");
      const href = rest.join("|").trim();
      return { label: (label || "").trim(), href: href || "#" };
    });
}

/* ============================================================
   Home V1 — base / reference composition
============================================================ */

const HomeV1 = () => {
  useEffect(() => {
    document.title = "Sunday Digital — V1 · One Partner. Every Shopify Need.";
  }, []);

  return (
    <div
      data-testid="home-page"
      data-variant="v1"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <HeroV1 />
        <TrustStrip />
        <LogoStrip />
        <DevelopmentOverview />
        <HowItWorks />
        <ServicesGrid />
        <HorizontalFeatures />
        <MarketingCreative />
        <CaseStudies />
        <Testimonials />
        <FAQ />
        <FinalCTA />
        <Blog />
      </main>
      <Footer />
    </div>
  );
};

/* ============================================================
   V5 — MEDIA / VISUAL LAYOUT COMPONENTS (placeholders only)
   ============================================================
   Stock visuals only — no copy modification.
   Unsplash for images (stable public CDN) + Pexels for 1 bg
   video. Every media slot has a poster/fallback background so
   nothing renders empty if a URL ever goes away.
============================================================ */

// Curated Unsplash photo IDs for each V5 media slot
const V5_MEDIA = {
  marquee: [
    { id: "1441986300917-64674bd600d8", alt: "Clothing rack" },
    { id: "1542291026-7eec264c27ff", alt: "Sneakers on stand" },
    { id: "1523275335684-37898b6baf30", alt: "Watch product" },
    { id: "1556228720-195a672e8a03", alt: "Skincare bottles" },
    { id: "1560769629-975ec94e6a86", alt: "White sneakers" },
    { id: "1540555700478-4be289fbecef", alt: "Minimal shirt" },
    { id: "1572635196237-14b3f281503f", alt: "Handbag white bg" },
    { id: "1553062407-98eeb64c6a62", alt: "Sneaker styled" },
    { id: "1571945153237-4929e783af4a", alt: "Coffee packaging" },
    { id: "1544441893-675973e31985", alt: "Clothing rack closeup" },
  ],
  snippets: [
    { id: "1556155092-490a1ba16284", label: "Mobile UI", tall: true },
    { id: "1556742049-0cfed4f6a45d", label: "Desktop Workflow", tall: false },
    { id: "1585386959984-a4155224a1ad", label: "Checkout Flow", tall: false },
    { id: "1607082348824-0a96f2a4b9da", label: "Product Page", tall: true },
    { id: "1555529669-e69e7aa0ba9a", label: "Analytics", tall: false },
    { id: "1512941937669-90a1b58e7e9c", label: "Storefront", tall: false },
  ],
  showcase: [
    { id: "1542291026-7eec264c27ff", title: "Custom swatches", body: "Variant UIs that render in real time — from engraving to bundle pricing." },
    { id: "1607082348824-0a96f2a4b9da", title: "Product pages", body: "Cinematic PDPs with sticky CTAs, zoomable media, and native extensibility." },
    { id: "1585386959984-a4155224a1ad", title: "Checkout upsell", body: "Dynamic cross-sells wired through Shopify Functions — zero theme bloat." },
    { id: "1523275335684-37898b6baf30", title: "Bundle builders", body: "Multi-product kits with inventory sync and native checkout support." },
    { id: "1556155092-490a1ba16284", title: "Storefront interactions", body: "Micro-animations and filter UIs tuned to your taxonomy and SKUs." },
  ],
  background: "1556228453-efd6c1ff04f6",
  spotlight: "1490481651871-ab68de25d43d",
};

const unsplash = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

/* --- 1. MediaMarqueeV5 — full-bleed 2-row counter-scrolling image strip --- */
const MediaMarqueeV5 = () => {
  const top = V5_MEDIA.marquee;
  const bot = [...V5_MEDIA.marquee].reverse();
  return (
    <section
      data-testid="media-marquee-v5"
      className="relative bg-[#0B0F14] py-14 md:py-20 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-[#0B0F14] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-[#0B0F14] to-transparent z-10" />

      <div className="flex flex-col gap-5">
        <div className="flex gap-5 will-change-transform animate-[marquee-left_60s_linear_infinite]">
          {[...top, ...top].map((m, i) => (
            <div
              key={`t-${i}`}
              className="shrink-0 w-[320px] md:w-[400px] h-[200px] md:h-[240px] rounded-xl overflow-hidden border border-white/[0.06] bg-[#151B23]"
            >
              <img
                src={unsplash(m.id, 800)}
                alt={m.alt}
                loading="lazy"
                className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-5 will-change-transform animate-[marquee-right_70s_linear_infinite]">
          {[...bot, ...bot].map((m, i) => (
            <div
              key={`b-${i}`}
              className="shrink-0 w-[320px] md:w-[400px] h-[200px] md:h-[240px] rounded-xl overflow-hidden border border-white/[0.06] bg-[#151B23]"
            >
              <img
                src={unsplash(m.id, 800)}
                alt={m.alt}
                loading="lazy"
                className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --- 2. UISnippetsGridV5 — 3-col staggered UI mockup grid --- */
const UISnippetsGridV5 = () => (
  <section
    data-testid="ui-snippets-v5"
    className="relative bg-[#0B0F14] py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp>
        <div className="flex items-end justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex flex-col gap-4 max-w-xl">
            <Eyebrow>In the wild</Eyebrow>
            <h3 className="font-display text-[2rem] md:text-[2.75rem] font-extrabold tracking-[-0.03em] leading-[1.05] text-white">
              Every pixel, <span className="italic font-normal text-neutral-400">accounted for.</span>
            </h3>
          </div>
          <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            Select work · 2024–26
          </span>
        </div>
      </FadeUp>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {V5_MEDIA.snippets.map((s, i) => (
          <FadeUp key={s.id} delay={i * 0.05}>
            <figure
              data-testid={`ui-snippet-${i + 1}`}
              className={`group relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#151B23] ${
                s.tall ? "aspect-[3/4]" : "aspect-[4/3]"
              }`}
            >
              <img
                src={unsplash(s.id, 900)}
                alt={s.label}
                loading="lazy"
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-[700ms]"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/40 to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
                  {s.label}
                </span>
                <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-[#F4D64E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
              </figcaption>
            </figure>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* --- 3. MediaBackgroundV5 — blurred Pexels video bg + overlay pull-quote --- */
const MediaBackgroundV5 = () => {
  // CMS overrides — admin-managed via /admin/content/home →
  // "Media background". Falls back to the original hard-coded copy
  // and Pexels video so visuals never break when unset.
  const home = useContent("home");
  const mb = (home && home.media_background) || {};
  const eyebrow = mb.eyebrow || "Craft & scale";
  const headlineMain = mb.headline_main || "Built for edge cases.";
  const headlineItalic = mb.headline_italic || "Engineered";
  const headlineTail = mb.headline_tail || "to scale.";
  const body =
    mb.body ||
    "A few of the customizations we've shipped for merchants who refused to settle for off-the-shelf apps.";
  const rawMedia =
    mb.media || "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";
  const resolvedMedia =
    rawMedia && /^https?:\/\//i.test(rawMedia) ? rawMedia :
    rawMedia ? `${process.env.REACT_APP_BACKEND_URL || ""}${rawMedia.startsWith("/") ? "" : "/"}${rawMedia}` :
    "";
  const isVideo = /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(resolvedMedia);

  return (
    <section
      data-testid="media-background-v5"
      className="relative bg-[#0B0F14] overflow-hidden"
    >
      <div className="relative h-[70vh] min-h-[520px] flex items-center justify-center">
        {/* Static fallback image layer (only when there's NO video — gives
            a non-empty bg while metadata loads, or if the admin uploaded
            an image instead of a video). */}
        {!isVideo && resolvedMedia ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{
              backgroundImage: `url(${resolvedMedia})`,
              filter: "blur(8px) brightness(0.5)",
              transform: "scale(1.05)",
            }}
          />
        ) : null}
        {isVideo ? (
          <video
            key={resolvedMedia}
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            style={{ filter: "blur(6px) brightness(0.55)" }}
          >
            <source src={resolvedMedia} type="video/mp4" />
          </video>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-transparent to-[#0B0F14] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/60 via-transparent to-[#0B0F14]/60 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-6 font-display text-[2.5rem] md:text-[4rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              {headlineMain}
              {headlineItalic ? (
                <>
                  {" "}
                  <span className="italic font-normal text-neutral-300">{headlineItalic}</span>
                </>
              ) : null}
              {headlineTail ? <> {headlineTail}</> : null}
            </span>
          </h2>
          {body ? (
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-6 text-[1.02rem] md:text-[1.2rem] text-neutral-300 leading-[1.6] max-w-2xl mx-auto"
            >
              <span>{body}</span>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};

/* --- 4. HorizontalShowcaseV5 — pinned horizontal scroll, image panels --- */
const HorizontalShowcaseV5 = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);

  useEffect(() => {
    const calc = () => {
      if (!trackRef.current) return;
      const trackWidth = trackRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;
      const END_PAD = 48;
      const distance = Math.max(0, trackWidth - viewportWidth + END_PAD);
      setMaxX(-distance);
    };
    calc();
    window.addEventListener("resize", calc);
    const raf = requestAnimationFrame(calc);
    const t = setTimeout(calc, 300);
    return () => {
      window.removeEventListener("resize", calc);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, maxX]);
  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="showcase"
      data-testid="horizontal-showcase-v5"
      className="relative bg-[#0B0F14]"
    >
      <div
        ref={ref}
        className="hidden md:block relative"
        style={{ height: "260vh" }}
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10">
            <FadeUp>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
                <div className="flex flex-col gap-4 max-w-3xl">
                  <Eyebrow>Storefront interactions</Eyebrow>
                  <h2 className="font-display text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                    Design that behaves{" "}
                    <PillarAccent>like software</PillarAccent>.
                  </h2>
                  <p className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[58ch]">
                    Micro-interactions, custom flows, and merchandising UI —
                    engineered directly into the theme, no bloated apps.
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-4 min-w-[220px]">
                  <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                    Scroll
                  </span>
                  <div className="relative flex-1 h-[2px] bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#F4D64E] rounded-full"
                      style={{ width: barWidth }}
                    />
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <motion.div
            ref={trackRef}
            style={{ x }}
            className="flex items-stretch gap-5 md:gap-6 px-6 md:px-10 will-change-transform"
          >
            {V5_MEDIA.showcase.map((s, i) => (
              <article
                key={s.title}
                data-testid={`showcase-panel-${i + 1}`}
                className="group relative shrink-0 w-[30vw] max-w-[560px] aspect-[3/4] rounded-2xl overflow-hidden border border-white/[0.08] bg-[#151B23] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.95)]"
              >
                <img
                  src={unsplash(s.id, 900)}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-[700ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/50 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 p-7 md:p-8 flex flex-col gap-3">
                  <span className="text-[0.72rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/90 tabular-nums">
                    {String(i + 1).padStart(2, "0")} · Interaction
                  </span>
                  <h3 className="font-display text-[1.5rem] md:text-[1.85rem] font-black tracking-[-0.02em] text-white leading-[1.1]">
                    {s.title}
                  </h3>
                  <p className="text-[0.92rem] md:text-[1rem] text-neutral-300 leading-[1.55] max-w-[44ch]">
                    {s.body}
                  </p>
                </div>
                <ArrowUpRight className="absolute top-5 right-5 w-5 h-5 text-white/70 group-hover:text-[#F4D64E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
              </article>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="md:hidden py-16">
        <div className="max-w-7xl mx-auto px-6 mb-8">
          <FadeUp>
            <Eyebrow>Storefront interactions</Eyebrow>
            <h2 className="mt-5 font-display text-[2.25rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Design that behaves like software.
            </h2>
          </FadeUp>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-6 pl-6 pr-6 scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {V5_MEDIA.showcase.map((s, i) => (
            <article
              key={s.title}
              className="snap-start shrink-0 w-[78vw] aspect-[3/4] rounded-xl overflow-hidden border border-white/[0.08] bg-[#151B23] relative"
            >
              <img
                src={unsplash(s.id, 700)}
                alt={s.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col gap-2">
                <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/90">
                  {String(i + 1).padStart(2, "0")} · Interaction
                </span>
                <h3 className="font-display text-[1.45rem] font-black tracking-[-0.02em] text-white leading-[1.1]">
                  {s.title}
                </h3>
                <p className="text-[0.9rem] text-neutral-300 leading-[1.55]">
                  {s.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --- 5. LifestyleSpotlightV5 — split image + existing copy --- */
const LifestyleSpotlightV5 = () => (
  <section
    data-testid="lifestyle-spotlight-v5"
    className="relative bg-[#11161D] py-20 md:py-28 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <FadeUp className="lg:col-span-7 order-2 lg:order-1">
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/[0.06] bg-[#151B23]">
          <img
            src={unsplash(V5_MEDIA.spotlight, 1600)}
            alt="Lifestyle product shot"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover animate-[kb-slow_22s_ease-in-out_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F14]/40 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 px-4 py-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
            <span className="text-[0.68rem] font-bold tracking-[0.22em] uppercase text-white/90">
              Lifestyle · Editorial
            </span>
          </div>
        </div>
      </FadeUp>

      <FadeUp className="lg:col-span-5 order-1 lg:order-2">
        <Eyebrow>Beyond design</Eyebrow>
        <h2 className="mt-6 font-display text-[2.25rem] md:text-[3rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
          Creative thinking.{" "}
          <span className="italic font-normal text-neutral-400">Proven</span>{" "}
          <PillarAccent>strategies</PillarAccent>.
        </h2>
        <p className="mt-6 text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65] max-w-[48ch]">
          From Shopify design and advanced UX/UI to video production and social
          ad creatives — we craft digital experiences that are clean, cohesive,
          and impactful.
        </p>
        <div className="mt-9">
          <SecondaryButton testId="spotlight-cta" href="#contact">
            See the work
          </SecondaryButton>
        </div>
      </FadeUp>
    </div>
  </section>
);



/* ============================================================
   V5 only — Vertical variant of "Our Shopify Services"
   ------------------------------------------------------------
   Duplicated under the existing horizontal accordion. Same
   header + same 6 services data (untouched); only the
   interaction is rotated 90° (vertical accordion).
============================================================ */
// Stock media per V5 service row (Redstone-style media panel on the right).
// Mix of images + videos; videos always have a poster fallback so the panel
// never goes blank if a CDN URL changes.
const SERVICE_MEDIA_V5V = [
  // 01 Custom Theme Development → designer/Figma workspace (image)
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80",
  },
  // 02 Store Maintenance → analytics dashboard (image)
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
  },
  // 03 App Development → looping code-on-screen video
  {
    type: "video",
    src: "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4",
    poster: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=1200&q=80",
  },
  // 04 Shopify Custom Tasks → dark, abstract code-on-screen still
  // (closely fits "Workflow automations + Custom features + Unique
  // requirements" — distinct from tile 03's IDE video and tile 06's
  // team-collab clip).
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?auto=format&fit=crop&w=1600&q=80",
  },
  // 05 Migration to Shopify → server/data abstract (image)
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80",
  },
  // 06 White Label Development → looping team-collaboration video
  {
    type: "video",
    src: "https://videos.pexels.com/video-files/2887463/2887463-hd_1920_1080_25fps.mp4",
    poster: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
  },
];

// Maps the schema's icon name → the actual lucide component.
// Definition lives near `serviceIconsV2` so it's available for both
// `ServiceMobileCardV2` (above) and `ServiceRowV5V` (below).

const ServiceRowV5V = ({ s, index, total, active, onActivate, ctaHref }) => {
  const Icon = SERVICE_ICON_MAP[s.icon] || serviceIconsV2[index] || Code2;
  // Tag is always rendered as `${number} / ${label}` on the public site.
  // `s.tag` is preserved for legacy hard-coded entries; new CMS items
  // pass tag_number + tag_label which we recombine here.
  const num = s.tag_number != null ? s.tag_number : (s.tag ? s.tag.split(" / ")[0] : String(index + 1).padStart(2, "0"));
  const label = s.tag_label != null ? s.tag_label : (s.tag ? s.tag.split(" / ")[1] || "" : "");
  const highlights = Array.isArray(s.highlights) ? s.highlights : (serviceHighlightsV2[index] || []);
  const mediaUrl = s.media || (SERVICE_MEDIA_V5V[index] && SERVICE_MEDIA_V5V[index].src) || "";
  const mediaIsVideo = /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(mediaUrl);
  const mediaPoster = (SERVICE_MEDIA_V5V[index] && SERVICE_MEDIA_V5V[index].poster) || undefined;
  const totalCount = total || 6;
  const isActive = active === index;
  const anyActive = active !== null && active !== undefined;
  const T = { duration: 0.4, ease: [0.4, 0, 0.2, 1] };

  const rowAnimate = isActive
    ? { flexGrow: 1, flexShrink: 1, flexBasis: 0 }
    : anyActive
    ? { flexGrow: 0, flexShrink: 0, flexBasis: "5rem" }
    : { flexGrow: 1, flexShrink: 1, flexBasis: 0 };

  return (
    <motion.div
      data-testid={`service-row-v5v-${index + 1}`}
      onMouseEnter={() => onActivate(index)}
      onFocus={() => onActivate(index)}
      onClick={() => onActivate(index)}
      tabIndex={0}
      role="button"
      aria-expanded={isActive}
      aria-label={s.title}
      data-active={isActive}
      animate={rowAnimate}
      transition={T}
      className={`group relative w-full min-h-[5rem] cursor-pointer overflow-hidden rounded-2xl border bg-gradient-to-br from-[#0D1117] via-[#0A0E13] to-[#070A0F] focus:outline-none focus-visible:border-[#F4D64E]/50 ${
        isActive ? "border-[#F4D64E]/30" : "border-white/[0.08]"
      }`}
      style={{ transition: "border-color 400ms ease-in-out" }}
    >
      {/* Left-side vertical accent bar — draws top→bottom when active */}
      <span
        aria-hidden
        className="absolute top-0 left-0 w-[2px] bg-[#F4D64E]"
        style={{
          height: isActive ? "100%" : "0%",
          transition: "height 600ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      {/* Subtle warm glow — repositioned BOTTOM-RIGHT (was top-right);
          sits behind the media panel and softly illuminates the active row */}
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-24 w-[28rem] h-80 rounded-full bg-[#F4D64E]/[0.05] blur-3xl"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 500ms ease-in-out",
        }}
      />

      {/* TITLE ROW — when collapsed: fully centered (no active row OR another
          row active). When THIS row is active: anchored near top with breathing
          room above (top-6 instead of top-0) */}
      <div
        className={`absolute left-0 right-0 px-6 md:px-8 flex items-center gap-6 pointer-events-none ${
          isActive ? "top-6 h-20" : "inset-y-0"
        }`}
      >
        <span className="tabular-nums text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] w-10 shrink-0">
          {num}
        </span>
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F4D64E]/10 border border-[#F4D64E]/25 shrink-0">
          <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.75} />
        </div>
        <h3
          className="font-display font-black tracking-[-0.015em] text-white"
          style={{
            fontSize: isActive ? "1.75rem" : "1.3rem",
            transition: "font-size 400ms ease-in-out",
          }}
        >
          {s.title}
        </h3>
        <div className="flex-1" />
        <span className="hidden md:inline text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 whitespace-nowrap">
          {label}
        </span>
        <ArrowUpRight
          aria-hidden
          className="w-5 h-5 shrink-0"
          style={{
            color: isActive ? "#F4D64E" : "rgba(115, 115, 115, 1)",
            transition: "color 400ms ease-in-out",
          }}
        />
      </div>

      {/* EXPANDED — left column (body + bullets stacked) + right media panel.
          Top starts at top-32 (was top-20) to give title room to breathe.
          Bottom is bottom-20 (was bottom-0) so the divider line sits well below
          the media. */}
      <div
        className="absolute left-0 right-0 top-32 bottom-20 px-6 md:px-8 pointer-events-none overflow-hidden"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 400ms ease-in-out",
          pointerEvents: isActive ? "auto" : "none",
        }}
      >
        <div className="h-full grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
          {/* LEFT — body paragraph THEN bullets stacked underneath */}
          <div className="md:col-span-6 min-w-0 flex flex-col gap-6 pt-2">
            <p className="text-[1rem] md:text-[1.08rem] text-neutral-300 leading-[1.6] max-w-[52ch]">
              {s.body}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-center gap-3 text-[0.92rem] text-neutral-300"
                >
                  <span className="w-1 h-1 rounded-full bg-[#F4D64E] shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — stock media panel (Redstone-style); supports image OR
              looping muted video; both with subtle Ken Burns / playback drift */}
          <div className="md:col-span-6 relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#0F141A]">
            {isActive && mediaUrl && (
              mediaIsVideo ? (
                <video
                  src={mediaUrl}
                  poster={mediaPoster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover opacity-90"
                />
              ) : (
                <img
                  src={mediaUrl}
                  alt={`${s.title} preview`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-90 animate-[kb-slow_22s_ease-in-out_infinite_alternate]"
                />
              )
            )}
            {/* Subtle vignette for depth + readability */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F14]/55 via-transparent to-transparent pointer-events-none" />
            {/* Tiny placeholder badge bottom-left */}
            <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-sm border border-white/10 px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
              <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-white/85">
                {label} · Preview
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* DIVIDER + footer line — anchored cleanly at bottom-8 with its own
          generous gap above the media (we made room via bottom-20 above) */}
      <div
        className="absolute left-6 right-6 md:left-8 md:right-8 bottom-8 pt-5 border-t border-white/[0.07] flex items-center justify-between"
        style={{
          opacity: isActive ? 1 : 0,
          transition: "opacity 400ms ease-in-out",
          pointerEvents: isActive ? "auto" : "none",
        }}
      >
        <a
          href={ctaHref || "#contact"}
          onClick={(e) => e.stopPropagation()}
          data-testid={`service-row-v5v-${index + 1}-cta`}
          className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors link-underline"
        >
          Learn more
        </a>
        <span className="text-[0.7rem] tracking-[0.22em] uppercase text-neutral-500 tabular-nums">
          {String(index + 1).padStart(2, "0")} / {String(totalCount).padStart(2, "0")}
        </span>
      </div>
    </motion.div>
  );
};

const ServicesGridV5Vertical = () => {
  const [active, setActive] = useState(null);
  // CMS overrides — admin-managed via /admin/content/home →
  // "Our Shopify services". Falls back to the live hard-coded
  // `services` array + `serviceIconsV2` + `serviceHighlightsV2` +
  // `SERVICE_MEDIA_V5V` so visuals never break when unset.
  const home = useContent("home");
  const sg = (home && home.services_grid) || {};
  const eyebrow = sg.eyebrow || "Our Shopify Services";
  const headlineMain = sg.headline_main || "Development expertise — across every surface of";
  const headlineAccent = sg.headline_accent || "your store";
  const description =
    sg.description ||
    "Comprehensive Shopify services tailored to your business needs — from the storefront to the checkout to the back office.";
  const ctaLabel = sg.cta_label || "See all services";
  const ctaHref = sg.cta_href || "#contact";
  const hoverHint = sg.hover_hint || "Hover a service to expand";
  const items = Array.isArray(sg.items) && sg.items.length ? sg.items : services;
  return (
    <section
      id="services-vertical"
      data-testid="services-vertical-section"
      className="relative overflow-hidden bg-[#11161D]"
    >
      <SectionAmbient variant="a" />
      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-28">
        {/* Same header copy as the horizontal version (duplicated as requested) */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-14 md:mb-20">
          <PillarHeader
            eyebrow={eyebrow}
            description={description}
          >
            <h2 className={pillarHeadingClass}>
              {headlineMain}
              {headlineAccent ? (
                <>
                  {" "}
                  <PillarAccent>{headlineAccent}</PillarAccent>
                </>
              ) : null}
              .
            </h2>
          </PillarHeader>
          {ctaLabel ? (
            <a
              href={ctaHref || "#"}
              data-testid="services-v5v-all-link"
              className="hidden md:inline-flex items-center gap-2 text-sm font-bold tracking-wide text-[#F4D64E] link-underline whitespace-nowrap"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </a>
          ) : null}
        </div>

        {/* Desktop — vertical accordion */}
        <div
          data-testid="services-accordion-v5v"
          className="hidden md:flex md:flex-col h-[64rem] gap-2 lg:gap-3"
        >
          {items.map((s, i) => (
            <ServiceRowV5V
              key={`${s.title || "svc"}-${i}`}
              s={s}
              index={i}
              total={items.length}
              active={active}
              onActivate={setActive}
              ctaHref={ctaHref}
            />
          ))}
        </div>

        {hoverHint ? (
          <div className="hidden md:flex items-center gap-3 mt-6 text-[0.68rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            <span className="h-px w-8 bg-neutral-700" />
            {hoverHint}
          </div>
        ) : null}

        {/* Mobile — stacked cards (reuses existing mobile card) */}
        <div className="md:hidden flex flex-col gap-4">
          {items.map((s, i) => (
            <ServiceMobileCardV2 key={`${s.title || "svc"}-m-${i}`} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};



/* ============================================================
   V5 only — Creative Capabilities (Evolve-style hover reveal)
   ------------------------------------------------------------
   - Header pattern matches `ServicesGridV5Vertical`.
   - 2 × 3 grid of capability cards, equal weight at idle.
   - Hover any card: that card stays full-opacity; the other 5
     drop to 15%; a full-width capability-themed background
     image fades in behind, with top/bottom gradient masks.
   - Cross-fade between images when hovering across cards.
   - All 6 images preloaded on mount → instant first hover.
============================================================ */
const CREATIVE_BG_MEDIA_V5 = [
  // 01 Advanced UI/UX → designer's screen with UI mockups
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=2000&q=80",
  },
  // 02 Shopify Web Design → desktop ecommerce design workflow
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=2000&q=80",
  },
  // 03 Video Ads Creative → cinematic looping video (with poster fallback)
  {
    type: "video",
    src: "https://videos.pexels.com/video-files/854149/854149-hd_1920_1080_30fps.mp4",
    poster:
      "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=2000&q=80",
  },
  // 04 Social Ads Creative → phone showing social feed
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=2000&q=80",
  },
  // 05 Brand Identity → designer brand workspace
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=2000&q=80",
  },
  // 06 Motion Design → abstract motion / 3D render
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=2000&q=80",
  },
];

const CreativeCapabilitiesV5 = () => {
  const home = useContent("home");
  const cc = home.creative_capabilities || {};
  const eyebrow = cc.eyebrow || "Creative Capabilities";
  const description =
    cc.description ||
    "From Shopify design and advanced UX/UI to video production and social ad creatives — we craft digital experiences that are clean, cohesive, and impactful.";
  const headlineMain = cc.headline_main || "Creative thinking.";
  const headlineItalic = cc.headline_italic || "Proven";
  const headlineAccent = cc.headline_accent || "strategies";
  const ctaLabel = cc.cta_label || "Work with us";
  const ctaHref = cc.cta_href || "#contact";

  // Items: live items from CMS, fallback to original `creative` array.
  // Each item carries: title, body, icon (lucide name), media (image
  // OR video URL — auto-detected from extension).
  //
  // CRITICAL: when CMS data hasn't arrived yet (cold load before
  // /api/content/home resolves), we render NOTHING for the items
  // list rather than the hardcoded `creative` array. Why: the old
  // fallback would briefly show Unsplash placeholder URLs (Netflix
  // banner, social-feed phone shot, etc.) and the browser would
  // fetch+cache those URLs. When the CMS data arrived a moment
  // later, the user would have already perceived the wrong content
  // and the wrong URLs would sit in cache forever. With this gate,
  // the panel is empty for ~200ms on cold load and then populated
  // with the actual CMS content — no flash, no wrong cache, no
  // race condition with the reveal system.
  const hasCmsItems = Array.isArray(cc.items) && cc.items.length > 0;
  const liveItems = hasCmsItems
    ? cc.items.map((it) => ({
        title: it.title || "",
        body: it.body || "",
        icon: it.icon || "Code2",
        media: it.media || "",
      }))
    : !cc.__ready
      ? // CMS still loading — render skeleton items so the layout
        // dimensions stay stable, but with no media URLs (so nothing
        // wrong gets fetched).
        creative.map((cdef, i) => ({
          title: cdef.title,
          body: cdef.body,
          icon:
            ({ Palette: "Palette", Code2: "Code2", Video: "Video",
              MousePointerClick: "MousePointerClick", PenTool: "PenTool", Film: "Film" })[
              cdef.icon?.displayName || cdef.icon?.name || ""
            ] ||
            ["Palette", "Code2", "Video", "MousePointerClick", "PenTool", "Film"][i] ||
            "Code2",
          media: "",
        }))
      : // CMS has resolved but the admin hasn't configured items
        // here — keep the original hardcoded array as a permanent
        // fallback so the page never goes blank.
        creative.map((cdef, i) => ({
          title: cdef.title,
          body: cdef.body,
          icon:
            ({ Palette: "Palette", Code2: "Code2", Video: "Video",
              MousePointerClick: "MousePointerClick", PenTool: "PenTool", Film: "Film" })[
              cdef.icon?.displayName || cdef.icon?.name || ""
            ] ||
            ["Palette", "Code2", "Video", "MousePointerClick", "PenTool", "Film"][i] ||
            "Code2",
          media: CREATIVE_BG_MEDIA_V5[i]?.src || "",
        }));

  const isVideoUrl = (u) => /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(u || "");

  const [hovered, setHovered] = useState(null);
  const gridRef = useRef(null);

  // Safety net for the once-on-mount IntersectionObserver in HomeV5:
  // when CMS items change (re-fetch, reorder, add, remove) React swaps
  // child DOM nodes; the new nodes were never observed and stay at the
  // default `opacity: 0` of `[data-reveal="eyebrow"]`. Force them
  // revealed here — they're already inside the viewport by definition
  // when they re-mount mid-scroll.
  useEffect(() => {
    if (!gridRef.current) return;
    const els = gridRef.current.querySelectorAll('[data-reveal="eyebrow"]');
    els.forEach((el) => el.classList.add("is-revealed"));
  }, [liveItems]);

  // Preload all backgrounds once — first hover is then instant.
  useEffect(() => {
    liveItems.forEach((m) => {
      if (!m.media || isVideoUrl(m.media)) return;
      const img = new Image();
      img.src = mediaUrl(m.media);
    });
  }, [liveItems]);

  return (
    <section
      id="creative"
      data-testid="creative-capabilities-v5"
      className="relative overflow-hidden bg-[#11161D]"
    >
      {/* Full-width background media layer — cross-fades between capabilities. */}
      <div className="absolute inset-0 pointer-events-none">
        {liveItems.map((m, i) => {
          const url = mediaUrl(m.media);
          if (!url) return null;
          const isVid = isVideoUrl(m.media);
          return (
            <motion.div
              key={i}
              initial={false}
              animate={{ opacity: hovered === i ? 1 : 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              {isVid ? (
                <video
                  src={url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  style={{ filter: "brightness(0.55)" }}
                />
              ) : (
                <img
                  src={url}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ filter: "brightness(0.55)" }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-[#11161D] via-transparent to-[#11161D] pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#11161D]/40 via-transparent to-[#11161D]/40 pointer-events-none" />
            </motion.div>
          );
        })}
      </div>

      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-24 md:pb-32">
        {/* Header — same pattern as ServicesGridV5Vertical */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-14 md:mb-20">
          <PillarHeader
            eyebrow={eyebrow}
            description={description}
          >
            <h2 className={pillarHeadingClass}>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </h2>
          </PillarHeader>
          {ctaLabel ? (
            <a
              href={ctaHref}
              data-testid="creative-v5-all-link"
              className="hidden md:inline-flex items-center gap-2 text-sm font-bold tracking-wide text-[#F4D64E] link-underline whitespace-nowrap"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </a>
          ) : null}
        </div>

        {/* Capability grid — flexes to whatever item count CMS provides */}
        <div
          ref={gridRef}
          data-testid="creative-grid-v5"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {liveItems.map((c, i) => {
            const Icon = SERVICE_ICON_MAP[c.icon] || Code2;
            const isHovered = hovered === i;
            const isDimmed = hovered !== null && !isHovered;
            return (
              <div
                key={i}
                data-reveal="eyebrow"
                style={{ "--i": i }}
              >
                <article
                  data-testid={`creative-card-v5-${i + 1}`}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(i)}
                  onBlur={() => setHovered(null)}
                  tabIndex={0}
                  aria-label={c.title}
                  className="group relative rounded-2xl border backdrop-blur-sm p-7 md:p-8 cursor-pointer focus:outline-none focus-visible:border-[#F4D64E]/50 h-full"
                  style={{
                    opacity: isDimmed ? 0.15 : 1,
                    borderColor: isHovered
                      ? "rgba(244,214,78,0.35)"
                      : "rgba(255,255,255,0.06)",
                    backgroundColor: isHovered
                      ? "rgba(15,20,26,0.85)"
                      : "rgba(21,27,35,0.7)",
                    transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                    boxShadow: isHovered
                      ? "0 30px 60px -30px rgba(0,0,0,0.65), 0 0 0 1px rgba(244,214,78,0.10)"
                      : "none",
                    transition:
                      "opacity 300ms ease-in-out, border-color 300ms ease-in-out, transform 300ms ease-in-out, box-shadow 300ms ease-in-out, background-color 300ms ease-in-out",
                  }}
                >
                {/* Top row — number + arrow */}
                <div className="flex items-start justify-between mb-6">
                  <span className="tabular-nums text-[0.72rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/85">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight
                    aria-hidden
                    className="w-4 h-4"
                    style={{
                      color: isHovered ? "#F4D64E" : "rgba(115, 115, 115, 1)",
                      transform: isHovered
                        ? "translate(2px, -2px)"
                        : "translate(0, 0)",
                      transition:
                        "color 300ms ease-in-out, transform 300ms ease-in-out",
                    }}
                  />
                </div>
                {/* Icon chip */}
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F4D64E]/10 border border-[#F4D64E]/25 mb-5">
                  <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.75} />
                </div>
                {/* Title + body */}
                <h3 className="font-display text-[1.4rem] md:text-[1.55rem] font-black tracking-[-0.015em] text-white leading-[1.15] mb-3">
                  {c.title}
                </h3>
                <p className="text-[0.95rem] text-neutral-400 leading-[1.55]">
                  {c.body}
                </p>
              </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


/* ============================================================
   V5 only — Editorial Collage (replaces MediaMarqueeV5)
   ------------------------------------------------------------
   Sticky-left text + asymmetric flowing image grid on the right.
   Inspired by madeinevolve.com — magazine-spread feel.
   - 9 tiles total: 7 stock images + 1 looping video + 1 brand
     color swatch tile
   - Mixed aspect ratios (3:4, 1:1, 16:10, 4:3) and widths
   - Alternating left/right alignment within the right column
   - Each tile fades + slides in on scroll (whileInView)
============================================================ */

// Tiny count-up helper — animates from 0 → target when first in view.
// Supports a numeric value with optional prefix/suffix, OR a "24–48h"
// style range that counts both halves in parallel.
const CountUpStat = ({ value, duration = 1.4 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (!isInView) return;
    // Range pattern e.g. "24–48h" → animate 0→24 and 0→48 in parallel.
    const range = value.match(/^(\d+)[–-](\d+)([a-zA-Z+]*)$/);
    // Single number with optional + or unit suffix e.g. "250+", "10+", "30+"
    const single = value.match(/^(\d+)([+a-zA-Z]*)$/);

    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // Smooth ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      if (range) {
        const a = Math.round(eased * parseInt(range[1], 10));
        const b = Math.round(eased * parseInt(range[2], 10));
        setDisplay(`${a}–${b}${range[3]}`);
      } else if (single) {
        const n = Math.round(eased * parseInt(single[1], 10));
        setDisplay(`${n}${single[2]}`);
      } else {
        setDisplay(value);
      }
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isInView, value, duration]);

  return <span ref={ref}>{display}</span>;
};

// Design-locked layout rhythm for the 5 Editorial-Collage tiles.
// Only the MEDIA and the optional CAPTION TAG are admin-editable —
// aspect ratio, width and alignment stay fixed so the asymmetric
// composition holds no matter what content is dropped in.
const COLLAGE_TILE_LAYOUT_V5 = [
  { aspect: "1/1",   w: "85%", align: "end"   },
  { aspect: "16/10", w: "92%", align: "start" },
  { aspect: "4/3",   w: "70%", align: "end"   },
  { aspect: "4/3",   w: "60%", align: "start" },
  { aspect: "3/4",   w: "78%", align: "end"   },
];

// Lightweight URL → media-kind detector. Mirrors
// `/app/frontend/src/admin/MediaPicker.jsx :: kindFromUrl`. We keep a
// local copy to avoid the public bundle importing from /admin.
const mediaKindFromUrl = (url) => {
  if (!url) return "image";
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogv)$/.test(clean)) return "video";
  return "image";
};

const CollageTileV5 = ({ layout, media, tag, index }) => {
  // Three-state lifecycle: "loading" → "ready" (media fully loaded
  // and ready to paint) OR "failed" (load error, stall, or 12s
  // timeout). The visible tile is ONLY rendered when status==="ready",
  // so the dark placeholder figure is never shown alone.
  const [status, setStatus] = useState(media ? "loading" : "failed");
  const kind = mediaKindFromUrl(media);

  // Pre-flight check. Verify the media actually loads BEFORE we mount
  // the visible <figure>. Production sometimes serves images/videos
  // that stall mid-stream (CDN/edge hiccup) without firing `error` on
  // the eventual <img>/<video>, leaving the dark placeholder showing
  // forever. We sidestep that by checking out-of-DOM first.
  useEffect(() => {
    if (!media) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const fail = () => {
      if (!cancelled) setStatus("failed");
    };
    const succeed = () => {
      if (!cancelled) setStatus("ready");
    };

    let cleanup = () => {};

    if (kind === "image") {
      const img = new Image();
      img.onload = succeed;
      img.onerror = fail;
      img.src = media;
      cleanup = () => {
        img.onload = null;
        img.onerror = null;
        img.src = "";
      };
    } else {
      // Video — preload metadata + first frame. We listen on multiple
      // events because browsers differ (Safari fires `loadeddata`
      // earliest, Chrome `canplay`).
      const v = document.createElement("video");
      v.preload = "auto";
      v.muted = true;
      v.playsInline = true;
      const onReady = () => succeed();
      const onErr = () => fail();
      v.addEventListener("loadeddata", onReady, { once: true });
      v.addEventListener("canplay", onReady, { once: true });
      v.addEventListener("error", onErr, { once: true });
      v.src = media;
      try {
        v.load();
      } catch (e) {
        fail();
      }
      cleanup = () => {
        v.removeEventListener("loadeddata", onReady);
        v.removeEventListener("canplay", onReady);
        v.removeEventListener("error", onErr);
        v.removeAttribute("src");
        try {
          v.load();
        } catch (e) {
          /* noop */
        }
      };
    }

    // Hard timeout — if media doesn't load within 15s, treat as
    // failed and hide the tile entirely. Better a missing tile than
    // a broken placeholder. 15s gives slower networks (mobile/3G,
    // VPN routes) enough headroom while still keeping the section
    // from waiting forever on a dead asset.
    const timer = setTimeout(() => {
      if (!cancelled) setStatus((s) => (s === "ready" ? s : "failed"));
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      cleanup();
    };
  }, [media, kind]);

  if (status !== "ready") return null;

  return (
    <CollageTileV5Inner
      layout={layout}
      media={media}
      tag={tag}
      index={index}
      kind={kind}
    />
  );
};

const CollageTileV5Inner = ({ layout, media, tag, index, kind }) => {
  const wrapperRef = useRef(null);
  const aspectClass = {
    "3/4": "aspect-[3/4]",
    "1/1": "aspect-square",
    "16/10": "aspect-[16/10]",
    "4/3": "aspect-[4/3]",
  }[layout.aspect];

  // Self-reveal safety net. The page-level IntersectionObserver in
  // HomeV5 is set up once on mount; tiles that arrive AFTER fetch
  // resolves (CMS-driven) or after media preload sometimes miss its
  // first fire and stay clipped invisible (`clip-path: inset(0 0 100% 0)`).
  // We can't rely on the IO alone — observe ourselves, and as a final
  // fallback reveal after 350ms no matter what.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || el.classList.contains("is-revealed")) return;

    const reveal = () => el.classList.add("is-revealed");

    if (typeof IntersectionObserver !== "undefined") {
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry && entry.isIntersecting) {
            reveal();
            io.disconnect();
          }
        },
        { threshold: 0.05, rootMargin: "0px 0px -2% 0px" }
      );
      io.observe(el);
      const timer = setTimeout(() => {
        reveal();
        io.disconnect();
      }, 350);
      return () => {
        io.disconnect();
        clearTimeout(timer);
      };
    }
    reveal();
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-reveal="image"
      style={{
        "--i": index % 3,
        width: layout.w,
        alignSelf: layout.align === "end" ? "flex-end" : "flex-start",
      }}
    >
      <figure
        data-testid={`collage-tile-v5-${index + 1}`}
        className={`relative ${aspectClass} rounded-2xl overflow-hidden border border-white/[0.08] bg-[#151B23] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.85)]`}
      >
        {kind === "video" && (
          <video
            src={media}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        )}
        {kind === "image" && (
          <img
            src={media}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center hover:scale-[1.03] transition-transform duration-[800ms]"
          />
        )}
        {/* Soft bottom gradient + tag */}
        {tag ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
            <figcaption className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]" />
              <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-white/85">
                {tag}
              </span>
            </figcaption>
          </>
        ) : null}
      </figure>
    </div>
  );
};

const EditorialCollageV5 = () => {
  // Read CMS overrides. Any missing field falls back to the hard-coded
  // live snapshot so visuals never break when `editorial_collage` is
  // empty. Admin-managed via /admin/content/home → "Editorial collage".
  const home = useContent("home");
  const ec = (home && home.editorial_collage) || {};

  const eyebrow = ec.eyebrow || "In Motion";
  const headlineMain = ec.headline_main || "Stories shipped, pixels poured,";
  const headlineItalic = ec.headline_italic || "stores";
  const headlineAccent = ec.headline_accent || "scaled";
  const body =
    ec.body ||
    "Six years. 250+ Shopify storefronts. Editorial creative, performance marketing, and full-stack development — all under one yellow roof.";
  const ctaLabel = ec.cta_label || "View the archive";
  const ctaHref = ec.cta_href || "#work";

  const stats = [
    { value: ec.stat_1_value || "250+",   label: ec.stat_1_label || "Custom Features"  },
    { value: ec.stat_2_value || "10+",    label: ec.stat_2_label || "Years on Shopify" },
    { value: ec.stat_3_value || "24–48h", label: ec.stat_3_label || "Turnaround"       },
    { value: ec.stat_4_value || "30+",    label: ec.stat_4_label || "Trusted Stores"   },
  ];

  // If the admin has uploaded relative paths like "/uploads/foo.jpg",
  // resolve them against REACT_APP_BACKEND_URL so they hit FastAPI.
  const resolveMedia = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // Empty media slot = tile is fully hidden (no placeholder, no
  // fallback). The admin clearing the media field in CMS removes
  // that tile from the layout entirely.
  const tiles = COLLAGE_TILE_LAYOUT_V5
    .map((layout, i) => ({
      layout,
      media: resolveMedia(ec[`tile_${i + 1}_media`] || ""),
      tag: ec[`tile_${i + 1}_tag`] != null ? ec[`tile_${i + 1}_tag`] : "",
      origIndex: i,
    }))
    .filter((t) => Boolean(t.media));

  return (
    <section
      id="in-motion"
      data-testid="editorial-collage-v5"
      className="relative bg-[#0B0F14] py-24 md:py-32"
    >
      <div className="max-w-[90rem] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* STICKY LEFT — text block */}
        <aside className="lg:col-span-4 relative">
          <div className="lg:sticky lg:top-28 flex flex-col gap-7">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              className="font-display text-[2.25rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
            >
              <span>
                {headlineMain}
                {headlineItalic ? (
                  <>
                    {" "}
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
                  </>
                ) : null}
                {headlineAccent ? (
                  <>
                    {" "}
                    <PillarAccent>{headlineAccent}</PillarAccent>
                  </>
                ) : null}
                .
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="text-[1rem] md:text-[1.08rem] text-neutral-400 leading-[1.65] max-w-[42ch]"
            >
              <span>{body}</span>
            </p>
            {ctaLabel ? (
              <div className="flex items-center gap-6 pt-2">
                <a
                  href={ctaHref || "#"}
                  data-testid="collage-archive-cta"
                  className="group inline-flex items-center gap-2 text-[0.82rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] link-underline"
                >
                  {ctaLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            ) : null}
            {/* Trust metrics — count up when in view */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-8 border-t border-white/[0.06] mt-4">
              {stats.map((m, i) => (
                <div
                  key={`${m.label}-${i}`}
                  data-reveal="eyebrow"
                  style={{ "--i": i }}
                >
                  <div
                    className={`font-display text-[1.5rem] md:text-[1.65rem] font-black tabular-nums leading-none ${
                      i === 0 || i === 2 ? "text-[#F4D64E]" : "text-white"
                    }`}
                  >
                    <CountUpStat value={m.value} />
                  </div>
                  <div className="mt-1.5 text-[0.62rem] font-bold tracking-[0.18em] uppercase text-neutral-500">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* FLOWING RIGHT — asymmetric tile column. Empty tiles are
            filtered out above, so we only render the ones that have
            real media. `origIndex` keeps the layout style stable per
            slot (tile 2 keeps its specific positioning even if tile 1
            was hidden). */}
        <div className="lg:col-span-8 flex flex-col gap-10 md:gap-14">
          {tiles.map((t) => (
            <CollageTileV5
              key={t.origIndex}
              layout={t.layout}
              media={t.media}
              tag={t.tag}
              index={t.origIndex}
            />
          ))}
        </div>
      </div>
    </section>
  );
};



/* ============================================================
   V5 only — Marketing 360° (typography + decorative shape)
   ------------------------------------------------------------
   Inspired by digitalpresent.io: oversized service titles with
   a slowly rotating yellow shape behind, one shape per service.
   Pinned vertical-to-horizontal scroll. Original copy untouched.
============================================================ */

// Six SVG decorative shapes — one per Marketing 360° service.
// Each is a centered viewBox 0 0 600 600. They render in #F4D64E at
// low opacity behind the title.
const MarketingShape = ({ index }) => {
  const C = "#F4D64E";
  const props = {
    viewBox: "0 0 600 600",
    className: "w-full h-full",
    fill: "none",
    stroke: C,
    strokeWidth: 1.5,
    strokeLinejoin: "round",
  };
  switch (index) {
    case 0:
      // Sparkle / 4-point star (matches reference exactly)
      return (
        <svg {...props}>
          <path
            d="M 300 60 C 320 220 380 280 540 300 C 380 320 320 380 300 540 C 280 380 220 320 60 300 C 220 280 280 220 300 60 Z"
            fill={C}
            fillOpacity="0.8"
            stroke="none"
          />
        </svg>
      );
    case 1:
      // Soft blob (organic rounded form)
      return (
        <svg {...props}>
          <path
            d="M 300 80 C 420 80 510 160 520 280 C 530 400 460 500 340 520 C 220 540 110 460 90 340 C 70 220 180 80 300 80 Z"
            fill={C}
            fillOpacity="0.8"
            stroke="none"
          />
        </svg>
      );
    case 2:
      // 6-petal flower
      return (
        <svg {...props}>
          <g fill={C} fillOpacity="0.8" stroke="none">
            {[0, 60, 120, 180, 240, 300].map((deg) => (
              <ellipse
                key={deg}
                cx="300"
                cy="170"
                rx="80"
                ry="180"
                transform={`rotate(${deg} 300 300)`}
              />
            ))}
            <circle cx="300" cy="300" r="60" fill="#0B0F14" />
          </g>
        </svg>
      );
    case 3:
      // Hexagon — clean, geometric, balanced
      return (
        <svg {...props}>
          <polygon
            points="300,80 480,200 480,400 300,520 120,400 120,200"
            fill={C}
            fillOpacity="0.8"
            stroke="none"
          />
        </svg>
      );
    case 4:
      // Concentric orbital rings
      return (
        <svg {...props}>
          <g stroke={C} strokeOpacity="0.85" fill="none">
            <circle cx="300" cy="300" r="250" strokeWidth="1.5" />
            <circle cx="300" cy="300" r="200" strokeWidth="1.5" />
            <circle cx="300" cy="300" r="150" strokeWidth="1.5" />
            <circle cx="300" cy="300" r="100" strokeWidth="1.5" />
            <circle cx="300" cy="300" r="50" strokeWidth="1.5" />
          </g>
          <circle cx="300" cy="50" r="10" fill={C} />
          <circle cx="510" cy="200" r="8" fill={C} />
          <circle cx="120" cy="380" r="6" fill={C} />
          <circle cx="450" cy="480" r="7" fill={C} />
        </svg>
      );
    case 5:
      // 12-ray asterisk burst
      return (
        <svg {...props}>
          <g stroke={C} strokeWidth="14" strokeLinecap="round">
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * Math.PI) / 6;
              const x1 = 300 + Math.cos(angle) * 80;
              const y1 = 300 + Math.sin(angle) * 80;
              const x2 = 300 + Math.cos(angle) * 240;
              const y2 = 300 + Math.sin(angle) * 240;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
            })}
          </g>
          <circle cx="300" cy="300" r="40" fill={C} />
        </svg>
      );
    default:
      return null;
  }
};

const MarketingPanelV5 = ({ m }) => (
  <article className="relative shrink-0 w-screen h-screen flex items-center justify-center px-10 md:px-20">
    <h2
      className="font-display font-black text-white text-center tracking-[-0.04em] leading-[0.9]"
      style={{
        fontSize: "clamp(2.75rem, 8vw, 8rem)",
      }}
    >
      {m.title}
    </h2>
  </article>
);

const MarketingPinnedV5 = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  // CMS overrides — admin-managed via /admin/content/home →
  // "Marketing 360 pinned". Falls back to the live `marketing` array.
  const home = useContent("home");
  const mp = (home && home.marketing_pinned) || {};
  const eyebrow = mp.eyebrow || "Marketing 360°";
  const headlineMain = mp.headline_main || "A single partner for every";
  const headlineAccent = mp.headline_accent || "digital task";
  const ctaPrimaryLabel = mp.cta_primary_label || "Talk to us";
  const ctaPrimaryHref = mp.cta_primary_href || "#contact";
  const ctaSecondaryLabel = mp.cta_secondary_label || "See all Marketing Services";
  const ctaSecondaryHref = mp.cta_secondary_href || "#services";
  const panels = Array.isArray(mp.panels) && mp.panels.length ? mp.panels : marketing;
  const total = panels.length;

  // Compute total horizontal travel (panels are full-viewport-width)
  useEffect(() => {
    const calc = () => {
      if (!trackRef.current) return;
      const trackWidth = trackRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;
      setMaxX(Math.max(0, trackWidth - viewportWidth));
    };
    calc();
    window.addEventListener("resize", calc);
    const t = setTimeout(calc, 300);
    return () => {
      window.removeEventListener("resize", calc);
      clearTimeout(t);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, -maxX]);
  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  // Track which panel is currently centered (active) so its shape is visible
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      // panels = total + 1 (header panel). Map progress → index.
      const idx = Math.min(total - 1, Math.max(0, Math.round(v * (total - 1))));
      setActiveIndex(idx);
    });
    return () => unsub();
  }, [scrollYProgress, total]);

  return (
    <section
      id="marketing-pinned"
      data-testid="marketing-pinned-v5"
      className="relative bg-[#0B0F14]"
    >
      {/* DESKTOP — pinned horizontal scroll */}
      <div ref={ref} className="hidden md:block relative" style={{ height: "560vh" }}>
        <div className="sticky top-0 h-screen overflow-hidden">
          {/* PINNED TOP — progress bar + dynamic counter row.
              pt-28 keeps the bar clear of the sticky site Nav. */}
          <div className="absolute top-0 left-0 right-0 z-20 px-10 md:px-20 pt-28">
            <div className="flex items-center gap-4">
              <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                Scroll
              </span>
              <div className="relative flex-1 h-[2px] bg-white/[0.08] rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-[#F4D64E] rounded-full"
                  style={{ width: barWidth }}
                />
              </div>
            </div>
            {/* Tags row — left static, right updates with active panel */}
            <div className="mt-5 flex items-center justify-between text-[0.72rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#F4D64E]" />
                <span>{eyebrow}</span>
              </div>
              <AnimatePresence mode="wait">
                <motion.span
                  key={`no-${activeIndex}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="tabular-nums"
                >
                  NO. {String(activeIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>

          {/* CENTERED STICKY SHAPE — single layer behind everything.
              Cross-fades between the 6 shapes as activeIndex changes,
              and slowly rotates the active one. Shapes stay indexed by
              POSITION (0–5), so reordering the panels also reorders
              their decorative shapes — no admin field needed. */}
          <div
            aria-hidden
            className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none"
          >
            <div
              className="relative w-[45vw] max-w-[540px] aspect-square"
              style={{ animation: "rotate-slow 40s linear infinite" }}
            >
              {panels.slice(0, 6).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0"
                  initial={false}
                  animate={{
                    opacity: activeIndex === i ? 0.18 : 0,
                    scale: activeIndex === i ? 1 : 0.45,
                    rotate: activeIndex === i ? 0 : i < activeIndex ? -45 : 45,
                  }}
                  transition={{
                    duration: 0.65,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <MarketingShape index={i} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* HORIZONTAL TRACK — only the giant titles slide */}
          <motion.div
            ref={trackRef}
            style={{ x }}
            className="relative z-10 flex h-full will-change-transform"
          >
            {panels.map((m, i) => (
              <MarketingPanelV5 key={`${m.title}-${i}`} m={m} />
            ))}
          </motion.div>

          {/* PINNED BOTTOM — body fade per slide + CTA buttons (static).
              Sits ABOVE the sliding title via z-20 + a soft gradient mask
              fading the title behind so the body text always reads cleanly. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-[36%] z-15 pointer-events-none bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/85 to-transparent"
          />
          <div className="absolute inset-x-0 bottom-0 z-20 pb-12 md:pb-16 px-10 md:px-20 flex flex-col items-center gap-8">
            <div className="min-h-[5.5rem] flex items-center justify-center w-full">
              <AnimatePresence mode="wait">
                <motion.p
                  key={`body-${activeIndex}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="text-[1.05rem] md:text-[1.2rem] text-neutral-300 leading-[1.6] max-w-[60ch] text-center"
                >
                  {panels[activeIndex] && panels[activeIndex].body}
                </motion.p>
              </AnimatePresence>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {ctaPrimaryLabel ? (
                <a
                  href={ctaPrimaryHref || "#"}
                  data-testid="marketing-pinned-v5-talk-cta"
                  className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-6 py-4 rounded-md transition-colors"
                >
                  {ctaPrimaryLabel}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : null}
              {ctaSecondaryLabel ? (
                <a
                  href={ctaSecondaryHref || "#"}
                  data-testid="marketing-pinned-v5-all-services-cta"
                  className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
                >
                  {ctaSecondaryLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE — vertical stack of cards (graceful fallback) */}
      <div className="md:hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-4">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display text-[2.25rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
            {headlineMain}
            {headlineAccent ? (
              <>
                {" "}
                <PillarAccent>{headlineAccent}</PillarAccent>
              </>
            ) : null}
            .
          </h2>
        </div>
        <div className="px-6 pb-16 flex flex-col gap-4">
          {panels.map((m, i) => (
            <article
              key={`${m.title}-m-${i}`}
              className="bg-[#151B23] border border-white/[0.06] rounded-xl p-7 flex flex-col gap-4"
            >
              <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]/85">
                {String(i + 1).padStart(2, "0")} · {eyebrow}
              </span>
              <h3 className="font-display text-[1.65rem] font-black text-white leading-[1.1] tracking-[-0.015em]">
                {m.title}
              </h3>
              <p className="text-[0.95rem] text-neutral-400 leading-relaxed">
                {m.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};



/* ============================================================
   V5 only — Featured Clients (dual-column scroll-storytelling)
   ------------------------------------------------------------
   - Single full-bleed blurred bg per project, cross-fades when
     scroll progresses to the next project.
   - LEFT column: 2 stacked images per project (vertically scroll
     within the section as the user reads).
   - RIGHT column: sticky. From top → big number, project name,
     tagline, stats, services, CTA. All cross-fades as activeIndex
     changes.
============================================================ */

// Each project gets a 2-image gallery for the scrolling left column.
// Falls back to the project's primary img twice if no second is set.
const CASE_GALLERY_V5 = {
  "Veronique Gabai": "https://images.unsplash.com/photo-1611605698335-8b1569810432?auto=format&fit=crop&w=1200&q=80",
  "SWAND": "https://images.unsplash.com/photo-1572044162444-ad60f128bdea?auto=format&fit=crop&w=1200&q=80",
  "Cafe Joyeux": "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1200&q=80",
};

const CaseStudiesV5 = () => {
  const home = useContent("home");
  const cs = home.case_studies || {};
  const eyebrow = cs.eyebrow || "Featured Clients";
  const headline = cs.headline || "Stores we've shaped, scaled, and shipped for.";
  const description =
    cs.description ||
    "A small sample of long-term partnerships — where every pixel, pipeline, and pageview has our fingerprints.";
  const ctaLabel = cs.cta_label || "View project";
  const ctaHref = cs.cta_href || "#contact";
  const partnerSinceLabel = cs.partner_since_label || "Partner Since";
  const completedLabel = cs.completed_label || "Completed";

  const liveStudies = Array.isArray(cs.items) && cs.items.length
    ? cs.items.map((it) => ({
        name: it.title || "",
        tagline: it.body || "",
        tasks: it.tasks || "",
        since: it.since || "",
        services: Array.isArray(it.highlights) ? it.highlights : [],
        img: it.media || "",
        secondary: it.secondary_media || "",
      }))
    : caseStudiesV2.map((c) => ({
        name: c.name,
        tagline: c.tagline,
        tasks: c.tasks,
        since: c.since,
        services: c.services,
        img: c.img,
        secondary: CASE_GALLERY_V5[c.name] || c.img,
      }));

  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const total = liveStudies.length;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Map scroll-progress → active project index
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      const idx = Math.min(total - 1, Math.floor(v * total + 0.0001));
      setActiveIndex(idx);
    });
    return () => unsub();
  }, [scrollYProgress, total]);

  if (!total) return null;

  const active = liveStudies[Math.min(activeIndex, total - 1)];

  return (
    <section
      id="work"
      data-testid="work-section-v5"
      ref={sectionRef}
      className="relative bg-[#0B0F14]"
    >
      {/* Section header */}
      <div className="relative z-50 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
        <SectionHeader
          eyebrow={eyebrow}
          title={headline}
          description={description}
        />
      </div>

      {/* DESKTOP — dual-column with sticky bg + sticky right */}
      <div className="hidden md:block relative pb-20 lg:pb-32">
        {/* STICKY BG LAYER — single bg image per project, cross-fades */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="sticky top-0 h-screen overflow-hidden">
            {liveStudies.map((c, i) => (
              <motion.div
                key={`bg-${c.name}-${i}`}
                initial={false}
                animate={{ opacity: activeIndex === i ? 1 : 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <img
                  src={mediaUrl(c.img)}
                  alt=""
                  aria-hidden
                  className="w-full h-full object-cover"
                  style={{
                    filter: "blur(18px) brightness(0.32)",
                    transform: "scale(1.1)",
                  }}
                />
              </motion.div>
            ))}
            <div className="absolute inset-0 bg-[#0B0F14]/30 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/40 via-transparent to-[#0B0F14]/40 pointer-events-none" />
          </div>
        </div>

        {/* MAIN GRID — left scrolls, right sticks */}
        <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — 2 images per project, stacked, scroll naturally */}
          <div className="col-span-7 flex flex-col">
            {liveStudies.map((c, i) => {
              const second = c.secondary || c.img;
              return (
                <div
                  key={`gallery-${c.name}-${i}`}
                  data-testid={`case-gallery-v5-${i + 1}`}
                  className="min-h-[200vh] flex flex-col gap-10 py-20"
                >
                  {/* Image 1 — primary project shot.
                      `once: true` is critical here: with `once: false`,
                      slow scrolling past the image causes framer to
                      reverse the animation as the image exits the
                      viewport, producing a visible blink/jump-down. */}
                  <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_60px_120px_-30px_rgba(0,0,0,0.85)]"
                  >
                    <img
                      src={mediaUrl(c.img)}
                      alt={c.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/40 via-transparent to-transparent pointer-events-none" />
                  </motion.div>

                  {/* Image 2 — secondary detail / lifestyle. Same
                      `once: true` fix as above. */}
                  <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{
                      duration: 0.7,
                      ease: [0.22, 1, 0.36, 1],
                      delay: 0.1,
                    }}
                    className="relative aspect-[5/4] rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_60px_120px_-30px_rgba(0,0,0,0.85)] self-end w-[88%]"
                  >
                    <img
                      src={mediaUrl(second)}
                      alt={`${c.name} detail`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F14]/30 via-transparent to-transparent pointer-events-none" />
                  </motion.div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — sticky content (number TOP, then name/tagline, then stats/services/CTA) */}
          <div className="col-span-5 relative">
            <div className="sticky top-24 h-[calc(100vh-6rem)] flex flex-col justify-center py-10">
              {/* TOP — gigantic number, anchored top-right */}
              <div className="flex justify-end mb-6">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={`num-${activeIndex}`}
                    initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="font-display font-black text-white/90 tabular-nums leading-[0.78]"
                    style={{
                      // Scale by both width AND height: the number stays
                      // huge on hi-res displays (≥1920×1080 unchanged at
                      // 16rem) but shrinks on shorter laptops so the
                      // sticky right column's content (name, stats,
                      // pills, CTA) never gets pushed off-screen.
                      fontSize: "clamp(7rem, min(18vw, 24vh), 16rem)",
                      letterSpacing: "-0.07em",
                    }}
                  >
                    {String(activeIndex + 1).padStart(2, "0")}
                  </motion.span>
                </AnimatePresence>
              </div>

              {/* TINY index marker */}
              <div className="flex items-center justify-end gap-3 mb-8 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
                <span>Client Work</span>
                <span className="text-neutral-600">·</span>
                <span className="tabular-nums">
                  {String(activeIndex + 1).padStart(2, "0")} /{" "}
                  {String(total).padStart(2, "0")}
                </span>
                <span className="h-px w-8 bg-[#F4D64E]" />
              </div>

              {/* MIDDLE — name + tagline + stats + services + CTA all cross-fade together */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${activeIndex}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{
                    duration: 0.55,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.05,
                  }}
                  className="flex flex-col gap-7 items-end text-right"
                >
                  <h3
                    className="font-display font-black text-white tracking-[-0.025em] leading-[1.0]"
                    style={{ fontSize: "clamp(2.25rem, 3.5vw, 3.25rem)" }}
                  >
                    {active.name}
                  </h3>

                  <p className="text-[1rem] md:text-[1.08rem] text-neutral-300 leading-[1.6] max-w-[40ch]">
                    {active.tagline}
                  </p>

                  <div className="flex items-center gap-6 pt-1">
                    <div className="text-right">
                      <div className="font-display text-[1.6rem] font-black text-[#F4D64E] tabular-nums leading-none">
                        {active.tasks}
                      </div>
                      <div className="mt-1.5 text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-400">
                        {completedLabel}
                      </div>
                    </div>
                    <div className="h-9 w-px bg-white/[0.12]" />
                    <div className="text-right">
                      <div className="font-display text-[1.6rem] font-black text-white tabular-nums leading-none">
                        {active.since}
                      </div>
                      <div className="mt-1.5 text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-400">
                        {partnerSinceLabel}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end max-w-[34rem]">
                    {(active.services || []).map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.1] text-[0.78rem] text-neutral-300 backdrop-blur-sm"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  <a
                    href={ctaHref}
                    data-testid={`case-study-v5-cta-${activeIndex + 1}`}
                    className="group inline-flex items-center gap-3 rounded-full border border-white/25 hover:border-[#F4D64E] hover:bg-[#F4D64E] hover:text-neutral-950 px-7 py-3.5 text-[0.82rem] font-bold tracking-[0.16em] uppercase text-white transition-colors"
                  >
                    {ctaLabel}
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE — vertical card fallback */}
      <div className="md:hidden flex flex-col gap-6 px-6 pb-16">
        {liveStudies.map((c, i) => (
          <article
            key={`m-${c.name}-${i}`}
            className="rounded-2xl overflow-hidden border border-white/[0.08] bg-[#151B23]"
          >
            <div className="relative aspect-[5/4]">
              <img
                src={mediaUrl(c.img)}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-transparent to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
                <span className="tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px w-6 bg-[#F4D64E]/50" />
                <span>Featured</span>
              </div>
            </div>
            <div className="p-7 flex flex-col gap-4">
              <h3 className="font-display text-[1.85rem] font-black text-white leading-[1.05] tracking-[-0.02em]">
                {c.name}
              </h3>
              <p className="text-[0.95rem] text-neutral-400 leading-relaxed">
                {c.tagline}
              </p>
              <div className="flex items-center gap-5 pt-2">
                <div>
                  <div className="font-display text-[1.4rem] font-black text-[#F4D64E] tabular-nums leading-none">
                    {c.tasks}
                  </div>
                  <div className="mt-1 text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-500">
                    {completedLabel}
                  </div>
                </div>
                <div className="h-8 w-px bg-white/[0.08]" />
                <div>
                  <div className="font-display text-[1.4rem] font-black text-white tabular-nums leading-none">
                    {c.since}
                  </div>
                  <div className="mt-1 text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-500">
                    {partnerSinceLabel}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(c.services || []).map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-[0.75rem] text-neutral-300"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <a
                href={ctaHref}
                className="inline-flex items-center gap-2 self-start mt-2 text-[#F4D64E] text-sm font-bold tracking-wide"
              >
                {ctaLabel} →
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};


/* ============================================================
   V5 only — Closing block (replaces FinalCTA on V5)
   ------------------------------------------------------------
   - Tiny status tag at top
   - Massive outline marquee — phrases scrolling slowly leftward
   - Giant clickable email below
   - Small geo / hours / handle strip at the bottom
============================================================ */
const ClosingV5 = () => {
  const home = useContent("home");
  const cc = home.closing_cta || {};
  const statusLabel = cc.status_label || "Still taking new projects";
  const counterText = cc.counter_text || "03 · 26";
  const line1 = cc.statement_line_1 || "Great brands";
  const line2 = cc.statement_line_2 || "aren't built";
  const line3Main = cc.statement_line_3_main || "overnight";
  const line3Period =
    cc.statement_line_3_period == null ? "." : cc.statement_line_3_period;
  const sigBrand = cc.signature_brand || "Sunday Digital";
  const sigEst = cc.signature_est || "Est. 2018";
  const sigTag = cc.signature_tag || "A 360° digital studio";
  const emailEyebrow = cc.email_eyebrow || "Tell us about your project";
  const email = cc.email || "hello@sundaydigital.io";
  const mobileCtaLabel = cc.mobile_cta_label || "Write us";
  const location = cc.location || "Brooklyn, NY";
  const hours = cc.hours || "Open Mon–Fri";
  const timezone = cc.timezone || "EST";
  const handleLabel = cc.handle_label || "@sundaydigital";
  const handleHref = cc.handle_href || "https://instagram.com/sundaydigital";

  // Tagline marquee — solid/outline alternation. CMS field "solid"
  // accepts "solid" / "outline" — fall back to alternating index.
  const fallbackWords = [
    { title: "Strategy", solid: "solid" },
    { title: "Design",   solid: "outline" },
    { title: "Code",     solid: "solid" },
    { title: "Growth",   solid: "outline" },
    { title: "Brand",    solid: "solid" },
    { title: "Commerce", solid: "outline" },
  ];
  const wordsCfg = Array.isArray(cc.tagline_words) && cc.tagline_words.length
    ? cc.tagline_words
    : fallbackWords;
  const taglineWords = wordsCfg.map((w, i) => {
    const v = (w.solid || "").toString().toLowerCase();
    const solid =
      v === "solid"   ? true
      : v === "outline" ? false
      : i % 2 === 0;
    return { t: w.title || "", solid };
  }).filter((w) => w.t !== "");
  const taglineLoop = [...taglineWords, ...taglineWords];

  return (
    <section
      id="closing"
      data-testid="closing-v5"
      className="relative bg-[#0B0F14] overflow-hidden border-t border-white/[0.05]"
    >
      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10 md:pb-14">
        {/* Status tag — top */}
        <div className="flex items-center gap-3 mb-14 md:mb-20">
          <span
            aria-hidden
            className="w-2 h-2 rounded-full bg-[#F4D64E] animate-pulse"
          />
          <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
            {statusLabel}
          </span>
          <span className="h-px flex-1 bg-white/[0.08] max-w-[120px]" />
          <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 tabular-nums">
            {counterText}
          </span>
        </div>

        {/* EDITORIAL STATEMENT — three stacked lines, mixed weights & styles */}
        <motion.div
          data-testid="closing-statement"
          className="relative font-display font-black tracking-[-0.04em] uppercase leading-[0.92] select-none"
          style={{ fontSize: "clamp(3.5rem, 11vw, 11rem)" }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.18, delayChildren: 0.05 } },
          }}
        >
          {[
            {
              key: "l1",
              className: "block text-white",
              style: {},
              content: line1,
            },
            {
              key: "l2",
              className:
                "block font-serif italic font-medium normal-case text-neutral-400 tracking-[-0.02em] pl-[0.08em]",
              style: { fontSize: "clamp(2.75rem, 9vw, 9rem)", lineHeight: "1.05" },
              content: line2,
            },
            {
              key: "l3",
              className: "block text-[#F4D64E]",
              style: {},
              content: (
                <>
                  {line3Main}
                  <span className="text-white">{line3Period}</span>
                </>
              ),
            },
          ].map((line) => (
            <div key={line.key} className="overflow-hidden pb-[0.05em]">
              <motion.div
                className={line.className}
                style={line.style}
                variants={{
                  hidden: { y: "110%" },
                  show: {
                    y: "0%",
                    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
              >
                {line.content}
              </motion.div>
            </div>
          ))}
        </motion.div>

        {/* Signature row — sits under the statement */}
        <div className="mt-10 md:mt-14 flex items-center gap-5 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
          <span className="h-px w-10 md:w-16 bg-neutral-700" />
          <span>{sigBrand}</span>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span>{sigEst}</span>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span className="text-neutral-400">{sigTag}</span>
        </div>
      </div>

      {/* SLOW TAGLINE MARQUEE — mixed solid + outline, ~3x slower than before */}
      <div className="relative py-8 md:py-12 border-y border-white/[0.05] bg-white/[0.015]">
        <div
          className="flex items-center will-change-transform animate-[marquee-left_140s_linear_infinite]"
          style={{ width: "max-content" }}
          data-testid="closing-marquee"
        >
          {taglineLoop.map((w, i) => (
            <span
              key={i}
              className="flex items-center whitespace-nowrap leading-none"
            >
              <span
                className={`font-display font-black tracking-[-0.03em] uppercase ${
                  w.solid ? "text-white" : ""
                }`}
                style={{
                  fontSize: "clamp(2.25rem, 6vw, 6rem)",
                  ...(w.solid
                    ? {}
                    : {
                        WebkitTextStroke: "1px rgba(255,255,255,0.55)",
                        color: "transparent",
                      }),
                }}
              >
                {w.t}
              </span>
              <span
                aria-hidden
                className="mx-8 md:mx-14 inline-block w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#F4D64E]"
              />
            </span>
          ))}
        </div>
        {/* Edge-fade masks → text materialises out of darkness */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 md:w-60 bg-gradient-to-r from-[#0B0F14] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 md:w-60 bg-gradient-to-l from-[#0B0F14] to-transparent" />
      </div>

      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
        {/* Giant clickable email */}
        <div className="flex flex-col items-center text-center gap-7 md:gap-10">
          <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            {emailEyebrow}
          </span>

          {/* MOBILE — pill CTA (fits cleanly, no wrap) */}
          <a
            href={`mailto:${email}`}
            data-testid="closing-email-mobile"
            className="md:hidden group inline-flex items-center gap-3 text-[0.82rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
          >
            {mobileCtaLabel}
            <ArrowUpRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2}
            />
          </a>
          <span className="md:hidden text-[0.82rem] text-neutral-400 tracking-tight break-all">
            {email}
          </span>

          {/* DESKTOP — giant editorial email */}
          <a
            href={`mailto:${email}`}
            data-testid="closing-email"
            className="hidden md:inline-block group relative font-display font-black text-[#F4D64E] tracking-[-0.035em] leading-[0.95] transition-transform hover:scale-[1.005]"
            style={{ fontSize: "clamp(3rem, 7vw, 7rem)" }}
          >
            <span className="relative inline-block">
              {email}
              <span
                aria-hidden
                className="absolute left-0 right-0 -bottom-2 h-[5px] bg-[#F4D64E]/30 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
              />
            </span>
            <ArrowUpRight
              aria-hidden
              className="inline-block ml-5 w-[0.7em] h-[0.7em] -translate-y-[0.15em] transition-transform group-hover:translate-x-2 group-hover:-translate-y-3"
              strokeWidth={1.4}
            />
          </a>

          {/* Small contact strip */}
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
            <span>{location}</span>
            <span className="w-1 h-1 rounded-full bg-neutral-700" />
            <span>{hours}</span>
            <span className="w-1 h-1 rounded-full bg-neutral-700" />
            <span>{timezone}</span>
            <span className="w-1 h-1 rounded-full bg-neutral-700" />
            <a
              href={handleHref}
              data-testid="closing-handle"
              className="text-neutral-300 hover:text-[#F4D64E] transition-colors"
            >
              {handleLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};




/* ============================================================
   V5 only — Editorial Testimonials (Option B, refined)
   ------------------------------------------------------------
   - Auto-rotates every 5s.
   - Hover-pause is scoped to the QUOTE block only (not the
     whole section), so hovering the tabs/page background does
     not pause the cycle.
   - Bottom navigation = width-progress bars: 4 horizontal
     bars, the active one fills yellow over 5s; click to jump.
   - Author block inside the review shows Name + Brand only
     (role is dropped — brand is the real credibility signal).
============================================================ */
const TESTIMONIALS_ROTATE_MS = 5000;

const TestimonialsV5 = ({ contentKey = "home", slug = null } = {}) => {
  const cms = useContent(contentKey);
  const tc = cms?.testimonials || {};
  const eyebrow = tc.eyebrow || "In Their Own Words";
  // Items list comes from the requested CMS key. When the key has no
  // testimonials configured, fall back to the home-page hardcoded list
  // ONLY for the home key — every other key (about, case_study, …)
  // simply renders nothing so the section auto-hides until the admin
  // adds reviews for that page.
  let rawItems = Array.isArray(tc.items) && tc.items.length
    ? tc.items
    : contentKey === "home"
      ? null   // home falls back to hardcoded list (see below)
      : [];
  // Case-study pages: filter items to the ones assigned to THIS slug,
  // and cap at one review per page (first match wins). Items with no
  // assigned slug are treated as "unassigned" and skipped.
  if (contentKey === "case_study" && slug && Array.isArray(rawItems)) {
    const match = rawItems.find((it) => (it && it.slug) === slug);
    rawItems = match ? [match] : [];
  }
  const liveTestimonials = rawItems
    ? rawItems.map((it) => ({
        quote: it.title || "",
        name: it.body || "",
        role: it.role || "",
      }))
    : testimonials;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = liveTestimonials.length;
  const ROTATE_MS = TESTIMONIALS_ROTATE_MS;

  // Reset active if items list shrunk
  useEffect(() => {
    if (active >= total) setActive(0);
  }, [active, total]);

  // Auto-rotate every 5s when not paused
  useEffect(() => {
    if (paused || total < 2) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % total);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, total]);

  if (!total) return null;

  const t = liveTestimonials[Math.min(active, total - 1)];
  // role = "Owner · sclarandis.com" → grab the BRAND only (right side of " · ")
  const brand = (t.role || "").split(" · ")[1] || t.role || "";
  const initial = (t.name || "").trim().charAt(0).toUpperCase() || "·";

  return (
    <TestimonialsV5Render
      contentKey={contentKey}
      eyebrow={eyebrow}
      items={liveTestimonials}
      t={t}
      active={active}
      setActive={setActive}
      total={total}
      brand={brand}
      initial={initial}
      paused={paused}
      setPaused={setPaused}
    />
  );
};

// Render layer — wrapped in its own component so we can attach a local
// IntersectionObserver to the section ref. This is required when the
// component is used on pages whose page-level reveal observer (a) only
// scans once at mount (e.g. /about), or (b) doesn't exist at all (e.g.
// /showcase/:slug). Without this, `[data-reveal]` children (the eyebrow,
// the right-side "01 / N" counter, the mask-block heading) stay clipped
// invisible because they only get the `.is-revealed` class via that
// page-level observer.
const TestimonialsV5Render = ({
  contentKey, eyebrow, items, t, active, setActive, total, brand, initial, paused, setPaused,
}) => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;
    const reveal = (el) => el && el.classList.add("is-revealed");
    if (typeof IntersectionObserver === "undefined") {
      root.querySelectorAll("[data-reveal]").forEach(reveal);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    const observe = (el) => {
      if (!el || el.classList.contains("is-revealed")) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh && rect.bottom > 0) {
        reveal(el);
        return;
      }
      io.observe(el);
    };
    root.querySelectorAll("[data-reveal]").forEach(observe);
    // Re-scan after one paint in case quote-swap re-mounts children.
    const rescan = setTimeout(
      () => root.querySelectorAll("[data-reveal]").forEach(observe),
      200
    );
    return () => {
      clearTimeout(rescan);
      io.disconnect();
    };
  }, [active, total]);

  return (
    <section
      ref={sectionRef}
      id={contentKey === "home" ? "testimonials" : `testimonials-${contentKey}`}
      data-testid={contentKey === "home" ? "testimonials-section" : `testimonials-section-${contentKey}`}
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <SectionAmbient variant="d" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Eyebrow + counter */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <div
            data-reveal="eyebrow"
            style={{ "--i": 1 }}
            className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500"
          >
            <span className="tabular-nums">
              {String(active + 1).padStart(2, "0")}
            </span>
            <span className="h-px w-8 bg-neutral-700" />
            <span className="tabular-nums">
              {String(total).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* FEATURED QUOTE BLOCK — pause-on-hover scoped here only */}
        <div
          data-testid="testimonials-v5-quote"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
        >
          {/* Drawing yellow accent bar */}
          <div className="hidden lg:flex lg:col-span-1 justify-center pt-3">
            <motion.span
              key={`bar-${active}`}
              aria-hidden
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              style={{ transformOrigin: "top center" }}
              className="block w-[2px] h-32 bg-[#F4D64E]"
            />
          </div>

          <div className="lg:col-span-11">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={`quote-${active}`}
                data-testid={`testimonial-v5-active-${active + 1}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold tracking-[-0.025em] leading-[1.15] text-white max-w-[26ch]"
              >
                <span className="text-[#F4D64E] mr-2">&ldquo;</span>
                {t.quote}
                <span className="text-[#F4D64E] ml-1">&rdquo;</span>
              </motion.blockquote>
            </AnimatePresence>

            {/* Author — Name + Brand only (role dropped) */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`author-${active}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
                className="mt-10 md:mt-12 flex items-center gap-5"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#F4D64E]/12 border border-[#F4D64E]/30 text-[#F4D64E] font-display font-bold text-lg shrink-0">
                  {initial}
                </div>
                <div className="flex flex-col">
                  <div className="font-display font-bold text-white text-[1.1rem] md:text-[1.2rem] leading-tight">
                    {t.name}
                  </div>
                  <div className="mt-1 text-[0.92rem] text-[#F4D64E] tracking-tight">
                    {brand}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* WIDTH-BAR NAVIGATION — N progress bars, active fills yellow over 5s */}
        <div
          data-testid="testimonials-v5-tabs"
          className="mt-16 md:mt-20 flex gap-3 md:gap-4 max-w-[28rem]"
        >
          {items.map((tt, i) => {
            const isActive = i === active;
            return (
              <button
                key={`${tt.name}-${i}`}
                type="button"
                data-testid={`testimonial-v5-tab-${i + 1}`}
                onClick={() => setActive(i)}
                aria-pressed={isActive}
                aria-label={`Show testimonial from ${tt.name}`}
                className="group relative flex-1 h-[2px] bg-white/[0.08] overflow-hidden rounded-full focus:outline-none focus-visible:ring-1 focus-visible:ring-[#F4D64E]/50"
              >
                {/* Idle (inactive) bars get a subtle grey fill on hover */}
                {!isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-white/0 group-hover:bg-white/15 transition-colors duration-300"
                  />
                )}
                {/* Active bar fills yellow over 5s; resets when paused */}
                {isActive && (
                  <motion.span
                    aria-hidden
                    key={`fill-${active}-${paused ? "p" : "r"}`}
                    initial={{ width: paused ? "100%" : "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: paused ? 0 : TESTIMONIALS_ROTATE_MS / 1000,
                      ease: "linear",
                    }}
                    className="absolute inset-y-0 left-0 bg-[#F4D64E]"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};



// Expose every section for V2/V3 compositions to reuse
export {
  Nav,
  Hero,
  HeroV3,
  TrustStrip,
  LogoStrip,
  LogoStripV2,
  DevelopmentOverview,
  HowItWorks,
  HowItWorksV2,
  HeroVideoV2,
  ServicesGrid,
  ServicesGridV2,
  HorizontalFeatures,
  MarketingCreative,
  MarketingPinnedV2,
  MarketingCreativeV2,
  CaseStudies,
  CaseStudiesV2,
  CaseStudiesV5,
  Testimonials,
  FAQ,
  FinalCTA,
  Blog,
  Footer,
  MediaMarqueeV5,
  EditorialCollageV5,
  UISnippetsGridV5,
  MediaBackgroundV5,
  HorizontalShowcaseV5,
  LifestyleSpotlightV5,
  ServicesGridV5Vertical,
  CreativeCapabilitiesV5,
  MarketingPinnedV5,
  TestimonialsV5,
  ClosingV5,
  // Atoms — reused by sub-pages
  Eyebrow,
  SectionHeader,
  PillarAccent,
  PrimaryButton,
  SecondaryButton,
  HeroAmbient,
};

/**
 * CmsRouteEntries — the public-facing routes for the 5 CMS-managed pages,
 * driven by the `/api/page-routes` slug map so admins can rename a
 * page's URL from the CMS without a deploy. When a page has been
 * renamed, we also register a client-side redirect from its original
 * hardcoded path so old links keep working.
 *
 * Returns an array of <Route> elements suitable for spreading into
 * React Router's top-level <Routes>.
 */
function CmsRouteEntries() {
  const map = useCmsPageRoutes();
  const pages = [
    { key: "home", element: <HomeV5 /> },
    { key: "shopify-dev", element: <ShopifyDevV2 /> },
    { key: "marketing", element: <MarketingPage /> },
    { key: "creative", element: <CreativePage /> },
    { key: "about", element: <AboutPage /> },
  ];
  const out = [];
  pages.forEach(({ key, element }) => {
    const entry = map[key];
    if (!entry) return;
    const current = entry.path;
    const def = entry.default;
    out.push(<Route key={`${key}-current`} path={current} element={element} />);
    if (current !== def) {
      out.push(
        <Route
          key={`${key}-redirect`}
          path={def}
          element={<Navigate to={current} replace />}
        />
      );
    }
  });
  return out;
}

function App() {
  const cmsRoutes = CmsRouteEntries();
  return (
    <div className="App">
      <BrowserRouter>
        <ContactPanelProvider>
          <CustomScripts />
          <Routes>
            {cmsRoutes}
            <Route path="/shopify-experts-development-v1" element={<ShopifyDev />} />
            <Route path="/shopify-experts-development-v2" element={<ShopifyDevV2 />} />
            <Route path="/ecommerce-marketing-agency-v2" element={<MarketingPageV2 />} />
            <Route path="/showcase" element={<ShowcasePage />} />
            <Route path="/showcase/veronique-gabai" element={<VeroniqueGabaiPage />} />
            <Route path="/showcase/:slug" element={<CaseStudyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/conversion-optimization" element={<ConversionPage />} />
            <Route path="/homepage-review" element={<HomepageReviewPage />} />
            <Route path="/privacy" element={<LegalPage contentKey="privacy" />} />
            <Route path="/terms" element={<LegalPage contentKey="terms" />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* ====== Admin / CMS ====== */}
            <Route
              path="/admin/*"
              element={
                <AdminAuthProvider>
                  <Routes>
                    <Route path="login" element={<AdminLoginPage />} />
                    <Route element={<AdminLayout />}>
                      <Route index element={<AdminDashboardPage />} />
                      <Route path="content" element={<AdminContentListPage />} />
                      <Route path="content/:key" element={<AdminContentEditPage />} />
                      <Route path="blog" element={<AdminBlogListPage />} />
                      <Route path="blog/:slug" element={<AdminBlogEditPage />} />
                      <Route path="showcase" element={<AdminShowcaseListPage />} />
                      <Route path="showcase/:slug" element={<AdminShowcaseEditPage />} />
                      <Route path="settings" element={<AdminSettingsPage />} />
                      <Route path="seo" element={<AdminSeoPage />} />
                      <Route path="media" element={<AdminMediaPage />} />
                      <Route path="scripts" element={<AdminScriptsPage />} />
                      <Route path="transactional" element={<AdminTransactionalPage />} />
                      <Route path="sync" element={<AdminSyncPage />} />
                      <Route path="backups" element={<AdminBackupsPage />} />
                    </Route>
                  </Routes>
                </AdminAuthProvider>
              }
            />
            <Route path="/v1" element={<HomeV1 />} />
            <Route path="/revamp01-1" element={<HomeV1 />} />
            <Route path="/v2" element={<HomeV2 />} />
            <Route path="/v3" element={<HomeV3 />} />
            <Route path="/v4" element={<HomeV4 />} />
            <Route path="/v5" element={<HomeV5 />} />
            <Route path="/v6" element={<HomeV6 />} />
          </Routes>
        </ContactPanelProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
